import '@fluentui/web-components/web-components-all.js';
import { setTheme } from '@fluentui/web-components';
import { webDarkTheme, webLightTheme } from '@fluentui/tokens';
import {
  WIZARD_DRAFT_KEY,
  applyIntakeRecommendations,
  buildTemplateBundle,
  countGeneratedContent,
  createWizardDraft,
  loadWizardDraft,
  newWizardLogo,
  newWizardPage,
  newWizardPlan,
  newWizardStory,
  newWizardTestimonial,
  saveWizardDraft,
  splitWizardLines,
} from './wizard-state.js';
import { renderHosting } from './hosting-view.js';

let currentLang = localStorage.getItem('pagix-admin-lang') || localStorage.getItem('landcraft-admin-lang') || 'en';

function t(trVal, enVal) {
  return currentLang === 'tr' ? trVal : enVal;
}

function applyStaticTranslations() {
  document.documentElement.lang = currentLang;

  const skipLink = document.querySelector('.skip-link');
  if (skipLink) skipLink.textContent = t('İçeriğe geç', 'Skip to content');

  const sidebarBrandLabel = document.querySelector('label[for="sidebar-brand"]');
  if (sidebarBrandLabel) sidebarBrandLabel.textContent = t('Çalışma alanı', 'Workspace');

  const mobileMenu = document.querySelector('#mobile-menu');
  if (mobileMenu) mobileMenu.textContent = t('Menü', 'Menu');

  const themeToggle = document.querySelector('#theme-toggle');
  if (themeToggle) themeToggle.textContent = t('Koyu tema', 'Dark theme');

  const navs = {
    overview: [t('Genel bakış', 'Overview'), t('Durum ve hızlı işlemler', 'Status & quick actions')],
    hosting: [t('Landing Pages & Hosting', 'Landing Pages & Hosting'), t('Live routing, status & catalog', 'Live routing, status & catalog')],
    wizard: [t('Şablon üret', 'Generate template'), t('AI destekli adım adım kurulum', 'AI-powered step-by-step setup')],
    preview: [t('Önizleme', 'Preview'), t('Sayfaları canlı incele', 'Inspect pages live')],
    content: [t('İçerikler', 'Contents'), t('Metin ve varyantlar', 'Copy and variants')],
    matrix: [t('Sayfa matrisi', 'Page matrix'), t('Route ve segmentler', 'Routes & segments')],
    brands: [t('Markalar', 'Brands'), t('Tema ve entegrasyonlar', 'Themes & integrations')],
    jobs: [t('İşlemler', 'Jobs'), t('Build ve üretim logları', 'Build & generation logs')],
  };

  Object.entries(navs).forEach(([key, [title, desc]]) => {
    const item = document.querySelector(`.nav-item[data-nav="${key}"]`);
    if (item) {
      item.innerHTML = `<span>${title}</span><small>${desc}</small>`;
    }
  });

  const studioLink = document.querySelector('a.nav-item[href="/generate.html"]');
  if (studioLink) {
    studioLink.innerHTML = `<span>${t('Şablon Stüdyosu ↗', 'Template Studio ↗')}</span><small>${t('10 Awwwards şablonu & klonlama', '10 Awwwards templates & cloner')}</small>`;
  }

  const localNote = document.querySelector('.local-note');
  if (localNote) {
    localNote.innerHTML = `<strong>${t('Yerel çalışma', 'Local workspace')}</strong><p>${t('Değişiklikler doğrulanarak JSON dosyalarına yazılır.', 'Changes are validated and saved to JSON files.')}</p>`;
  }

  const authTitle = document.querySelector('#auth-title');
  if (authTitle) authTitle.textContent = t('Yönetim Paneli Girişi', 'Admin Panel Sign In');

  const authSubtitle = document.querySelector('#auth-subtitle');
  if (authSubtitle) authSubtitle.textContent = t('Marka, sayfa matrisi ve AI şablonlarını yönetmek için oturum açın.', 'Sign in to manage brands, page matrix, and AI templates.');

  const authUsernameLabel = document.querySelector('#auth-username-label');
  if (authUsernameLabel) authUsernameLabel.textContent = t('Kullanıcı adı', 'Username');

  const authPasswordLabel = document.querySelector('#auth-password-label');
  if (authPasswordLabel) authPasswordLabel.textContent = t('Şifre', 'Password');

  const authDevHintText = document.querySelector('#auth-dev-hint-text');
  if (authDevHintText) {
    authDevHintText.innerHTML = t(
      '💡 Geliştirme modu varsayılanı: <strong>admin</strong> / <strong>admin</strong>',
      '💡 Dev mode default: <strong>admin</strong> / <strong>admin</strong>'
    );
  }

  const authSubmitBtn = document.querySelector('#auth-submit-btn');
  if (authSubmitBtn) authSubmitBtn.textContent = t('Giriş Yap', 'Sign In');

  const logoutBtn = document.querySelector('#logout-btn');
  if (logoutBtn) logoutBtn.textContent = t('Çıkış', 'Log out');

  const authThemeToggle = document.querySelector('#auth-theme-toggle');
  if (authThemeToggle) authThemeToggle.textContent = t('Koyu tema', 'Dark theme');
}

const content = document.querySelector('#content');
const notice = document.querySelector('#notice');
const sidebar = document.querySelector('#sidebar');
const mobileMenu = document.querySelector('#mobile-menu');
const themeToggle = document.querySelector('#theme-toggle');
const sidebarBrand = document.querySelector('#sidebar-brand');
const contextTitle = document.querySelector('#context-title');
const contextSubtitle = document.querySelector('#context-subtitle');

const state = {
  auth: {
    authenticated: false,
    user: null,
    devMode: false,
    authRequired: false,
  },
  meta: null,
  section: 'overview',
  brandId: null,
  contentCombos: [],
  selectedContentIndex: 0,
  selectedJobId: null,
  jobTimer: null,
  aiStatusTimer: null,
  contentBuildChainTimer: null,
  previewKey: null,
  previewDevice: 'desktop',
  wizardStep: 0,
  wizardDraft: loadWizardDraft(localStorage),
  wizardTimer: null,
  wizardPreparing: false,
  wizardRenderKey: null,
};
state.wizardStep = Math.max(0, Math.min(7, Number(state.wizardDraft.currentStep || 0)));

const sectionMeta = {
  overview: [
    ['Genel bakış', 'Overview'],
    ['Portföy durumu ve hızlı işlemler', 'Portfolio status and quick actions']
  ],
  hosting: [
    ['Landing Pages & Hosting', 'Landing Pages & Hosting'],
    ['Live routing, Azure setup, published status & landing pages catalog', 'Live routing, Azure setup, published status & landing pages catalog']
  ],
  wizard: [
    ['Şablon üret', 'Generate template'],
    ['AI destekli adım adım kurulum', 'AI-powered step-by-step setup']
  ],
  preview: [
    ['Önizleme', 'Preview'],
    ['Üretilen sayfaları cihaz ölçülerinde inceleyin', 'Inspect generated pages across device dimensions']
  ],
  content: [
    ['İçerikler', 'Contents'],
    ['Dil, sayfa ve varyant metinlerini yönetin', 'Manage language, page and variant copy']
  ],
  matrix: [
    ['Sayfa matrisi', 'Page matrix'],
    ['Route, segment ve sayfa sözleşmeleri', 'Route, segment and page contracts']
  ],
  brands: [
    ['Markalar', 'Brands'],
    ['Tema, entegrasyon ve deploy ayarları', 'Theme, integration and deploy settings']
  ],
  jobs: [
    ['İşlemler', 'Jobs'],
    ['Build ve içerik üretim logları', 'Build and content generation logs']
  ],
};

function enhanceFluentSemantics(root = document) {
  root.querySelectorAll('fluent-button').forEach((button) => {
    button.setAttribute('role', 'button');
    if (!button.hasAttribute('tabindex')) button.tabIndex = 0;
  });
  root.querySelectorAll('fluent-progress-bar').forEach((progress) => progress.setAttribute('role', 'progressbar'));
  themeToggle.setAttribute('role', 'switch');
  themeToggle.setAttribute('aria-checked', String(Boolean(themeToggle.checked)));
}

function escapeHtml(value = '') {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function showNotice(message = '', tone = '') {
  notice.textContent = message;
  notice.dataset.tone = tone;
}

function openAssetLibraryModal(brandId, onSelect) {
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-content">
      <div class="modal-header">
        <h3>Görsel Kütüphanesi (${escapeHtml(brandId)})</h3>
        <button class="modal-close" type="button" aria-label="Kapat">×</button>
      </div>
      <div class="modal-body">
        <div class="upload-area">
          <label class="upload-dropzone">
            <input type="file" accept="image/png, image/jpeg, image/webp, image/svg+xml" style="display:none" id="asset-upload-input">
            <span>📁 Sürükleyip bırakın veya seçmek için tıklayın</span>
            <small>Desteklenenler: PNG, JPG, WEBP, SVG (Maks 2 MB)</small>
          </label>
        </div>
        <div class="assets-gallery">
          <div class="assets-loading">Yükleniyor...</div>
          <div class="assets-grid" style="display:none"></div>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  const closeBtn = modal.querySelector('.modal-close');
  const uploadInput = modal.querySelector('#asset-upload-input');
  const dropzone = modal.querySelector('.upload-dropzone');
  const assetsGrid = modal.querySelector('.assets-grid');
  const loading = modal.querySelector('.assets-loading');

  const closeModal = () => {
    modal.remove();
  };

  closeBtn.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  const loadAssets = async () => {
    try {
      const response = await fetch(`/api/brands/${encodeURIComponent(brandId)}/assets`);
      if (!response.ok) throw new Error('Assets listelenemedi');
      const files = await response.json();
      
      if (files.length === 0) {
        assetsGrid.innerHTML = '<div class="empty-gallery">Kütüphane boş. Yeni bir görsel yükleyin.</div>';
      } else {
        assetsGrid.innerHTML = files.map(file => `
          <div class="asset-card" data-url="${escapeHtml(file.url)}" data-name="${escapeHtml(file.name)}">
            <div class="asset-preview">
              <img src="${escapeHtml(file.url)}" alt="${escapeHtml(file.name)}">
            </div>
            <div class="asset-info">
              <span class="asset-name" title="${escapeHtml(file.name)}">${escapeHtml(file.name)}</span>
              <span class="asset-size">${(file.size / 1024).toFixed(1)} KB</span>
            </div>
            <button class="asset-delete" type="button" data-delete-name="${escapeHtml(file.name)}" title="Sil">×</button>
          </div>
        `).join('');
      }
      loading.style.display = 'none';
      assetsGrid.style.display = 'grid';
    } catch (error) {
      assetsGrid.innerHTML = `<div class="error-gallery">Hata: ${escapeHtml(error.message)}</div>`;
      loading.style.display = 'none';
      assetsGrid.style.display = 'block';
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Dosya boyutu 2 MB sınırını aşamaz.');
      return;
    }
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!['.png', '.jpg', '.jpeg', '.webp', '.svg'].includes(ext)) {
      alert('Yalnızca PNG, JPG, JPEG, WEBP ve SVG dosyaları desteklenir.');
      return;
    }

    loading.style.display = 'block';
    assetsGrid.style.display = 'none';

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const response = await fetch('/api/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            brandId,
            filename: file.name,
            content: reader.result
          })
        });
        const res = await response.json();
        if (!response.ok) throw new Error(res.error || 'Yükleme başarısız.');
        
        onSelect(res.url);
        closeModal();
      } catch (error) {
        alert('Yükleme hatası: ' + error.message);
        loadAssets();
      }
    };
    reader.readAsDataURL(file);
  };

  uploadInput.addEventListener('change', (e) => {
    uploadFile(e.target.files[0]);
  });

  dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add('dragover');
  });
  dropzone.addEventListener('dragleave', () => {
    dropzone.classList.remove('dragover');
  });
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragover');
    uploadFile(e.dataTransfer.files[0]);
  });

  assetsGrid.addEventListener('click', async (e) => {
    const deleteBtn = e.target.closest('.asset-delete');
    if (deleteBtn) {
      e.stopPropagation();
      const filename = deleteBtn.dataset.deleteName;
      if (!confirm(`"${filename}" dosyasını silmek istediğinize emin misiniz?`)) return;
      
      try {
        const response = await fetch(`/api/brands/${encodeURIComponent(brandId)}/assets/${encodeURIComponent(filename)}`, {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Silme başarısız');
        loadAssets();
      } catch (error) {
        alert('Silme hatası: ' + error.message);
      }
      return;
    }

    const card = e.target.closest('.asset-card');
    if (card) {
      onSelect(card.dataset.url);
      closeModal();
    }
  });

  loadAssets();
}

function parseOptionalJson(value, label) {
  const source = String(value || '').trim();
  if (!source) return undefined;
  try {
    return JSON.parse(source);
  } catch (error) {
    throw new Error(`${label} geçerli JSON değil: ${error.message}`);
  }
}

function showAuthScreen(errorMessage = '') {
  state.auth.authenticated = false;
  state.auth.user = null;
  const authShell = document.querySelector('#auth-shell');
  const appShell = document.querySelector('#app-shell');
  const authError = document.querySelector('#auth-error');
  if (authShell) authShell.hidden = false;
  if (appShell) appShell.hidden = true;
  if (authError) {
    if (errorMessage) {
      authError.textContent = errorMessage;
      authError.hidden = false;
    } else {
      authError.textContent = '';
      authError.hidden = true;
    }
  }
}

function showAppScreen(user) {
  state.auth.authenticated = true;
  state.auth.user = user;
  const authShell = document.querySelector('#auth-shell');
  const appShell = document.querySelector('#app-shell');
  const userNameEl = document.querySelector('#user-name');
  if (authShell) authShell.hidden = true;
  if (appShell) appShell.hidden = false;
  if (userNameEl && user?.username) {
    userNameEl.textContent = user.username;
  }
}

async function api(path, options = {}) {
  const response = await fetch(path, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 401 && !path.startsWith('/api/auth/')) {
      showAuthScreen(t('Oturum süresi doldu veya yetkilendirme gerekli. Lütfen giriş yapın.', 'Session expired or authorization required. Please sign in.'));
    }
    throw new Error(data.error || `İstek başarısız: ${response.status}`);
  }
  return data;
}

function brandOptions(selected = state.brandId) {
  return (state.meta?.brands || [])
    .map((brand) => `<option value="${escapeHtml(brand.id)}" ${brand.id === selected ? 'selected' : ''}>${escapeHtml(brand.id)}</option>`)
    .join('');
}

function pageTypeOptions(selected) {
  return (state.meta?.pageTypes || [])
    .map((type) => `<option value="${escapeHtml(type.id)}" ${type.id === selected ? 'selected' : ''}>${escapeHtml(type.id)}</option>`)
    .join('');
}

function heading(title, description, actions = '') {
  return `<header class="page-heading"><div><h1>${escapeHtml(title)}</h1><p>${escapeHtml(description)}</p></div><div class="actions">${actions}</div></header>`;
}

function setBusy(label = 'Yükleniyor') {
  content.innerHTML = `<div class="loading-state" aria-label="${escapeHtml(label)}" aria-busy="true"><span></span><span></span><span></span></div>`;
}

function closeMobileMenu() {
  sidebar.dataset.open = 'false';
  mobileMenu.setAttribute('aria-expanded', 'false');
}

function setActiveBrand(brandId) {
  state.brandId = brandId;
  state.previewKey = null;
  if (sidebarBrand.value !== brandId) sidebarBrand.value = brandId;
}

async function refreshMeta() {
  state.meta = await api('/api/meta');
  if (!state.brandId || !state.meta.brands.some((brand) => brand.id === state.brandId)) {
    state.brandId = state.meta.brands[0]?.id ?? null;
  }
  sidebarBrand.innerHTML = brandOptions();
  sidebarBrand.disabled = !state.meta.brands.length;
  if (state.brandId) sidebarBrand.value = state.brandId;
}

function previewKey(combo) {
  return `${combo.row.pageId}/${combo.lang}/${combo.variant}`;
}

function previewUrl(combo) {
  return `/preview/${encodeURIComponent(state.brandId)}/${encodeURIComponent(combo.variant)}/${encodeURIComponent(combo.lang)}/${encodeURIComponent(combo.row.segment)}/`;
}

async function openPreview(combo) {
  state.previewKey = previewKey(combo);
  await navigate('preview');
}

async function navigate(section) {
  state.section = section;
  clearTimeout(state.jobTimer);
  clearTimeout(state.wizardTimer);
  clearTimeout(state.aiStatusTimer);
  clearTimeout(state.contentBuildChainTimer);
  if (section !== 'wizard') state.wizardPreparing = false;
  closeMobileMenu();
  window.scrollTo({ top: 0, behavior: 'auto' });
  document.querySelectorAll('[data-nav]').forEach((button) => {
    button.dataset.current = button.dataset.nav === section ? 'true' : 'false';
    button.setAttribute('aria-current', button.dataset.nav === section ? 'page' : 'false');
  });
  const meta = sectionMeta[section] ?? sectionMeta.overview;
  contextTitle.textContent = t(meta[0][0], meta[0][1]);
  contextSubtitle.textContent = t(meta[1][0], meta[1][1]);
  setBusy();
  showNotice();
  try {
    if (section === 'overview') await renderOverview();
    if (section === 'hosting') await renderHosting(state, { api, t, showNotice, escapeHtml, openPreview, navigate, refreshMeta, heading, brandOptions, setActiveBrand, startJob });
    if (section === 'wizard') await renderTemplateWizard();
    if (section === 'preview') await renderPreview();
    if (section === 'brands') await renderBrands();
    if (section === 'matrix') await renderMatrix();
    if (section === 'content') await renderContent();
    if (section === 'jobs') await renderJobs();
    document.querySelector('#main-content')?.focus({ preventScroll: true });
  } catch (error) {
    content.innerHTML = `<div class="panel"><h1>Bu bölüm yüklenemedi</h1><p>${escapeHtml(error.message)}</p></div>`;
    showNotice(error.message, 'error');
  }
}

async function startJob(kind, { pageIds = [], langs = [], variants = [], force = false } = {}) {
  if (!state.brandId) throw new Error('Önce bir marka oluşturun.');
  const job = await api('/api/jobs', {
    method: 'POST',
    body: { kind, brandId: state.brandId, pageIds, langs, variants, force },
  });
  state.selectedJobId = job.id;
  showNotice(`${kind === 'build' ? 'Build' : 'İçerik üretimi'} başlatıldı.`, 'success');
  await navigate('jobs');
}

/**
 * İçerik editöründeki "AI ile üret / Zorla yeniden üret" butonu bir içerik
 * üretim job'u başlatır; ancak Preview yalnızca en son statik build'i
 * gösterdiği için (bkz. renderPreview notu), üretim tek başına yeterli
 * değildir — kullanıcı ayrıca ayrı bir sekmedeki "Yeni build al" butonunu
 * bulup tıklamazsa "Build gerekli" hatasıyla karşılaşır. Bu, sihirbazın
 * kendi generate→build zincirlemesiyle aynı deneyimi burada da sağlar:
 * içerik üretimi bitince marka için otomatik olarak bir build başlatılır.
 */
async function generateContentAndBuild(combo) {
  if (!state.brandId) return showNotice('Önce bir marka oluşturun.', 'error');
  try {
    const job = await api('/api/jobs', {
      method: 'POST',
      body: {
        kind: 'generate',
        brandId: state.brandId,
        pageIds: [combo.row.pageId],
        langs: [combo.lang],
        variants: [combo.variant],
        force: combo.exists,
      },
    });
    state.selectedJobId = job.id;
    showNotice(t('İçerik üretimi başlatıldı. Tamamlanınca önizleme için otomatik build alınacak.', 'Content generation started. A build will run automatically once it finishes, so preview is ready.'), 'success');
    await navigate('jobs');
    pollContentGenerationThenBuild(job.id, state.brandId);
  } catch (error) {
    showNotice(error.message, 'error');
  }
}

async function pollContentGenerationThenBuild(jobId, brandId) {
  let job;
  try {
    job = await api(`/api/jobs/${jobId}`);
  } catch {
    return; // Job durumu alınamadı; kullanıcı Jobs sekmesinden elle takip edebilir/build alabilir.
  }
  if (job.status === 'running') {
    state.contentBuildChainTimer = setTimeout(() => pollContentGenerationThenBuild(jobId, brandId), 1500);
    return;
  }
  if (job.status !== 'success') {
    showNotice(t('İçerik üretimi başarısız olduğu için build otomatik başlatılmadı.', 'Content generation failed, so the automatic build was not started.'), 'error');
    return;
  }
  try {
    const buildJob = await api('/api/jobs', {
      method: 'POST',
      body: { kind: 'build', brandId, pageIds: [], langs: [], variants: [], force: false },
    });
    state.selectedJobId = buildJob.id;
    showNotice(t('İçerik üretildi, önizleme için build otomatik başlatıldı.', 'Content generated — a build started automatically so preview is ready.'), 'success');
    if (state.section === 'jobs') await renderJobs();
  } catch (error) {
    showNotice(`${t('İçerik üretildi ama otomatik build başlatılamadı', 'Content generated, but the automatic build could not start')}: ${error.message}`, 'error');
  }
}

const WIZARD_STEPS = [
  ['Amaç', 'Sektör ve hedef'],
  ['Brief', 'Hedef ve teklif'],
  ['Tasarım', 'Marka ve pricing'],
  ['Sayfalar', 'Kapsam ve varyant'],
  ['Kanıtlar', 'Logo ve testimonial'],
  ['Hikayeler', 'Müşteri hikaye rayı'],
  ['Kontrol', 'Üretim planı'],
  ['Üretim', 'AI, build ve preview'],
];

const INDUSTRY_OPTIONS = [
  ['software', 'Yazılım ve SaaS', 'Dijital ürünler ve platformlar'],
  ['hr', 'İK ve People Ops', 'Çalışan deneyimi ve ekip süreçleri'],
  ['ecommerce', 'E-ticaret', 'Ürün, mağaza ve perakende'],
  ['finance', 'Finans ve fintech', 'Finansal hizmetler ve operasyonlar'],
  ['healthcare', 'Sağlık', 'Bakım, klinik ve sağlık teknolojileri'],
  ['services', 'Profesyonel hizmetler', 'Ajans, danışmanlık ve uzmanlık'],
  ['education', 'Eğitim', 'Öğrenme ürünleri ve kurumlar'],
  ['realestate', 'Gayrimenkul', 'Mülk, operasyon ve pazar yerleri'],
  ['other', 'Başka bir sektör', 'Sektörü kendiniz tanımlayın'],
];

const GOAL_OPTIONS = [
  ['leads', 'Demo veya lead toplamak', 'Nitelikli talepleri satış ekibine ulaştırın'],
  ['waitlist', 'Bekleme listesi oluşturmak', 'Lansman öncesi talebi ve ilgiyi toplayın'],
  ['launch', 'Yeni ürünü tanıtmak', 'Değeri açıklayın ve ilk kullanımı başlatın'],
  ['sales', 'Doğrudan satış yapmak', 'Satın almaya hazır ziyaretçiyi dönüştürün'],
  ['campaign', 'Kampanya sayfası hazırlamak', 'Tek bir teklif için odaklı sayfa üretin'],
];

const BUSINESS_MODEL_OPTIONS = [
  ['b2b', 'B2B', 'Şirketlere veya ekiplere satış'],
  ['b2c', 'B2C', 'Bireysel müşterilere satış'],
  ['marketplace', 'Pazar yeri', 'İki taraflı ürün veya hizmet modeli'],
];

const PROJECT_STAGE_OPTIONS = [
  ['new', 'Yeni proje', 'Sıfırdan bir sayfa oluşturuyoruz'],
  ['existing', 'Mevcut ürün', 'Ürün var, daha iyi anlatmak istiyoruz'],
  ['redesign', 'Yeniden tasarım', 'Mevcut dili koruyarak geliştireceğiz'],
];

const optionLabel = (options, value) => options.find(([id]) => id === value)?.[1] || value;

const PAGE_TYPE_LABELS = {
  waitlist: 'Waitlist',
  'paid-lp': 'Paid campaign landing',
  'organic-seo': 'Organic SEO landing',
  comparison: 'Comparison',
  'local-geo': 'Local / geo landing',
  calculator: 'Calculator',
  'lead-magnet': 'Lead magnet',
  'case-study': 'Case study',
  'trust-security': 'Trust and security',
  'webinar-demo': 'Webinar / demo',
  'quiz-configurator': 'Quiz / configurator',
  'retargeting-objection': 'Retargeting objection',
};

function selected(value, expected) {
  return value === expected ? 'selected' : '';
}

function checked(condition) {
  return condition ? 'checked' : '';
}

function persistWizard() {
  state.wizardDraft.currentStep = state.wizardStep;
  saveWizardDraft(localStorage, state.wizardDraft);
}

function wizardPageTypeOptions(selectedType) {
  return (state.meta?.pageTypes || []).map((type) => (
    `<option value="${escapeHtml(type.id)}" ${selected(type.id, selectedType)}>${escapeHtml(PAGE_TYPE_LABELS[type.id] || type.id)}</option>`
  )).join('');
}

function pageSkeletonHtml(pageTypeId) {
  const sections = state.meta?.pageTypes?.find((type) => type.id === pageTypeId)?.sections || [];
  if (!sections.length) return '';
  return `<div class="wizard-page-skeleton" data-page-skeleton aria-label="Sayfa iskeleti önizlemesi">
    ${sections.map((name) => `<span class="wizard-skeleton-chip">${escapeHtml(name)}</span>`).join('<span class="wizard-skeleton-arrow" aria-hidden="true">→</span>')}
  </div>`;
}

const WIZARD_STEPS_EN = [
  ['Goal', 'Industry & objective'],
  ['Brief', 'Audience & offer'],
  ['Design', 'Brand & pricing'],
  ['Pages', 'Scope & variants'],
  ['Proof', 'Logos & testimonials'],
  ['Stories', 'Customer stories rail'],
  ['Review', 'Production plan'],
  ['Launch', 'AI, build & preview'],
];

const OPTION_TRANSLATIONS = {
  software: ['Software & SaaS', 'Digital products and platforms'],
  hr: ['HR & People Ops', 'Employee experience and team workflows'],
  ecommerce: ['E-commerce', 'Products, storefronts and retail'],
  finance: ['Finance & Fintech', 'Financial services and operations'],
  healthcare: ['Healthcare', 'Care, clinics and health tech'],
  services: ['Professional Services', 'Agencies, consulting and expertise'],
  education: ['Education', 'Learning platforms and institutions'],
  realestate: ['Real Estate', 'Properties, operations and marketplaces'],
  other: ['Another Industry', 'Define your industry yourself'],

  leads: ['Collect Leads or Demos', 'Route qualified prospects to sales team'],
  waitlist: ['Build a Waitlist', 'Gather early demand and pre-launch hype'],
  launch: ['Launch New Product', 'Demonstrate value and initiate adoption'],
  sales: ['Direct Sales Conversion', 'Convert visitors ready to purchase'],
  campaign: ['Campaign Landing Page', 'Build a focused page for a single offer'],

  b2b: ['B2B', 'Sales to companies or teams'],
  b2c: ['B2C', 'Sales to individual consumers'],
  marketplace: ['Marketplace', 'Two-sided product or service network'],

  new: ['New Project', 'Creating a brand-new page from scratch'],
  existing: ['Existing Product', 'Have a live product, want to communicate better'],
  redesign: ['Redesign', 'Refining the experience while keeping brand voice'],
};

function wizardStepNavigation() {
  return `<ol class="wizard-steps" aria-label="${t('Şablon üretim adımları', 'Template wizard steps')}">${WIZARD_STEPS.map(([title, subtitle], index) => {
    const displayTitle = currentLang === 'tr' ? title : (WIZARD_STEPS_EN[index]?.[0] || title);
    const displaySubtitle = currentLang === 'tr' ? subtitle : (WIZARD_STEPS_EN[index]?.[1] || subtitle);
    return `
    <li data-state="${index < state.wizardStep ? 'complete' : index === state.wizardStep ? 'current' : 'upcoming'}">
      <button type="button" data-wizard-jump="${index}" ${index > state.wizardStep || state.wizardDraft.result ? 'disabled' : ''} ${index === state.wizardStep ? 'aria-current="step"' : ''}>
        <span>${index < state.wizardStep ? '✓' : index + 1}</span><strong>${displayTitle}</strong><small>${displaySubtitle}</small>
      </button>
    </li>`;
  }).join('')}</ol>`;
}

function wizardShell(body, actions = '') {
  return `
    ${heading(t('AI şablon sihirbazı', 'AI Template Wizard'), t('Ne üretmek istediğinizi anlatın. Motor doğru brief, sayfa planı ve içerik akışını sizinle birlikte hazırlasın.', 'Describe what you want to build. The engine guides the right brief, page plan, and copy flow with you.'))}
    <div class="wizard-layout">
      <aside class="panel wizard-progress">${wizardStepNavigation()}<p>${t('Taslak bu tarayıcıda otomatik kaydedilir.', 'Draft is saved automatically in this browser.')}</p><button class="wizard-reset" type="button" data-wizard-reset>${t('Taslağı temizle', 'Reset draft')}</button></aside>
      <section class="panel wizard-stage">${body}${actions ? `<div class="wizard-actions">${actions}</div>` : ''}</section>
    </div>`;
}

function wizardOptionCards(name, options, value) {
  return `<div class="wizard-option-grid">${options.map(([id, title, description]) => {
    const en = OPTION_TRANSLATIONS[id];
    const displayTitle = currentLang === 'tr' ? title : (en?.[0] || title);
    const displayDesc = currentLang === 'tr' ? description : (en?.[1] || description);
    return `
    <label class="wizard-option">
      <input type="radio" name="${name}" value="${id}" ${checked(value === id)} required>
      <span><strong>${escapeHtml(displayTitle)}</strong><small>${escapeHtml(displayDesc)}</small></span>
    </label>`;
  }).join('')}</div>`;
}

function renderWizardIntake() {
  const draft = state.wizardDraft;
  return wizardShell(`
    <form id="wizard-form" class="wizard-form wizard-intake-form">
      <div class="wizard-stage-heading"><span>1 / 8</span><h2>${t('Nasıl bir sonuç elde etmek istiyorsunuz?', 'What outcome do you want to achieve?')}</h2><p>${t('Dört kısa seçim yapın. Bir sonraki ekranda size uygun başlangıç brief\'ini ve sayfa tipini önereceğiz.', 'Make four quick selections. On the next step, we will recommend a tailored initial brief and page structure.')}</p></div>
      <fieldset class="wizard-question"><legend>${t('Bu sayfanın ana görevi ne?', 'What is the primary goal of this page?')}</legend>${wizardOptionCards('journeyGoal', GOAL_OPTIONS, draft.journeyGoal)}</fieldset>
      <fieldset class="wizard-question"><legend>${t('Hangi sektörde çalışıyorsunuz?', 'What industry are you in?')}</legend>${wizardOptionCards('industry', INDUSTRY_OPTIONS, draft.industry)}
        <div class="field wizard-other-industry" data-industry-other ${draft.industry === 'other' ? '' : 'hidden'}><label for="wizard-industry-other">${t('Sektörünüz', 'Your Industry')}</label><input id="wizard-industry-other" name="industryOther" maxlength="120" value="${escapeHtml(draft.industryOther)}" ${draft.industry === 'other' ? 'required' : ''} placeholder="${t('Örneğin lojistik veya enerji', 'e.g. logistics or energy')}"></div>
      </fieldset>
      <fieldset class="wizard-question"><legend>${t('Kime satış yapıyorsunuz?', 'Who are you selling to?')}</legend>${wizardOptionCards('businessModel', BUSINESS_MODEL_OPTIONS, draft.businessModel)}</fieldset>
      <fieldset class="wizard-question"><legend>${t('Proje hangi aşamada?', 'What stage is the project in?')}</legend>${wizardOptionCards('projectStage', PROJECT_STAGE_OPTIONS, draft.projectStage)}</fieldset>
    </form>`, '<fluent-button data-wizard-next appearance="accent">' + t('Başlangıç planımı hazırla', 'Prepare my initial plan') + '</fluent-button>');
}

function renderWizardPreparing() {
  const draft = state.wizardDraft;
  const indLabel = currentLang === 'tr' ? optionLabel(INDUSTRY_OPTIONS, draft.industry) : (OPTION_TRANSLATIONS[draft.industry]?.[0] || draft.industry);
  const goalLabel = currentLang === 'tr' ? optionLabel(GOAL_OPTIONS, draft.journeyGoal).toLocaleLowerCase('tr-TR') : (OPTION_TRANSLATIONS[draft.journeyGoal]?.[0]?.toLowerCase() || draft.journeyGoal);
  return wizardShell(`
    <div class="wizard-preparing" role="status" aria-live="polite" aria-label="${t('Başlangıç planı hazırlanıyor', 'Initial plan is being prepared')}">
      <div class="wizard-preparing-visual" aria-hidden="true"><span></span><span></span><span></span></div>
      <div class="wizard-stage-heading"><span>${t('Yanıtlar alındı', 'Responses received')}</span><h2>${t('Başlangıç planınız hazırlanıyor', 'Preparing your initial plan')}</h2><p>${currentLang === 'tr' ? `${escapeHtml(indLabel)} için ${escapeHtml(goalLabel)} odaklı bir akış kuruyoruz.` : `Structuring a ${escapeHtml(goalLabel)}-focused flow for ${escapeHtml(indLabel)}.`}</p></div>
      <div class="wizard-preparing-list">
        <div><span></span><strong>${t('Dönüşüm hedefi eşleştiriliyor', 'Matching conversion objective')}</strong></div>
        <div><span></span><strong>${t('Uygun sayfa tipi seçiliyor', 'Selecting recommended page types')}</strong></div>
        <div><span></span><strong>${t('Brief başlangıcı hazırlanıyor', 'Drafting initial brief')}</strong></div>
      </div>
      <fluent-progress-bar aria-label="${t('Plan hazırlanıyor', 'Preparing plan')}"></fluent-progress-bar>
    </div>`);
}

function renderWizardBrief() {
  const draft = state.wizardDraft;
  const indLabel = currentLang === 'tr' ? optionLabel(INDUSTRY_OPTIONS, draft.industry) : (OPTION_TRANSLATIONS[draft.industry]?.[0] || draft.industry);
  const goalLabel = currentLang === 'tr' ? optionLabel(GOAL_OPTIONS, draft.journeyGoal) : (OPTION_TRANSLATIONS[draft.journeyGoal]?.[0] || draft.journeyGoal);
  const stageLabel = currentLang === 'tr' ? optionLabel(PROJECT_STAGE_OPTIONS, draft.projectStage) : (OPTION_TRANSLATIONS[draft.projectStage]?.[0] || draft.projectStage);
  return wizardShell(`
    <form id="wizard-form" class="wizard-form">
      <div class="wizard-stage-heading"><span>2 / 8</span><h2>${t('Şimdi markanızı ve teklifinizi netleştirelim', 'Now let\'s clarify your brand and offer')}</h2><p>${t('Seçimlerinize göre hedef, CTA ve ilk sayfa önerildi. Önerilen her alanı değiştirebilirsiniz.', 'Based on your selections, we suggested goals, CTA, and initial pages. You can edit any field.')}</p></div>
      <div class="wizard-recommendation"><strong>${t('Başlangıç önerisi hazır', 'Initial recommendation ready')}</strong><span>${escapeHtml(indLabel)}</span><span>${escapeHtml(goalLabel)}</span><span>${escapeHtml(stageLabel)}</span></div>
      <div class="wizard-ai-brief">
        <div class="field wide"><label for="wizard-company-brief">${t('Şirketiniz hakkında (genel tanım)', 'About your company (general description)')}</label><textarea id="wizard-company-brief" name="companyBrief" rows="4" placeholder="${t('Şirketinizin ne iş yaptığını, kime ne sattığını ve öne çıkan yönlerini birkaç cümleyle anlatın.', 'Describe in a few sentences what your company does, who it sells to, and what makes it stand out.')}">${escapeHtml(draft.companyBrief || '')}</textarea><small>${t('Bu açıklamayı yazıp aşağıdaki düğmeye tıklayın; AI, alttaki tüm alanları sizin için dolduracaktır. Doldurulan her alanı istediğiniz gibi düzenleyebilirsiniz.', 'Write this description and click the button below; AI will fill in all the fields below for you. You can edit any filled field afterwards.')}</small></div>
        <div class="wizard-ai-brief-actions">
          <fluent-button type="button" id="wizard-generate-brief" appearance="outline">✨ ${t('AI ile Doldur', 'Generate with AI')}</fluent-button>
          <span id="wizard-generate-brief-status" class="wizard-ai-brief-status" role="status" aria-live="polite"></span>
        </div>
      </div>
      <div class="form-grid">
        <div class="field"><label for="wizard-brand-id">${t('Marka id', 'Brand ID')}</label><input id="wizard-brand-id" name="brandId" required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value="${escapeHtml(draft.brandId)}"><small>${t('Küçük harf ve tire: ornek-marka', 'Lowercase and hyphens: example-brand')}</small></div>
        <div class="field"><label for="wizard-domain">Domain</label><input id="wizard-domain" name="domain" required placeholder="example.com" value="${escapeHtml(draft.domain)}"></div>
        <div class="field wide"><label for="wizard-audience">${t('Hedef kitle', 'Target audience')}</label><textarea id="wizard-audience" name="audience" required rows="3" placeholder="${t('Kim karar veriyor, hangi problemle karşılaşıyor?', 'Who makes decisions, what problem do they face?')}">${escapeHtml(draft.audience)}</textarea></div>
        <div class="field wide"><label for="wizard-offer">${t('Teklif', 'Core offer')}</label><textarea id="wizard-offer" name="offer" required rows="3" placeholder="${t('Ürün ne sunuyor ve kullanıcı ne elde ediyor?', 'What does the product offer and what does the user get?')}">${escapeHtml(draft.offer)}</textarea></div>
        <div class="field wide"><label for="wizard-objective">${t('Sayfanın iş hedefi', 'Business objective')}</label><textarea id="wizard-objective" name="objective" required rows="3" placeholder="${t('Demo talebi, satın alma, waitlist veya başka bir dönüşüm hedefi', 'Demo request, direct sale, waitlist, or another conversion goal')}">${escapeHtml(draft.objective)}</textarea></div>
        <div class="field"><label for="wizard-primary-cta">${t('Birincil CTA', 'Primary CTA')}</label><input id="wizard-primary-cta" name="primaryCta" required placeholder="Request demo" value="${escapeHtml(draft.primaryCta)}"></div>
        <fieldset class="field"><legend>${t('Diller', 'Languages')}</legend><div class="checks wizard-language-checks">
          ${['en', 'de', 'fr', 'tr'].map((lang) => `<label><input type="checkbox" name="languages" value="${lang}" ${checked(draft.languages.includes(lang))}>${lang}</label>`).join('')}
        </div></fieldset>
        <div class="field"><label for="wizard-additional-languages">${t('Ek dil kodları', 'Additional language codes')}</label><textarea id="wizard-additional-languages" name="additionalLanguages" rows="2" placeholder="de-CH&#10;es">${escapeHtml(draft.additionalLanguages)}</textarea><small>${t('Her satıra en veya de-CH biçiminde bir kod.', 'One code per line in en or de-CH format.')}</small></div>
        <div class="field wide"><label for="wizard-differentiators">${t('Farklılaştırıcılar', 'Differentiators')}</label><textarea id="wizard-differentiators" name="differentiators" rows="4" placeholder="${t('Her satıra bir doğrulanabilir fark yazın', 'One verifiable differentiator per line')}">${escapeHtml(draft.differentiators)}</textarea></div>
        <div class="field wide"><label for="wizard-notes">${t('AI için ek kısıtlar', 'Additional constraints for AI')}</label><textarea id="wizard-notes" name="notes" rows="3" placeholder="${t('Kullanılmaması gereken iddialar, kelimeler veya ton notları', 'Claims, words, or tone notes to avoid')}">${escapeHtml(draft.notes)}</textarea></div>
      </div>
    </form>`, '<fluent-button data-wizard-back appearance="outline">' + t('Geri', 'Back') + '</fluent-button><fluent-button data-wizard-next appearance="accent">' + t('Tasarımı belirle', 'Set Design') + '</fluent-button>');
}

function renderWizardPlanCard(plan, index) {
  const canRemove = state.wizardDraft.pricing.plans.length > (state.wizardDraft.pricing.model === 'tiered' ? 2 : 1);
  const pricingEnabled = state.wizardDraft?.pricing?.model !== 'none';
  const req = pricingEnabled ? 'required' : '';
  return `<fieldset class="wizard-card wizard-plan-card" data-plan-index="${index}">
    <legend>${t('Plan', 'Plan')} ${index + 1}</legend>
    <div class="form-grid">
      <div class="field"><label>${t('Plan id', 'Plan ID')}</label><input data-plan-field="id" aria-label="Plan ${index + 1} id" ${req} ${pricingEnabled ? 'pattern="[a-z0-9]+(?:-[a-z0-9]+)*"' : ''} value="${escapeHtml(plan.id)}"></div>
      <div class="field"><label>${t('Plan adı', 'Plan name')}</label><input data-plan-field="name" aria-label="Plan ${index + 1} adı" ${req} value="${escapeHtml(plan.name)}"></div>
      <div class="field"><label>${t('Ödeme etiketi', 'Billing label')}</label><input data-plan-field="billingLabel" aria-label="Plan ${index + 1} ödeme etiketi" ${req} value="${escapeHtml(plan.billingLabel)}"></div>
      <div class="field"><label>${t('Fiyat', 'Price')}</label><input data-plan-field="price" aria-label="Plan ${index + 1} fiyatı" ${req} placeholder="$49" value="${escapeHtml(plan.price)}"></div>
      <div class="field"><label>${t('Fiyat detayı', 'Price detail')}</label><input data-plan-field="priceDetail" aria-label="Plan ${index + 1} fiyat detayı" placeholder="per month" value="${escapeHtml(plan.priceDetail)}"></div>
      <div class="field"><label>${t('CTA etiketi', 'CTA label')}</label><input data-plan-field="ctaLabel" aria-label="Plan ${index + 1} CTA etiketi" ${req} value="${escapeHtml(plan.ctaLabel)}"></div>
      <div class="field wide"><label>${t('Plan açıklaması', 'Plan description')}</label><input data-plan-field="description" aria-label="Plan ${index + 1} açıklaması" value="${escapeHtml(plan.description)}"></div>
      <div class="field wide"><label>${t('CTA hedefi', 'CTA target')}</label><input data-plan-field="ctaHref" aria-label="Plan ${index + 1} CTA hedefi" ${req} value="${escapeHtml(plan.ctaHref)}"><small>${t('#get-started, site içi path veya HTTPS adresi', '#get-started, internal path, or HTTPS address')}</small></div>
    </div>
    ${canRemove ? `<button class="text-action" type="button" data-remove-plan="${index}">${t('Planı kaldır', 'Remove plan')}</button>` : ''}
  </fieldset>`;
}


function renderWizardDesign() {
  const draft = state.wizardDraft;
  const pricingEnabled = draft.pricing.model !== 'none';
  return wizardShell(`
    <form id="wizard-form" class="wizard-form">
      <div class="wizard-stage-heading"><span>3 / 8</span><h2>${t('Marka dilini ve teklif yapısını belirleyin', 'Define brand styling and offer structure')}</h2><p>${t('Renkler ve font deterministik uygulanır. AI; ton, brief ve doğrulanmış pricing verisini kullanır.', 'Colors and fonts are deterministically applied. AI utilizes tone, brief, and verified pricing data.')}</p></div>
      <div class="form-grid">
        <div class="field wide"><label for="wizard-tone">${t('Marka tonu', 'Brand tone')}</label><textarea id="wizard-tone" name="tone" required rows="3">${escapeHtml(draft.tone)}</textarea></div>
        <div class="field"><label for="wizard-primary">${t('Primary renk', 'Primary color')}</label><input id="wizard-primary" name="primary" required pattern="#[0-9A-Fa-f]{3,6}" value="${escapeHtml(draft.primary)}"></div>
        <div class="field"><label for="wizard-secondary">${t('Secondary renk', 'Secondary color')}</label><input id="wizard-secondary" name="secondary" required pattern="#[0-9A-Fa-f]{3,6}" value="${escapeHtml(draft.secondary)}"></div>
        <div class="field"><label for="wizard-font">Font</label><input id="wizard-font" name="font" required value="${escapeHtml(draft.font)}"></div>
        <div class="field"><label for="wizard-endpoint">${t('Form endpoint', 'Form endpoint')}</label><input id="wizard-endpoint" name="waitlistEndpoint" type="url" required value="${escapeHtml(draft.waitlistEndpoint || (draft.domain ? `https://${draft.domain}/waitlist` : ''))}"></div>
        <div class="field wide"><label for="wizard-design-references">${t('Tasarım referansları', 'Design references')}</label><textarea id="wizard-design-references" name="designReferences" rows="3" placeholder="${t('Her satıra bir Figma veya web sitesi URL\'si', 'One Figma or website URL per line')}">${escapeHtml(draft.designReferences)}</textarea><small>${t('Referanslar marka profili handoff\'u için saklanır; içerik AI\'ı URL içeriğini kendiliğinden okuyamaz.', 'References are saved for brand handoff; content AI does not crawl URL contents.')}</small></div>
      </div>
      <fieldset class="wizard-subsection">
        <legend>Pricing</legend>
        <div class="field"><label for="wizard-pricing-model">${t('Pricing modeli', 'Pricing model')}</label><select id="wizard-pricing-model" name="pricingModel">
          <option value="none" ${selected(draft.pricing.model, 'none')}>${t('Pricing bölümü yok', 'No pricing section')}</option>
          <option value="one-time" ${selected(draft.pricing.model, 'one-time')}>${t('Tek seferlik ödeme', 'One-time payment')}</option>
          <option value="subscription" ${selected(draft.pricing.model, 'subscription')}>${t('Abonelik', 'Subscription')}</option>
          <option value="tiered" ${selected(draft.pricing.model, 'tiered')}>${t('Çok planlı', 'Tiered multi-plan')}</option>
        </select></div>
        <fieldset class="wizard-pricing-fieldset" ${pricingEnabled ? '' : 'disabled'} style="border:0;padding:0;margin:0;" data-wizard-pricing-fields ${pricingEnabled ? '' : 'hidden'}>
          <div class="form-grid wizard-pricing-copy">
            <div class="field"><label for="wizard-pricing-heading">${t('Bölüm başlığı', 'Section heading')}</label><input id="wizard-pricing-heading" name="pricingHeading" ${pricingEnabled ? 'required' : ''} value="${escapeHtml(draft.pricing.heading)}"></div>
            <div class="field"><label for="wizard-pricing-product">${t('Ürün veya paket adı', 'Product or package name')}</label><input id="wizard-pricing-product" name="pricingProductName" ${pricingEnabled ? 'required' : ''} value="${escapeHtml(draft.pricing.productName)}"></div>
            <div class="field wide"><label for="wizard-pricing-intro">${t('Kısa açıklama', 'Short description')}</label><input id="wizard-pricing-intro" name="pricingIntro" value="${escapeHtml(draft.pricing.intro)}"></div>
            <div class="field wide"><label for="wizard-pricing-features">${t('Ortak özellikler', 'Shared features')}</label><textarea id="wizard-pricing-features" name="pricingFeatures" rows="5" ${pricingEnabled ? 'required' : ''}>${escapeHtml(draft.pricing.features)}</textarea><small>${t('Her satıra bir özellik. En az 2 özellik.', 'One feature per line. Minimum 2 features.')}</small></div>
            <div class="field wide"><label for="wizard-pricing-footnote">${t('Alt not', 'Footnote')}</label><input id="wizard-pricing-footnote" name="pricingFootnote" value="${escapeHtml(draft.pricing.footnote)}"></div>
          </div>
          <div class="wizard-card-list">${draft.pricing.plans.map(renderWizardPlanCard).join('')}</div>
          ${draft.pricing.plans.length < 4 ? '<button class="text-action" type="button" data-add-plan>' + t('Plan ekle', 'Add plan') + '</button>' : ''}
        </fieldset>
      </fieldset>
    </form>`, '<fluent-button data-wizard-back appearance="outline">' + t('Geri', 'Back') + '</fluent-button><fluent-button data-wizard-next appearance="accent">' + t('Sayfa planına geç', 'Next: Plan pages') + '</fluent-button>');
}

function renderWizardPageCard(page, index) {
  return `<fieldset class="wizard-card wizard-page-card" data-page-index="${index}">
    <legend>${t('Sayfa', 'Page')} ${index + 1}</legend>
    <div class="form-grid">
      <div class="field"><label>Page id</label><input data-page-field="pageId" aria-label="Sayfa ${index + 1} page id" required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value="${escapeHtml(page.pageId)}"></div>
      <div class="field"><label>${t('URL segmenti', 'URL segment')}</label><input data-page-field="segment" aria-label="Sayfa ${index + 1} URL segmenti" required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value="${escapeHtml(page.segment)}"></div>
      <div class="field"><label>${t('Sayfa tipi', 'Page type')}</label><select data-page-field="pageType" aria-label="Sayfa ${index + 1} tipi">${wizardPageTypeOptions(page.pageType)}</select></div>
      <div class="field"><label>${t('Segment ekseni', 'Segment axis')}</label><select data-page-field="axis" aria-label="Sayfa ${index + 1} segment ekseni">
        ${['use-case', 'persona', 'comparison', 'industry', 'moment'].map((axis) => `<option value="${axis}" ${selected(page.axis, axis)}>${axis}</option>`).join('')}
      </select></div>
      <div class="field"><label>Tier</label><select data-page-field="tier" aria-label="Sayfa ${index + 1} tier">${['T1', 'T2', 'T3'].map((tier) => `<option value="${tier}" ${selected(page.tier, tier)}>${tier}</option>`).join('')}</select></div>
      <fieldset class="field wizard-variant-field"><legend>${t('Varyant', 'Variant')}</legend><div class="checks">
        ${['organic', 'paid'].map((variant) => `<label><input type="checkbox" data-page-variant="${variant}" aria-label="Sayfa ${index + 1} ${variant} varyantı" ${checked(page.variants.includes(variant))}>${variant}</label>`).join('')}
      </div></fieldset>
      <div class="field wide"><label>${t('Çekirdek mesaj', 'Core hook')}</label><textarea data-page-field="hook" aria-label="Sayfa ${index + 1} çekirdek mesajı" required rows="3" placeholder="${t('Bu sayfanın savunduğu tek net fikir', 'The single core value proposition of this page')}">${escapeHtml(page.hook)}</textarea></div>
    </div>
    ${pageSkeletonHtml(page.pageType)}
    ${state.wizardDraft.pages.length > 1 ? `<button class="text-action" type="button" data-remove-page="${index}">${t('Sayfayı kaldır', 'Remove page')}</button>` : ''}
  </fieldset>`;
}

function renderWizardPages() {
  return wizardShell(`
    <form id="wizard-form" class="wizard-form">
      <div class="wizard-stage-heading"><span>4 / 8</span><h2>${t('Üretilecek sayfaları planlayın', 'Plan pages to generate')}</h2><p>${t('İlk sayfa amacınıza göre önerildi. Her kart ayrı bir route ve AI içerik işi oluşturur.', 'The first page is suggested based on your goal. Each card creates an independent route and AI content job.')}</p></div>
      <div class="wizard-card-list">${state.wizardDraft.pages.map(renderWizardPageCard).join('')}</div>
      ${state.wizardDraft.pages.length < 12 ? '<button class="text-action" type="button" data-add-page>' + t('Sayfa ekle', 'Add page') + '</button>' : ''}
    </form>`, '<fluent-button data-wizard-back appearance="outline">' + t('Geri', 'Back') + '</fluent-button><fluent-button data-wizard-next appearance="accent">' + t('Kanıtlara geç', 'Next: Add proof') + '</fluent-button>');
}

function renderWizardTestimonialCard(testimonial, index) {
  const canRemove = state.wizardDraft.proof.testimonials.length > 1;
  return `<fieldset class="wizard-card wizard-testimonial-card" data-testimonial-index="${index}">
    <legend>${t('Testimonial', 'Testimonial')} ${index + 1}</legend>
    <div class="form-grid">
      <div class="field wide"><label>${t('Alıntı', 'Quote')}</label><textarea data-testimonial-field="quote" aria-label="Testimonial ${index + 1} alıntı" rows="3">${escapeHtml(testimonial.quote)}</textarea></div>
      <div class="field"><label>${t('Yazar', 'Author')}</label><input data-testimonial-field="author" aria-label="Testimonial ${index + 1} yazar" value="${escapeHtml(testimonial.author)}"></div>
      <div class="field"><label>${t('Rol', 'Role')}</label><input data-testimonial-field="role" aria-label="Testimonial ${index + 1} rol" value="${escapeHtml(testimonial.role)}"></div>
      <div class="field"><label>${t('Şirket', 'Company')}</label><input data-testimonial-field="company" aria-label="Testimonial ${index + 1} şirket" value="${escapeHtml(testimonial.company)}"></div>
      <div class="field"><label>${t('Sonuç etiketi', 'Result label')}</label><input data-testimonial-field="result" aria-label="Testimonial ${index + 1} sonuç etiketi" value="${escapeHtml(testimonial.result)}"></div>
      <div class="field"><label>${t('Kaynak etiketi', 'Source label')}</label><input data-testimonial-field="sourceLabel" aria-label="Testimonial ${index + 1} kaynak etiketi" value="${escapeHtml(testimonial.sourceLabel)}"></div>
      <div class="field"><label>${t('Kaynak URL', 'Source URL')}</label><input data-testimonial-field="sourceUrl" aria-label="Testimonial ${index + 1} kaynak URL" value="${escapeHtml(testimonial.sourceUrl)}"></div>
    </div>
    ${canRemove ? `<button class="text-action" type="button" data-remove-testimonial="${index}">${t("Testimonial'ı kaldır", 'Remove testimonial')}</button>` : ''}
  </fieldset>`;
}

function renderWizardProof() {
  const proof = state.wizardDraft.proof;
  return wizardShell(`
    <form id="wizard-form" class="wizard-form">
      <div class="wizard-stage-heading"><span>5 / 8</span><h2>${t('Yalnızca doğrulanmış kanıtları ekleyin', 'Add verified social proof only')}</h2><p>${t('Bu adım isteğe bağlıdır. AI müşteri adı, testimonial veya sonuç metriği uydurmaz.', 'This step is optional. AI never fabricates customer names, testimonials, or metrics.')}</p></div>
      <label class="wizard-choice"><input id="wizard-proof-enabled" type="checkbox" name="proofEnabled" ${checked(proof.enabled)}><span><strong>${t('Logo bandı veya müşteri kanıtı ekle', 'Add logo wall or customer proof')}</strong><small>${t('Doğrulanmış müşteri, entegrasyon, iş ortağı logoları ve kaynaklı testimonial', 'Verified client, integration, partner logos and sourced testimonials')}</small></span></label>
      <div class="wizard-proof-fields" data-wizard-proof-fields ${proof.enabled ? '' : 'hidden'}>
        <fieldset class="wizard-subsection">
          <legend>${t('Hero logo bandı', 'Hero logo wall')}</legend>
          <div class="form-grid">
            <div class="field"><label for="wizard-logo-relationship">${t('Logo ilişkisi', 'Logo relationship')}</label><select id="wizard-logo-relationship" name="logoWallRelationship">
              <option value="customers" ${selected(proof.relationship, 'customers')}>${t('Müşteriler', 'Customers')}</option>
              <option value="integrations" ${selected(proof.relationship, 'integrations')}>${t('Entegrasyonlar', 'Integrations')}</option>
              <option value="partners" ${selected(proof.relationship, 'partners')}>${t('İş ortakları', 'Partners')}</option>
            </select></div>
            <div class="field"><label for="wizard-logo-label">${t('Bant başlığı', 'Wall heading')}</label><input id="wizard-logo-label" name="logoWallLabel" value="${escapeHtml(proof.logoWallLabel)}"></div>
          </div>
          <div class="wizard-card-list">${proof.logos.map((logo, index) => `<div class="wizard-card wizard-logo-card" data-logo-index="${index}">
            <strong>${t('Logo', 'Logo')} ${index + 1}</strong><div class="form-grid">
              <div class="field"><label>${t('Şirket adı', 'Company name')}</label><input data-logo-field="name" aria-label="Logo ${index + 1} şirket adı" value="${escapeHtml(logo.name)}"></div>
              <div class="field"><label>${t('Logo asset URL', 'Logo asset URL')}</label><div class="input-action-group"><div class="input-thumbnail-preview">${logo.src ? `<img src="${escapeHtml(logo.src)}" alt="Önizleme">` : ''}</div><input data-logo-field="src" aria-label="Logo ${index + 1} asset URL" value="${escapeHtml(logo.src)}"><button class="text-action" type="button" data-asset-picker="logo" title="${t('Kütüphaneden Görsel Seç', 'Pick Asset')}">🖼️</button></div></div>
              <div class="field wide"><label>${t('Şirket sitesi', 'Company website')}</label><input data-logo-field="href" aria-label="Logo ${index + 1} şirket sitesi" value="${escapeHtml(logo.href)}"></div>
            </div>
            ${proof.logos.length > 2 ? `<button class="text-action" type="button" data-remove-logo="${index}">${t('Logoyu kaldır', 'Remove logo')}</button>` : ''}
          </div>`).join('')}</div>
          ${proof.logos.length < 12 ? '<button class="text-action" type="button" data-add-logo>' + t('Logo ekle', 'Add logo') + '</button>' : ''}
        </fieldset>
        <fieldset class="wizard-subsection">
          <legend>${t("Kaynaklı testimonial'lar", 'Sourced testimonials')}</legend>
          <div class="field wide"><label for="wizard-testimonial-heading">${t('Bölüm başlığı', 'Section heading')}</label><input id="wizard-testimonial-heading" name="testimonialHeading" value="${escapeHtml(proof.testimonialHeading)}"></div>
          <div class="wizard-card-list">${proof.testimonials.map(renderWizardTestimonialCard).join('')}</div>
          ${proof.testimonials.length < 12 ? '<button class="text-action" type="button" data-add-testimonial>' + t('Testimonial ekle', 'Add testimonial') + '</button>' : ''}
        </fieldset>
      </div>
    </form>`, '<fluent-button data-wizard-back appearance="outline">' + t('Geri', 'Back') + '</fluent-button><fluent-button data-wizard-next appearance="accent">' + t('Hikayelere geç', 'Next: Customer stories') + '</fluent-button>');
}

function renderWizardStoryCard(story, index) {
  const total = state.wizardDraft.storyRail.stories.length;
  const canRemove = total > 2;
  return `<fieldset class="wizard-card wizard-story-card" data-story-index="${index}" data-story-kind="${story.kind}">
    <legend>${t('Hikaye', 'Story')} ${index + 1}</legend>
    <div class="wizard-card-order">
      <button class="text-action" type="button" data-move-story-up="${index}" ${index === 0 ? 'disabled' : ''} aria-label="Hikaye ${index + 1} yukarı">↑</button>
      <button class="text-action" type="button" data-move-story-down="${index}" ${index === total - 1 ? 'disabled' : ''} aria-label="Hikaye ${index + 1} aşağı">↓</button>
    </div>
    <div class="form-grid">
      <div class="field"><label>${t('Hikaye türü', 'Story type')}</label><select data-story-field="kind" data-story-kind-select aria-label="Hikaye ${index + 1} türü">
        <option value="testimonial" ${selected(story.kind, 'testimonial')}>${t('Müşteri alıntısı', 'Customer quote')}</option>
        <option value="product-proof" ${selected(story.kind, 'product-proof')}>${t('Ürün kanıtı', 'Product proof')}</option>
      </select></div>
      <div class="field"><label>${t('Şirket', 'Company')}</label><input data-story-field="company" aria-label="Hikaye ${index + 1} şirket" value="${escapeHtml(story.company)}"></div>
      <div class="field wide" data-kind="testimonial"><label>${t('Alıntı', 'Quote')}</label><textarea data-story-field="quote" aria-label="Hikaye ${index + 1} alıntı" rows="3">${escapeHtml(story.quote)}</textarea></div>
      <div class="field" data-kind="testimonial"><label>${t('Yazar', 'Author')}</label><input data-story-field="author" aria-label="Hikaye ${index + 1} yazar" value="${escapeHtml(story.author)}"></div>
      <div class="field" data-kind="testimonial"><label>${t('Rol', 'Role')}</label><input data-story-field="role" aria-label="Hikaye ${index + 1} rol" value="${escapeHtml(story.role)}"></div>
      <div class="field wide" data-kind="product-proof"><label>${t('Başlık', 'Title')}</label><input data-story-field="title" aria-label="Hikaye ${index + 1} başlık" value="${escapeHtml(story.title)}"></div>
      <div class="field wide" data-kind="product-proof"><label>${t('Özet', 'Summary')}</label><textarea data-story-field="summary" aria-label="Hikaye ${index + 1} özet" rows="2">${escapeHtml(story.summary)}</textarea></div>
      <div class="field"><label>${t('Ürün ekran görüntüsü URL', 'Product screenshot URL')}</label><div class="input-action-group"><div class="input-thumbnail-preview">${story.mediaSrc ? `<img src="${escapeHtml(story.mediaSrc)}" alt="Önizleme">` : ''}</div><input data-story-field="mediaSrc" aria-label="Hikaye ${index + 1} ürün ekran görüntüsü URL" value="${escapeHtml(story.mediaSrc)}"><button class="text-action" type="button" data-asset-picker="screenshot" title="${t('Kütüphaneden Görsel Seç', 'Pick Asset')}">🖼️</button></div></div>
      <div class="field"><label>${t('Görsel alt metni', 'Image alt text')}</label><input data-story-field="mediaAlt" aria-label="Hikaye ${index + 1} görsel alt metni" value="${escapeHtml(story.mediaAlt)}"></div>
      <div class="field"><label>${t('Şirket logosu URL', 'Company logo URL')}</label><div class="input-action-group"><div class="input-thumbnail-preview">${story.companyLogoSrc ? `<img src="${escapeHtml(story.companyLogoSrc)}" alt="Önizleme">` : ''}</div><input data-story-field="companyLogoSrc" aria-label="Hikaye ${index + 1} şirket logosu URL" value="${escapeHtml(story.companyLogoSrc)}"><button class="text-action" type="button" data-asset-picker="logo" title="${t('Kütüphaneden Görsel Seç', 'Pick Asset')}">🖼️</button></div></div>
      <div class="field"><label>${t('Logo alt metni', 'Logo alt text')}</label><input data-story-field="companyLogoAlt" aria-label="Hikaye ${index + 1} logo alt metni" value="${escapeHtml(story.companyLogoAlt)}"></div>
      <div class="field"><label>${t('Sonuç değeri', 'Metric value')}</label><input data-story-field="metricValue" aria-label="Hikaye ${index + 1} sonuç değeri" placeholder="45+ saat" value="${escapeHtml(story.metricValue)}"></div>
      <div class="field"><label>${t('Sonuç etiketi', 'Metric label')}</label><input data-story-field="metricLabel" aria-label="Hikaye ${index + 1} sonuç etiketi" placeholder="${t('ayda kazanılan zaman', 'time saved per month')}" value="${escapeHtml(story.metricLabel)}"></div>
      <div class="field"><label>${t('Kaynak etiketi', 'Source label')}</label><input data-story-field="sourceLabel" aria-label="Hikaye ${index + 1} kaynak etiketi" value="${escapeHtml(story.sourceLabel)}"></div>
      <div class="field"><label>${t('Kaynak URL', 'Source URL')}</label><input data-story-field="sourceUrl" aria-label="Hikaye ${index + 1} kaynak URL" value="${escapeHtml(story.sourceUrl)}"></div>
    </div>
    ${canRemove ? `<button class="text-action" type="button" data-remove-story="${index}">${t('Hikayeyi kaldır', 'Remove story')}</button>` : ''}
  </fieldset>`;
}

function renderWizardStoryRail() {
  const storyRail = state.wizardDraft.storyRail;
  return wizardShell(`
    <form id="wizard-form" class="wizard-form">
      <div class="wizard-stage-heading"><span>6 / 8</span><h2>${t('Kaynaklı müşteri hikayelerini sıralayın', 'Sequence sourced customer stories')}</h2><p>${t('Bu adım isteğe bağlıdır. Her hikaye gerçek bir kaynak URL\'siyle desteklenmeli; AI hikaye uydurmaz.', 'This step is optional. Each story must be backed by a verifiable URL; AI never fabricates stories.')}</p></div>
      <label class="wizard-choice"><input id="wizard-storyrail-enabled" type="checkbox" name="storyRailEnabled" ${checked(storyRail.enabled)}><span><strong>${t('Müşteri hikaye rayı ekle', 'Add customer story rail')}</strong><small>${t('Yatay kaydırmalı, kaynaklı testimonial ve ürün kanıtı kartları', 'Horizontal carousel of sourced testimonial and product proof cards')}</small></span></label>
      <div class="wizard-storyrail-fields" data-wizard-storyrail-fields ${storyRail.enabled ? '' : 'hidden'}>
        <div class="form-grid">
          <div class="field"><label for="wizard-storyrail-heading">${t('Bölüm başlığı', 'Section heading')}</label><input id="wizard-storyrail-heading" name="storyRailHeading" ${storyRail.enabled ? 'required' : ''} value="${escapeHtml(storyRail.heading)}"></div>
          <div class="field wide"><label for="wizard-storyrail-intro">${t('Kısa açıklama', 'Short description')}</label><input id="wizard-storyrail-intro" name="storyRailIntro" value="${escapeHtml(storyRail.intro)}"></div>
          <div class="field"><label for="wizard-storyrail-background">${t('Arka plan rengi', 'Background color')}</label><input id="wizard-storyrail-background" name="storyRailBackground" pattern="#[0-9A-Fa-f]{3,6}" value="${escapeHtml(storyRail.background)}"></div>
          <div class="field"><label for="wizard-storyrail-foreground">${t('Metin rengi', 'Text color')}</label><input id="wizard-storyrail-foreground" name="storyRailForeground" pattern="#[0-9A-Fa-f]{3,6}" value="${escapeHtml(storyRail.foreground)}"></div>
          <div class="field"><label for="wizard-storyrail-accent">${t('Vurgu rengi', 'Accent color')}</label><input id="wizard-storyrail-accent" name="storyRailAccent" pattern="#[0-9A-Fa-f]{3,6}" value="${escapeHtml(storyRail.accent)}"></div>
          <div class="field"><label for="wizard-storyrail-advance">${t('Otomatik ilerleme (ms)', 'Auto advance (ms)')}</label><input id="wizard-storyrail-advance" name="storyRailAutoAdvance" type="number" min="0" max="20000" step="500" value="${escapeHtml(String(storyRail.autoAdvanceMs))}"></div>
        </div>
        <div class="wizard-card-list">${storyRail.stories.map(renderWizardStoryCard).join('')}</div>
        ${storyRail.stories.length < 8 ? '<button class="text-action" type="button" data-add-story>' + t('Hikaye ekle', 'Add story') + '</button>' : ''}
      </div>
    </form>`, '<fluent-button data-wizard-back appearance="outline">' + t('Geri', 'Back') + '</fluent-button><fluent-button data-wizard-next appearance="accent">' + t('Planı kontrol et', 'Review plan') + '</fluent-button>');
}

function renderWizardReview() {
  const draft = state.wizardDraft;
  const bundle = buildTemplateBundle(draft);
  const combinations = bundle.rows.reduce((total, row) => total + row.langs.length * row.variants.length, 0);
  const aiReady = Boolean(state.meta?.capabilities?.aiConfigured || state.meta?.capabilities?.openRouterConfigured);
  const brandExists = state.meta?.brands?.some((brand) => brand.id === bundle.brand.id);
  const canStart = !brandExists && aiReady;
  const aiModel = state.meta?.capabilities?.aiModel || state.meta?.capabilities?.openRouterModel || 'AI';
  const preflight = brandExists
    ? `<div class="wizard-callout" data-tone="error"><strong>${t('Marka id kullanımda', 'Brand ID already in use')}</strong><p>${escapeHtml(bundle.brand.id)} ${t('zaten var. Brief adımına dönüp yeni bir marka id seçin.', 'already exists. Return to Brief step and choose a unique Brand ID.')}</p></div>`
    : aiReady
      ? `<div class="wizard-callout" data-tone="success"><strong>${t('AI bağlantısı hazır', 'AI connection ready')}</strong><p>${escapeHtml(aiModel)} ${t('modeliyle üretim başlatılabilir.', 'model is ready for automated generation.')}</p></div>`
      : `<div class="wizard-callout" data-tone="error"><strong>${t('AI anahtarı gerekli', 'AI API key required')}</strong><p>${t('.env dosyasına AI_API_KEY ekleyip admin sunucusunu yeniden başlatın. Taslağınız korunur.', 'Add AI_API_KEY to your .env file and restart the admin server. Your draft is preserved.')}</p></div>`;
  return wizardShell(`
    <div class="wizard-stage-heading"><span>7 / 8</span><h2>${t('AI üretim planını kontrol edin', 'Review AI production plan')}</h2><p>${t('Onaydan sonra marka ve matrix birlikte oluşturulur. Ardından içerik üretimi ve build otomatik sırayla çalışır.', 'Upon approval, the brand and matrix are created. Content generation and build will run automatically in sequence.')}</p></div>
    <div class="wizard-review-grid">
      <article><span>${t('Marka ve sektör', 'Brand & industry')}</span><strong>${escapeHtml(bundle.brand.id)}</strong><small>${escapeHtml(bundle.brand.generationBrief.industry || t('Sektör belirtilmedi', 'No industry specified'))}</small></article>
      <article><span>${t('Sayfa planı', 'Page plan')}</span><strong>${bundle.rows.length}</strong><small>${combinations} ${t('dil ve varyant çıktısı', 'language & variant outputs')}</small></article>
      <article><span>Pricing</span><strong>${bundle.brand.pricing ? escapeHtml(bundle.brand.pricing.model) : t('Kapalı', 'Off')}</strong><small>${bundle.brand.pricing ? `${bundle.brand.pricing.plans.length} plan` : t('Fiyat uydurulmayacak', 'No price hallucinated')}</small></article>
      <article><span>${t('Müşteri kanıtı', 'Social proof')}</span><strong>${bundle.brand.socialProof ? t('Hazır', 'Ready') : t('Atlandı', 'Skipped')}</strong><small>${bundle.brand.socialProof ? t('Şema ile doğrulanacak', 'Validated with schema') : t('Daha sonra eklenebilir', 'Can be added later')}</small></article>
      <article><span>${t('Hikaye rayı', 'Story rail')}</span><strong>${bundle.brand.storyRail ? t('Hazır', 'Ready') : t('Atlandı', 'Skipped')}</strong><small>${bundle.brand.storyRail ? `${bundle.brand.storyRail.stories.length} ${t('hikaye', 'stories')}` : t('Daha sonra eklenebilir', 'Can be added later')}</small></article>
    </div>
    <section class="wizard-review-list"><h3>${t('Sayfalar', 'Pages')}</h3>${bundle.rows.map((row) => `<div class="wizard-review-row">
      <div class="wizard-review-row-info"><strong>${escapeHtml(row.pageId)}</strong><span>${escapeHtml(PAGE_TYPE_LABELS[row.pageType] || row.pageType)}</span><small>${row.langs.map(escapeHtml).join(', ')} / ${row.variants.map(escapeHtml).join(', ')}</small></div>
      ${pageSkeletonHtml(row.pageType)}
    </div>`).join('')}</section>
    ${preflight}
    <div class="wizard-callout"><strong>${t('AI güvenlik sınırı', 'AI safety boundaries')}</strong><p>${t('AI brief, marka tonu, CTA, sayfa hook\'u ve varsa doğrulanmış pricing bilgisini kullanır. Müşteri adı, testimonial ve sayısal sonuç uydurmaması prompt seviyesinde zorunludur.', 'AI utilizes brief, brand tone, CTA, hooks, and verified pricing. Hallucinating client names, testimonials, or metrics is forbidden at prompt level.')}</p></div>`,
  `<fluent-button data-wizard-back appearance="outline">${t('Geri', 'Back')}</fluent-button><fluent-button id="wizard-create-template" appearance="accent" ${canStart ? '' : 'disabled'}>${t('Şablonu oluştur ve AI üretimini başlat', 'Create template and launch AI generation')}</fluent-button>`);
}

function wizardStageState(stage) {
  const result = state.wizardDraft.result;
  if (!result) return 'waiting';
  if (stage === 'create') return 'success';
  if (stage === 'generate') {
    if (result.phase === 'generating') return 'running';
    if (result.phase === 'error' && result.errorStage === 'generate') return 'error';
    if (['building', 'ready'].includes(result.phase)) return 'success';
  }
  if (stage === 'build') {
    if (result.phase === 'building') return 'running';
    if (result.phase === 'error' && result.errorStage === 'build') return 'error';
    if (result.phase === 'ready') return 'success';
  }
  return 'waiting';
}

function renderWizardResult() {
  const result = state.wizardDraft.result;
  const stages = [
    ['create', t('Marka ve sayfa planı', 'Brand & page plan'), t('Doğrulandı ve kaydedildi', 'Validated and saved')],
    ['generate', t('AI içerik üretimi', 'AI content generation'), t('Brief ve sayfa hooklarına göre yapılandırılmış içerik', 'Structured copy matching brief and hooks')],
    ['build', t('Statik build', 'Static build'), t('Preview için production çıktısı', 'Production output for preview')],
  ];
  const ready = result?.phase === 'ready';
  const error = result?.phase === 'error';
  const generating = result?.phase === 'generating';
  const loadingTitle = generating ? t('İçerik sistemi brief üzerinde çalışıyor', 'AI engine is crafting copy from brief') : t('Production önizlemesi hazırlanıyor', 'Compiling production preview');
  const loadingDetail = generating
    ? result.completedFiles
      ? (currentLang === 'tr' ? `${result.completedFiles} / ${result.totalOutputs || '?'} içerik dosyası tamamlandı.` : `${result.completedFiles} / ${result.totalOutputs || '?'} content files completed.`)
      : (currentLang === 'tr' ? `${optionLabel(INDUSTRY_OPTIONS, state.wizardDraft.industry)} bağlamı ve sayfa planı modele aktarılıyor.` : `Streaming context and page matrix into model.`)
    : t('Üretilen içerikler doğrulanıyor ve statik sayfalar oluşturuluyor.', 'Validating generated copy and building static HTML.');
  const actions = ready
    ? `<fluent-button id="wizard-open-content" appearance="outline">${t('İçerikleri gözden geçir', 'Review Content')}</fluent-button><fluent-button id="wizard-open-preview" appearance="accent">${t('Önizlemeyi aç', 'Open Preview')}</fluent-button><fluent-button id="wizard-new-template" appearance="subtle">${t('Yeni şablon', 'New Template')}</fluent-button>`
    : error
      ? `<fluent-button id="wizard-retry" appearance="accent">${t('Hatalı adımdan devam et', 'Retry failed step')}</fluent-button><fluent-button id="wizard-open-jobs" appearance="outline">${t('Logları aç', 'Open logs')}</fluent-button>`
      : result?.phase === 'created'
        ? `<fluent-button id="wizard-resume-generate" appearance="accent">${t('AI üretimini başlat', 'Launch AI generation')}</fluent-button>`
        : `<fluent-button id="wizard-open-jobs" appearance="outline">${t('Canlı logları aç', 'Open live logs')}</fluent-button>`;
  return wizardShell(`
    <div class="wizard-stage-heading"><span>8 / 8</span><h2>${ready ? t('Şablon önizlemeye hazır', 'Template ready for preview') : error ? t('Akış bir adımda durdu', 'Workflow halted on step') : t('Şablon hazırlanıyor', 'Preparing template')}</h2><p>${ready ? t('AI içerikleri üretildi ve statik build tamamlandı. Canlıya almadan önce metinleri ve mobil görünümü kontrol edin.', 'AI copy generated and static build completed. Inspect copy and mobile responsiveness before publishing.') : error ? escapeHtml(result.error || t('İşlem tamamlanamadı. Logları inceleyip güvenli biçimde devam edebilirsiniz.', 'Operation could not complete. Review logs and resume safely.')) : t('İşlem arka planda ilerler. Bu sayfadan ayrılırsanız İşlemler ekranından durumu izleyebilirsiniz.', 'Job runs in the background. You can track progress in the Jobs screen if you navigate away.')}</p></div>
    ${!ready && !error ? `<div class="wizard-ai-loader" role="status" aria-live="polite"><div class="wizard-ai-loader-visual" aria-hidden="true"><span></span><span></span><span></span><span></span></div><div><strong>${loadingTitle}</strong><p>${escapeHtml(loadingDetail)}</p></div><fluent-progress-bar aria-label="${escapeHtml(loadingTitle)}"></fluent-progress-bar></div>` : ''}
    <div class="wizard-run-list">${stages.map(([id, title, description]) => {
      const stageState = wizardStageState(id);
      return `<article data-state="${stageState}"><span aria-hidden="true">${stageState === 'success' ? '✓' : stageState === 'error' ? '!' : stageState === 'running' ? '…' : ''}</span><div><strong>${title}</strong><small>${description}</small></div><b>${stageState === 'success' ? t('Tamamlandı', 'Done') : stageState === 'error' ? t('Hata', 'Error') : stageState === 'running' ? t('Çalışıyor', 'Running') : t('Bekliyor', 'Waiting')}</b></article>`;
    }).join('')}</div>
    ${error && result.log ? `<details class="wizard-error-log"><summary>${t('Son hata çıktısı', 'Latest error output')}</summary><pre>${escapeHtml(result.log)}</pre></details>` : ''}`,
  actions);
}

function collectWizardStep({ validate = true } = {}) {
  const form = document.querySelector('#wizard-form');
  if (!form) return true;
  if (validate && !form.reportValidity()) {
    const firstInvalid = form.querySelector(':invalid');
    if (firstInvalid) {
      try { firstInvalid.focus(); } catch {}
      const label = form.querySelector(`label[for="${firstInvalid.id}"]`)?.textContent
        || firstInvalid.getAttribute('aria-label')
        || firstInvalid.name
        || 'Zorunlu alan';
      showNotice(`${label}: Lütfen bu alanı doldurun veya geçerli bir değer girin.`, 'error');
    }
    return false;
  }
  const data = new FormData(form);
  const draft = state.wizardDraft;

  if (state.wizardStep === 0) {
    Object.assign(draft, {
      journeyGoal: String(data.get('journeyGoal') || ''),
      industry: String(data.get('industry') || ''),
      industryOther: String(data.get('industryOther') || '').trim(),
      businessModel: String(data.get('businessModel') || ''),
      projectStage: String(data.get('projectStage') || ''),
    });
    if (validate && draft.industry === 'other' && !draft.industryOther) return showNotice('Sektörünüzü kısaca yazın.', 'error'), false;
  }

  if (state.wizardStep === 1) {
    const additionalLanguages = String(data.get('additionalLanguages') || '');
    const languages = [...new Set([...data.getAll('languages'), ...splitWizardLines(additionalLanguages)])];
    if (validate && !languages.length) return showNotice('En az bir dil seçin.', 'error'), false;
    if (validate && languages.some((lang) => !/^[a-z]{2}(?:-[A-Z]{2})?$/.test(lang))) return showNotice('Ek dil kodları en veya de-CH biçiminde olmalı.', 'error'), false;
    Object.assign(draft, {
      brandId: String(data.get('brandId') || '').trim(),
      domain: String(data.get('domain') || '').trim(),
      companyBrief: String(data.get('companyBrief') || ''),
      audience: String(data.get('audience') || '').trim(),
      offer: String(data.get('offer') || '').trim(),
      objective: String(data.get('objective') || '').trim(),
      primaryCta: String(data.get('primaryCta') || '').trim(),
      differentiators: String(data.get('differentiators') || ''),
      notes: String(data.get('notes') || ''),
      languages,
      additionalLanguages,
    });
    if (!draft.waitlistEndpoint && draft.domain) draft.waitlistEndpoint = `https://${draft.domain}/waitlist`;
  }

  if (state.wizardStep === 2) {
    Object.assign(draft, {
      tone: String(data.get('tone') || '').trim(),
      primary: String(data.get('primary') || '').trim(),
      secondary: String(data.get('secondary') || '').trim(),
      font: String(data.get('font') || '').trim(),
      waitlistEndpoint: String(data.get('waitlistEndpoint') || '').trim(),
      designReferences: String(data.get('designReferences') || ''),
    });
    draft.pricing = {
      ...draft.pricing,
      model: String(data.get('pricingModel') || 'none'),
      heading: String(data.get('pricingHeading') || ''),
      intro: String(data.get('pricingIntro') || ''),
      productName: String(data.get('pricingProductName') || ''),
      features: String(data.get('pricingFeatures') || ''),
      footnote: String(data.get('pricingFootnote') || ''),
      plans: [...form.querySelectorAll('.wizard-plan-card')].map((card) => Object.fromEntries(
        [...card.querySelectorAll('[data-plan-field]')].map((input) => [input.dataset.planField, input.value]),
      )),
    };
    if (validate && draft.pricing.model !== 'none') {
      if (splitWizardLines(draft.pricing.features).length < 2) return showNotice('Pricing için en az iki ortak özellik yazın.', 'error'), false;
      if (draft.pricing.model === 'tiered' && draft.pricing.plans.length < 2) return showNotice('Çok planlı pricing en az iki plan gerektirir.', 'error'), false;
    }
  }

  if (state.wizardStep === 3) {
    draft.pages = [...form.querySelectorAll('.wizard-page-card')].map((card) => ({
      pageId: card.querySelector('[data-page-field="pageId"]').value.trim(),
      segment: card.querySelector('[data-page-field="segment"]').value.trim(),
      pageType: card.querySelector('[data-page-field="pageType"]').value,
      axis: card.querySelector('[data-page-field="axis"]').value,
      tier: card.querySelector('[data-page-field="tier"]').value,
      hook: card.querySelector('[data-page-field="hook"]').value.trim(),
      variants: [...card.querySelectorAll('[data-page-variant]:checked')].map((input) => input.dataset.pageVariant),
    }));
    if (validate && draft.pages.some((page) => !page.variants.length)) return showNotice('Her sayfa için en az bir varyant seçin.', 'error'), false;
  }

  if (state.wizardStep === 4) {
    draft.proof.enabled = data.get('proofEnabled') === 'on';
    draft.proof.relationship = String(data.get('logoWallRelationship') || 'customers');
    draft.proof.logoWallLabel = String(data.get('logoWallLabel') || '');
    draft.proof.testimonialHeading = String(data.get('testimonialHeading') || '');
    draft.proof.logos = [...form.querySelectorAll('.wizard-logo-card')].map((card) => Object.fromEntries(
      [...card.querySelectorAll('[data-logo-field]')].map((input) => [input.dataset.logoField, input.value]),
    ));
    draft.proof.testimonials = [...form.querySelectorAll('.wizard-testimonial-card')].map((card) => Object.fromEntries(
      [...card.querySelectorAll('[data-testimonial-field]')].map((input) => [input.dataset.testimonialField, input.value]),
    ));
    if (validate && draft.proof.enabled) {
      const logos = draft.proof.logos.filter((logo) => logo.name.trim() || logo.src.trim() || logo.href.trim());
      const partialLogo = logos.some((logo) => !logo.name.trim() || !logo.src.trim());
      if (partialLogo || (logos.length > 0 && logos.length < 2)) return showNotice('Logo bandı için en az iki eksiksiz logo girin.', 'error'), false;
      const testimonialFields = ['quote', 'author', 'role', 'company', 'sourceLabel', 'sourceUrl'];
      const filledTestimonials = draft.proof.testimonials.filter((testimonial) => testimonialFields.some((field) => testimonial[field].trim()));
      const partialTestimonial = filledTestimonials.some((testimonial) => testimonialFields.some((field) => !testimonial[field].trim()));
      if (partialTestimonial) return showNotice('Her testimonial için alıntı, yazar, rol, şirket ve kaynak alanlarını tamamlayın.', 'error'), false;
      if (filledTestimonials.length && !draft.proof.testimonialHeading.trim()) return showNotice('Testimonial eklediyseniz bölüm başlığı gerekli.', 'error'), false;
      if (!logos.length && !filledTestimonials.length) return showNotice('Kanıt adımını açtıysanız logo bandı veya kaynaklı testimonial ekleyin.', 'error'), false;
    }
  }

  if (state.wizardStep === 5) {
    draft.storyRail.enabled = data.get('storyRailEnabled') === 'on';
    draft.storyRail.heading = String(data.get('storyRailHeading') || '');
    draft.storyRail.intro = String(data.get('storyRailIntro') || '');
    draft.storyRail.background = String(data.get('storyRailBackground') || '').trim();
    draft.storyRail.foreground = String(data.get('storyRailForeground') || '').trim();
    draft.storyRail.accent = String(data.get('storyRailAccent') || '').trim();
    draft.storyRail.autoAdvanceMs = Number(data.get('storyRailAutoAdvance') || 6500);
    draft.storyRail.stories = [...form.querySelectorAll('.wizard-story-card')].map((card) => Object.fromEntries(
      [...card.querySelectorAll('[data-story-field]')].map((input) => [input.dataset.storyField, input.value]),
    ));
    if (validate && draft.storyRail.enabled) {
      if (!draft.storyRail.heading.trim()) return showNotice('Hikaye rayı için bölüm başlığı gerekli.', 'error'), false;
      const presenceFields = ['quote', 'author', 'role', 'title', 'summary', 'company', 'mediaSrc', 'sourceLabel', 'sourceUrl'];
      const filledStories = draft.storyRail.stories.filter((story) => presenceFields.some((field) => story[field]?.trim()));
      if (filledStories.length < 2) return showNotice('Hikaye rayı için en az iki kaynaklı hikaye ekleyin.', 'error'), false;
      const requiredByKind = {
        testimonial: ['quote', 'author', 'role', 'company', 'sourceLabel', 'sourceUrl'],
        'product-proof': ['title', 'summary', 'mediaSrc', 'company', 'sourceLabel', 'sourceUrl'],
      };
      const incomplete = filledStories.some((story) => requiredByKind[story.kind].some((field) => !story[field]?.trim()));
      if (incomplete) return showNotice('Her hikaye için türüne uygun tüm zorunlu alanları doldurun.', 'error'), false;
      const partialMetric = filledStories.some((story) => Boolean(story.metricValue?.trim()) !== Boolean(story.metricLabel?.trim()));
      if (partialMetric) return showNotice('Sonuç metriği eklediyseniz hem değer hem etiket girin.', 'error'), false;
    }
  }

  persistWizard();
  showNotice();
  return true;
}

function syncWizardVariantSupport() {
  document.querySelectorAll('.wizard-page-card').forEach((card) => {
    const typeId = card.querySelector('[data-page-field="pageType"]').value;
    const supported = state.meta.pageTypes.find((type) => type.id === typeId)?.supportedVariants || [];
    card.querySelectorAll('[data-page-variant]').forEach((input) => {
      input.disabled = !supported.includes(input.dataset.pageVariant);
      if (input.disabled) input.checked = false;
    });
    if (![...card.querySelectorAll('[data-page-variant]')].some((input) => input.checked && !input.disabled)) {
      const first = [...card.querySelectorAll('[data-page-variant]')].find((input) => !input.disabled);
      if (first) first.checked = true;
    }
    const skeleton = card.querySelector('[data-page-skeleton]');
    if (skeleton) skeleton.outerHTML = pageSkeletonHtml(typeId);
  });
}

async function createWizardTemplate() {
  const button = document.querySelector('#wizard-create-template');
  button?.setAttribute('disabled', '');
  try {
    const bundle = buildTemplateBundle(state.wizardDraft);
    await api('/api/templates', { method: 'POST', body: bundle });
    state.wizardDraft.result = {
      phase: 'created', brandId: bundle.brand.id, pageIds: bundle.rows.map((row) => row.pageId),
      currentJobId: null, errorStage: null, error: '', log: '', completedFiles: 0,
      totalOutputs: bundle.rows.reduce((total, row) => total + row.langs.length * row.variants.length, 0),
    };
    state.wizardStep = 7;
    persistWizard();
    await refreshMeta();
    setActiveBrand(bundle.brand.id);
    await startWizardJob('generate');
  } catch (error) {
    button?.removeAttribute('disabled');
    showNotice(error.message, 'error');
  }
}

async function startWizardJob(kind) {
  const result = state.wizardDraft.result;
  try {
    const job = await api('/api/jobs', {
      method: 'POST',
      body: { kind, brandId: result.brandId, pageIds: kind === 'generate' ? result.pageIds : [], langs: [], variants: [], force: false },
    });
    Object.assign(result, { phase: kind === 'generate' ? 'generating' : 'building', currentJobId: job.id, errorStage: null, error: '', log: '' });
    persistWizard();
    await renderTemplateWizard();
  } catch (error) {
    Object.assign(result, { phase: 'error', errorStage: kind, error: error.message, log: '' });
    persistWizard();
    await renderTemplateWizard();
  }
}

async function pollWizardJob() {
  const result = state.wizardDraft.result;
  if (!result?.currentJobId || !['generating', 'building'].includes(result.phase)) return;
  try {
    const job = await api(`/api/jobs/${result.currentJobId}`);
    if (job.status === 'running') {
      result.log = String(job.log || '').slice(-8000);
      if (job.kind === 'generate') result.completedFiles = countGeneratedContent(job.log);
      persistWizard();
      const detail = document.querySelector('.wizard-ai-loader p');
      if (detail && job.kind === 'generate' && result.completedFiles) detail.textContent = `${result.completedFiles} / ${result.totalOutputs || '?'} içerik dosyası tamamlandı.`;
      state.wizardTimer = setTimeout(pollWizardJob, 1200);
      return;
    }
    if (job.status === 'error') {
      Object.assign(result, { phase: 'error', errorStage: job.kind, error: `${job.kind === 'generate' ? 'AI içerik üretimi' : 'Build'} tamamlanamadı.`, log: String(job.log || '').slice(-4000) });
      persistWizard();
      await renderTemplateWizard();
      return;
    }
    if (job.kind === 'generate') {
      await startWizardJob('build');
      return;
    }
    Object.assign(result, { phase: 'ready', currentJobId: null, errorStage: null, error: '', log: '' });
    persistWizard();
    await renderTemplateWizard();
  } catch (error) {
    Object.assign(result, { phase: 'error', errorStage: result.phase === 'building' ? 'build' : 'generate', error: error.message, log: '' });
    persistWizard();
    await renderTemplateWizard();
  }
}

function syncWizardOtherIndustry() {
  const selectedIndustry = document.querySelector('input[name="industry"]:checked')?.value;
  const wrapper = document.querySelector('[data-industry-other]');
  const input = document.querySelector('#wizard-industry-other');
  if (!wrapper || !input) return;
  wrapper.hidden = selectedIndustry !== 'other';
  input.required = selectedIndustry === 'other';
}

async function prepareWizardBrief() {
  applyIntakeRecommendations(state.wizardDraft);
  persistWizard();
  state.wizardPreparing = true;
  await renderTemplateWizard();
  const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 80 : 950;
  state.wizardTimer = setTimeout(async () => {
    state.wizardPreparing = false;
    state.wizardStep = 1;
    persistWizard();
    await renderTemplateWizard();
  }, delay);
}

async function generateWizardBriefWithAI() {
  clearTimeout(state.wizardTimer);
  collectWizardStep({ validate: false });
  const draft = state.wizardDraft;
  const companyBrief = String(draft.companyBrief || '').trim();
  const button = document.querySelector('#wizard-generate-brief');
  const statusEl = document.querySelector('#wizard-generate-brief-status');
  if (!companyBrief) {
    showNotice(t('AI ile doldurmak için önce şirketiniz hakkında birkaç cümle yazın.', 'Write a few sentences about your company before generating with AI.'), 'error');
    return;
  }
  button?.setAttribute('disabled', '');
  if (statusEl) statusEl.textContent = t('AI brief\'i hazırlıyor…', 'AI is drafting your brief…');
  try {
    const indLabel = draft.industry === 'other'
      ? draft.industryOther
      : (currentLang === 'tr' ? optionLabel(INDUSTRY_OPTIONS, draft.industry) : (OPTION_TRANSLATIONS[draft.industry]?.[0] || draft.industry));
    const goalLabel = currentLang === 'tr' ? optionLabel(GOAL_OPTIONS, draft.journeyGoal) : (OPTION_TRANSLATIONS[draft.journeyGoal]?.[0] || draft.journeyGoal);
    const stageLabel = currentLang === 'tr' ? optionLabel(PROJECT_STAGE_OPTIONS, draft.projectStage) : (OPTION_TRANSLATIONS[draft.projectStage]?.[0] || draft.projectStage);
    const modelLabel = currentLang === 'tr' ? optionLabel(BUSINESS_MODEL_OPTIONS, draft.businessModel) : (OPTION_TRANSLATIONS[draft.businessModel]?.[0] || draft.businessModel);
    const res = await api('/api/wizard/generate-brief', {
      method: 'POST',
      body: {
        companyBrief,
        industryLabel: indLabel,
        businessModelLabel: modelLabel,
        projectStageLabel: stageLabel,
        journeyGoalLabel: goalLabel,
        lang: currentLang,
      },
    });
    const brief = res.brief || {};
    if (!draft.brandId.trim() && brief.brandId) draft.brandId = brief.brandId;
    if (!draft.domain.trim() && brief.domain) draft.domain = brief.domain;
    if (brief.audience) draft.audience = brief.audience;
    if (brief.offer) draft.offer = brief.offer;
    if (brief.objective) draft.objective = brief.objective;
    if (brief.primaryCta) draft.primaryCta = brief.primaryCta;
    if (Array.isArray(brief.differentiators) && brief.differentiators.length) draft.differentiators = brief.differentiators.join('\n');
    if (brief.notes) draft.notes = brief.notes;
    persistWizard();
    await renderTemplateWizard();
    showNotice(t('AI, brief alanlarını doldurdu. Gözden geçirip dilediğiniz gibi düzenleyebilirsiniz.', 'AI filled in the brief fields. Review and edit anything as needed.'), 'success');
  } catch (error) {
    showNotice(error.message, 'error');
    button?.removeAttribute('disabled');
    if (statusEl) statusEl.textContent = '';
  }
}

function bindWizardEvents() {
  document.querySelector('#wizard-form')?.addEventListener('input', () => {
    clearTimeout(state.wizardTimer);
    state.wizardTimer = setTimeout(() => collectWizardStep({ validate: false }), 250);
  });
  document.querySelector('[data-wizard-reset]')?.addEventListener('click', async () => {
    if (state.wizardDraft.result && !window.confirm('Sihirbaz taslağı temizlensin mi? Oluşturulmuş marka ve dosyalar korunur.')) return;
    localStorage.removeItem(WIZARD_DRAFT_KEY);
    localStorage.removeItem('landcraft-admin-template-wizard-v1');
    state.wizardDraft = createWizardDraft();
    state.wizardStep = 0;
    state.wizardPreparing = false;
    await renderTemplateWizard();
  });
  document.querySelector('[data-wizard-next]')?.addEventListener('click', async () => {
    if (!collectWizardStep()) return;
    if (state.wizardStep === 0) {
      await prepareWizardBrief();
      return;
    }
    state.wizardStep = Math.min(6, state.wizardStep + 1);
    persistWizard();
    await renderTemplateWizard();
  });
  document.querySelector('[data-wizard-back]')?.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    state.wizardStep = Math.max(0, state.wizardStep - 1);
    persistWizard();
    await renderTemplateWizard();
  });
  document.querySelectorAll('[data-wizard-jump]').forEach((button) => button.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    state.wizardStep = Number(button.dataset.wizardJump);
    persistWizard();
    await renderTemplateWizard();
  }));
  document.querySelector('#wizard-generate-brief')?.addEventListener('click', () => {
    generateWizardBriefWithAI();
  });

  document.querySelector('#wizard-pricing-model')?.addEventListener('change', async (event) => {
    collectWizardStep({ validate: false });
    state.wizardDraft.pricing.model = event.target.value;
    const minimum = event.target.value === 'tiered' ? 2 : 1;
    while (state.wizardDraft.pricing.plans.length < minimum) state.wizardDraft.pricing.plans.push(newWizardPlan(state.wizardDraft.pricing.plans.length));
    persistWizard();
    await renderTemplateWizard();
  });
  document.querySelector('[data-add-plan]')?.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    state.wizardDraft.pricing.plans.push(newWizardPlan(state.wizardDraft.pricing.plans.length));
    persistWizard();
    await renderTemplateWizard();
  });
  document.querySelectorAll('[data-remove-plan]').forEach((button) => button.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    state.wizardDraft.pricing.plans.splice(Number(button.dataset.removePlan), 1);
    persistWizard();
    await renderTemplateWizard();
  }));
  document.querySelector('[data-add-page]')?.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    const page = newWizardPage(state.wizardDraft.pages.length);
    page.hook = state.wizardDraft.objective;
    state.wizardDraft.pages.push(page);
    persistWizard();
    await renderTemplateWizard();
  });
  document.querySelectorAll('[data-remove-page]').forEach((button) => button.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    state.wizardDraft.pages.splice(Number(button.dataset.removePage), 1);
    persistWizard();
    await renderTemplateWizard();
  }));
  document.querySelectorAll('[data-page-field="pageType"]').forEach((select) => select.addEventListener('change', syncWizardVariantSupport));
  syncWizardVariantSupport();
  document.querySelectorAll('input[name="industry"]').forEach((input) => input.addEventListener('change', syncWizardOtherIndustry));
  syncWizardOtherIndustry();

  document.querySelector('#wizard-proof-enabled')?.addEventListener('change', (event) => {
    const fields = document.querySelector('[data-wizard-proof-fields]');
    fields.hidden = !event.target.checked;
  });
  document.querySelector('[data-add-logo]')?.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    state.wizardDraft.proof.logos.push(newWizardLogo());
    persistWizard();
    await renderTemplateWizard();
  });
  document.querySelectorAll('[data-remove-logo]').forEach((button) => button.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    state.wizardDraft.proof.logos.splice(Number(button.dataset.removeLogo), 1);
    persistWizard();
    await renderTemplateWizard();
  }));
  document.querySelector('[data-add-testimonial]')?.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    state.wizardDraft.proof.testimonials.push(newWizardTestimonial());
    persistWizard();
    await renderTemplateWizard();
  });
  document.querySelectorAll('[data-remove-testimonial]').forEach((button) => button.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    state.wizardDraft.proof.testimonials.splice(Number(button.dataset.removeTestimonial), 1);
    persistWizard();
    await renderTemplateWizard();
  }));

  document.querySelector('#wizard-storyrail-enabled')?.addEventListener('change', (event) => {
    const fields = document.querySelector('[data-wizard-storyrail-fields]');
    fields.hidden = !event.target.checked;
  });
  document.querySelector('[data-add-story]')?.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    state.wizardDraft.storyRail.stories.push(newWizardStory());
    persistWizard();
    await renderTemplateWizard();
  });
  document.querySelectorAll('[data-remove-story]').forEach((button) => button.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    state.wizardDraft.storyRail.stories.splice(Number(button.dataset.removeStory), 1);
    persistWizard();
    await renderTemplateWizard();
  }));
  document.querySelectorAll('[data-move-story-up]').forEach((button) => button.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    const index = Number(button.dataset.moveStoryUp);
    const stories = state.wizardDraft.storyRail.stories;
    if (index > 0) [stories[index - 1], stories[index]] = [stories[index], stories[index - 1]];
    persistWizard();
    await renderTemplateWizard();
  }));
  document.querySelectorAll('[data-move-story-down]').forEach((button) => button.addEventListener('click', async () => {
    collectWizardStep({ validate: false });
    const index = Number(button.dataset.moveStoryDown);
    const stories = state.wizardDraft.storyRail.stories;
    if (index < stories.length - 1) [stories[index], stories[index + 1]] = [stories[index + 1], stories[index]];
    persistWizard();
    await renderTemplateWizard();
  }));
  document.querySelectorAll('[data-story-kind-select]').forEach((select) => select.addEventListener('change', (event) => {
    const card = event.target.closest('.wizard-story-card');
    if (card) card.dataset.storyKind = event.target.value;
  }));
  document.querySelector('#wizard-create-template')?.addEventListener('click', createWizardTemplate);
  document.querySelector('#wizard-retry')?.addEventListener('click', () => startWizardJob(state.wizardDraft.result.errorStage));
  document.querySelector('#wizard-resume-generate')?.addEventListener('click', () => startWizardJob('generate'));
  document.querySelector('#wizard-open-jobs')?.addEventListener('click', () => {
    state.selectedJobId = state.wizardDraft.result?.currentJobId;
    navigate('jobs');
  });
  document.querySelector('#wizard-open-content')?.addEventListener('click', () => navigate('content'));
  document.querySelector('#wizard-open-preview')?.addEventListener('click', () => navigate('preview'));
  document.querySelector('#wizard-new-template')?.addEventListener('click', async () => {
    localStorage.removeItem(WIZARD_DRAFT_KEY);
    localStorage.removeItem('landcraft-admin-template-wizard-v1');
    state.wizardDraft = createWizardDraft();
    state.wizardStep = 0;
    state.wizardPreparing = false;
    await renderTemplateWizard();
  });
}

async function renderTemplateWizard() {
  await refreshMeta();
  clearTimeout(state.wizardTimer);
  if (state.wizardDraft.result) state.wizardStep = 7;
  const renderKey = state.wizardPreparing ? `preparing-${state.wizardStep}` : `step-${state.wizardStep}`;
  const focusNewStage = state.wizardRenderKey !== renderKey;
  state.wizardRenderKey = renderKey;
  content.innerHTML = state.wizardPreparing ? renderWizardPreparing()
    : state.wizardStep === 0 ? renderWizardIntake()
      : state.wizardStep === 1 ? renderWizardBrief()
        : state.wizardStep === 2 ? renderWizardDesign()
          : state.wizardStep === 3 ? renderWizardPages()
            : state.wizardStep === 4 ? renderWizardProof()
              : state.wizardStep === 5 ? renderWizardStoryRail()
                : state.wizardStep === 6 ? renderWizardReview()
                  : renderWizardResult();
  bindWizardEvents();
  if (focusNewStage) {
    const stageHeading = document.querySelector('.wizard-stage h2');
    stageHeading?.setAttribute('tabindex', '-1');
    stageHeading?.focus({ preventScroll: true });
    document.querySelector('.wizard-steps [aria-current="step"]')?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }
  if (['generating', 'building'].includes(state.wizardDraft.result?.phase)) state.wizardTimer = setTimeout(pollWizardJob, 400);
}

async function renderOverview() {
  await refreshMeta();
  const brands = state.meta.brands;
  const totals = brands.reduce((acc, brand) => ({
    rows: acc.rows + brand.rows,
    activeRows: acc.activeRows + brand.activeRows,
    contentFiles: acc.contentFiles + brand.contentFiles,
  }), { rows: 0, activeRows: 0, contentFiles: 0 });
  const disabled = brands.length ? '' : 'disabled';
  const activeBrand = brands.find((brand) => brand.id === state.brandId);
  content.innerHTML = `
    ${heading(t('Kontrol merkezi', 'Control Center'), t('Marka kataloğunun, içerik kapsamının ve üretim işlemlerinin güncel durumu.', 'Current status of brand catalog, content scope, and generation jobs.'), '<fluent-button id="start-template-wizard" appearance="accent">' + t('Yeni şablon üret', 'Generate new template') + '</fluent-button><fluent-button id="preview-brand" appearance="outline" ' + disabled + '>' + t('Sayfaları incele', 'Inspect pages') + '</fluent-button>')}
    <section class="metric-grid" aria-label="${t('Pagix özeti', 'Pagix summary')}">
      <article class="metric"><span>${t('Marka portföyü', 'Brand portfolio')}</span><strong>${brands.length}</strong><small>${t('yapılandırılmış marka', 'configured brands')}</small></article>
      <article class="metric"><span>${t('Sayfa matrisi', 'Page matrix')}</span><strong>${totals.rows}</strong><small>${t('toplam route tanımı', 'total route definitions')}</small></article>
      <article class="metric"><span>${t('Aktif kapsam', 'Active scope')}</span><strong>${totals.activeRows}</strong><small>${t('build edilen sayfa', 'built pages')}</small></article>
      <article class="metric"><span>${t('Hazır içerik', 'Ready content')}</span><strong>${totals.contentFiles}</strong><small>${t('JSON içerik dosyası', 'JSON content files')}</small></article>
    </section>
    <section class="panel command-panel">
      <div class="panel-heading"><div><h2>${t('Üretim merkezi', 'Generation Hub')}</h2><p>${activeBrand ? `${escapeHtml(activeBrand.id)} ${t('için içerik ve statik çıktı işlemleri.', 'content & static output operations.')}` : t('Başlamak için bir marka oluşturun.', 'Create a brand to get started.')}</p></div><span class="status" data-status="success">${t('Yerel ve güvenli', 'Local and secure')}</span></div>
      <div class="command-grid">
        <div class="field"><label for="overview-brand">${t('Aktif marka', 'Active brand')}</label><select id="overview-brand" ${disabled}>${brandOptions()}</select></div>
        <div class="command-actions">
          <fluent-button id="generate-missing" appearance="accent" ${disabled}>${t('Eksik içerikleri üret', 'Generate missing content')}</fluent-button>
          <fluent-button id="build-brand" appearance="outline" ${disabled}>${t('Statik build al', 'Run static build')}</fluent-button>
        </div>
      </div>
      <p class="supporting-copy">${t('Üretim mevcut dosyaları korur. Değişen JSON içeriklerini preview\'da görmek için yeni build alın.', 'Generation preserves existing files. Run a new build to see updated JSON content in preview.')}</p>
    </section>
    <section class="panel">
      <h2>${t('Yayın öncesi checklist', 'Pre-launch checklist')}</h2>
      <div class="data-wrap"><table><thead><tr><th>${t('Kontrol', 'Check')}</th><th>${t('Durum', 'Status')}</th><th>${t('Not', 'Notes')}</th></tr></thead><tbody>
        <tr><td>${t('Aktif içerik matrisi', 'Active content matrix')}</td><td><span class="status" data-status="success">${t('Otomatik', 'Automated')}</span></td><td>${t('Testler eksik dosya ve placeholder yayınını durdurur.', 'Tests halt deployment on missing files or placeholders.')}</td></tr>
        <tr><td>${t('Mobil ve klavye erişimi', 'Mobile & keyboard accessibility')}</td><td><span class="status" data-status="success">${t('Aktif', 'Active')}</span></td><td>${t('Viewport, dokunma hedefi ve reduced motion kontrolleri uygulanır.', 'Viewport, touch target, and reduced motion checks applied.')}</td></tr>
        <tr><td>${t('İnsan içerik onayı', 'Human copy review')}</td><td><span class="status">${t('Bekliyor', 'Pending')}</span></td><td>${t('AI içeriği canlıya çıkmadan marka sahibi tarafından onaylanmalı.', 'AI copy must be reviewed by brand owner before publishing.')}</td></tr>
        <tr><td>${t('Deploy ve analytics kimlikleri', 'Deploy & analytics IDs')}</td><td><span class="status">${t('Dış bağımlılık', 'External dependency')}</span></td><td>${t('Sunucu, Cloudflare ve gerçek ölçüm kimlikleri gerekir.', 'Requires production server, Cloudflare, and measurement IDs.')}</td></tr>
      </tbody></table></div>
    </section>
    <section class="panel" id="ai-status-panel">
      <div class="panel-heading"><div><h2>${t('AI servis durumu', 'AI service status')}</h2><p>${t('mindstack.ch bağlantısının anlık kontrolü.', 'Live check of the mindstack.ch connection.')}</p></div><span class="status" id="ai-status-badge">${t('Kontrol ediliyor…', 'Checking…')}</span></div>
      <p class="supporting-copy" id="ai-status-detail">${t('İlk kontrol yapılıyor…', 'Running first check…')}</p>
    </section>`;

  document.querySelector('#overview-brand')?.addEventListener('change', (event) => setActiveBrand(event.target.value));
  document.querySelector('#start-template-wizard')?.addEventListener('click', () => navigate('wizard'));
  document.querySelector('#preview-brand')?.addEventListener('click', () => navigate('preview'));
  document.querySelector('#generate-missing')?.addEventListener('click', () => startJob('generate').catch((error) => showNotice(error.message, 'error')));
  document.querySelector('#build-brand')?.addEventListener('click', () => startJob('build').catch((error) => showNotice(error.message, 'error')));
  refreshAiStatus();
}

const AI_STATUS_POLL_MS = 30000;

async function refreshAiStatus() {
  const badge = document.querySelector('#ai-status-badge');
  const detail = document.querySelector('#ai-status-detail');
  if (!badge || !detail) return;
  try {
    const result = await api('/api/ai-status');
    badge.dataset.status = result.ok ? 'success' : 'error';
    badge.textContent = result.ok ? t('Çalışıyor', 'Working') : t('Çalışmıyor', 'Down');
    const latency = typeof result.latencyMs === 'number' ? ` · ${result.latencyMs}ms` : '';
    const checkedAt = new Date().toLocaleTimeString(currentLang === 'tr' ? 'tr-TR' : 'en-US');
    detail.textContent = `${result.message || ''}${latency} · ${t('Model', 'Model')}: ${result.model || '—'} · ${t('Son kontrol', 'Last checked')}: ${checkedAt}`;
  } catch (error) {
    badge.dataset.status = 'error';
    badge.textContent = t('Çalışmıyor', 'Down');
    detail.textContent = error.message;
  } finally {
    if (state.section === 'overview') {
      state.aiStatusTimer = setTimeout(refreshAiStatus, AI_STATUS_POLL_MS);
    }
  }
}

async function renderBrands() {
  await refreshMeta();
  content.innerHTML = `
    ${heading(t('Markalar', 'Brands'), t('Tema, dil, analytics ve deploy sözleşmelerini yönetin.', 'Manage theme, language, analytics, and deployment contracts.'), '<fluent-button id="new-brand" appearance="accent">' + t('Yeni marka', 'New brand') + '</fluent-button>')}
    <div class="data-wrap"><table><thead><tr><th>${t('Marka', 'Brand')}</th><th>Domain</th><th>${t('Diller', 'Languages')}</th><th>${t('Aktif tier', 'Active tiers')}</th><th>${t('Sayfa', 'Pages')}</th><th>${t('İçerik', 'Content')}</th><th></th></tr></thead><tbody>
      ${state.meta.brands.length ? state.meta.brands.map((brand) => `<tr>
        <td><strong>${escapeHtml(brand.id)}</strong></td><td><a class="text-link" href="https://${escapeHtml(brand.domain)}" target="_blank" rel="noreferrer">${escapeHtml(brand.domain)}</a></td><td>${brand.languages.map(escapeHtml).join(', ')}</td>
        <td>${brand.activeTiers.map(escapeHtml).join(', ')}</td><td>${brand.activeRows}/${brand.rows}</td><td>${brand.contentFiles}</td>
        <td><div class="row-actions"><fluent-button appearance="subtle" data-preview-brand="${escapeHtml(brand.id)}">${t('Önizle', 'Preview')}</fluent-button><fluent-button appearance="subtle" data-edit-brand="${escapeHtml(brand.id)}">${t('Düzenle', 'Edit')}</fluent-button><fluent-button appearance="subtle" data-delete-brand="${escapeHtml(brand.id)}">${t('Sil', 'Delete')}</fluent-button></div></td>
      </tr>`).join('') : `<tr><td colspan="7" class="empty">${t('Henüz marka yok.', 'No brands configured yet.')}</td></tr>`}
    </tbody></table></div>`;
  document.querySelector('#new-brand').addEventListener('click', () => renderBrandEditor());
  document.querySelectorAll('[data-preview-brand]').forEach((button) => button.addEventListener('click', async () => {
    setActiveBrand(button.dataset.previewBrand);
    await navigate('preview');
  }));
  document.querySelectorAll('[data-edit-brand]').forEach((button) => button.addEventListener('click', async () => renderBrandEditor(await api(`/api/brands/${button.dataset.editBrand}`))));
  document.querySelectorAll('[data-delete-brand]').forEach((button) => button.addEventListener('click', async () => {
    const id = button.dataset.deleteBrand;
    if (!window.confirm(t(`${id} markası, matrix ve içerikleri geri alınabilir çöpe taşınacak. Devam edilsin mi?`, `${id} brand, matrix and content will be moved to recoverable trash. Continue?`))) return;
    try {
      const result = await api(`/api/brands/${id}`, { method: 'DELETE' });
      showNotice(`Marka çöpe taşındı: ${result.destination}`, 'success');
      await renderBrands();
    } catch (error) { showNotice(error.message, 'error'); }
  }));
}

function renderBrandEditor(value = {}) {
  const editing = Boolean(value.id);
  const brand = {
    id: '', domain: '', languages: ['en'], primaryCta: '', tone: '',
    theme: { primary: '#0f6cbd', secondary: '#242424', font: 'Segoe UI' },
    analytics: { ga4Id: null, googleAdsId: null, linkedInPartnerId: null },
    waitlistEndpoint: 'https://example.com/waitlist',
    deployTarget: { protocol: 'sftp', host: '', path: '' }, activeTiers: ['T1'],
    ...value,
  };
  const socialProof = brand.socialProof || {};
  const logoWall = socialProof.logoWall || {};
  const clientLogos = logoWall.logos || [];
  const testimonials = socialProof.testimonials || [];
  const pricing = brand.pricing || {};
  const pricingModel = pricing.model || 'none';
  const proofLegend = currentLang === 'tr'
    ? `Logo bandı ve müşteri kanıtları${editing ? '' : ' (ilk kurulum)'}`
    : `Logo wall & customer proof${editing ? '' : ' (initial setup)'}`;
  content.innerHTML = `
    ${heading(editing ? (currentLang === 'tr' ? `${brand.id} markasını düzenle` : `Edit brand ${brand.id}`) : t('Yeni marka', 'New brand'), t('Marka id oluşturulduktan sonra dosya yollarını korumak için değiştirilemez.', 'Brand ID cannot be modified after creation to preserve directory paths.'))}
    <form id="brand-form" class="panel form-grid">
      <div class="field"><label for="brand-id">${t('Marka id', 'Brand ID')}</label><input id="brand-id" name="id" required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value="${escapeHtml(brand.id)}" ${editing ? 'readonly' : ''}></div>
      <div class="field"><label for="brand-domain">Domain</label><input id="brand-domain" name="domain" required value="${escapeHtml(brand.domain)}"></div>
      <div class="field wide"><label for="brand-languages">${t('Diller', 'Languages')}</label><input id="brand-languages" name="languages" required value="${escapeHtml(brand.languages.join(', '))}"><small>${t('Virgülle ayırın: en, de', 'Comma-separated: en, de')}</small></div>
      <div class="field"><label for="brand-cta">${t('Birincil CTA', 'Primary CTA')}</label><input id="brand-cta" name="primaryCta" required value="${escapeHtml(brand.primaryCta)}"></div>
      <div class="field"><label for="brand-font">Font</label><input id="brand-font" name="font" required value="${escapeHtml(brand.theme.font)}"></div>
      <div class="field wide"><label for="brand-tone">${t('Marka tonu', 'Brand tone')}</label><textarea id="brand-tone" name="tone" required>${escapeHtml(brand.tone)}</textarea></div>
      <div class="field"><label for="brand-primary">${t('Primary renk', 'Primary color')}</label><input id="brand-primary" name="primary" required value="${escapeHtml(brand.theme.primary)}"></div>
      <div class="field"><label for="brand-secondary">${t('Secondary renk', 'Secondary color')}</label><input id="brand-secondary" name="secondary" required value="${escapeHtml(brand.theme.secondary)}"></div>
      <div class="field wide"><label for="brand-endpoint">Waitlist endpoint</label><input id="brand-endpoint" name="waitlistEndpoint" type="url" required value="${escapeHtml(brand.waitlistEndpoint)}"></div>
      <fieldset class="wide proof-onboarding">
        <legend>${proofLegend}</legend>
        <p>${editing ? t('Hero altındaki ilişki türü belirtilmiş logo bandını ve basit testimonial bölümünü buradan güncelleyebilirsiniz.', 'Update the relationship-typed logo wall and testimonial section below the hero.') : t('Markayı oluştururken doğrulanmış müşteri, entegrasyon veya iş ortağı logolarını ve kaynaklı testimonial bilgilerini ekleyin. Bu alanlar isteğe bağlıdır ve daha sonra düzenlenebilir.', 'Add verified client, integration, or partner logos and sourced testimonials. These fields are optional and can be edited anytime.')}</p>
        <div class="proof-fields">
          <div class="field"><label for="brand-logo-relationship">${t('Logo ilişkisi', 'Logo relationship')}</label><select id="brand-logo-relationship" name="logoWallRelationship">
            <option value="customers" ${selected(logoWall.relationship || 'customers', 'customers')}>${t('Müşteriler', 'Customers')}</option>
            <option value="integrations" ${selected(logoWall.relationship, 'integrations')}>${t('Entegrasyonlar', 'Integrations')}</option>
            <option value="partners" ${selected(logoWall.relationship, 'partners')}>${t('İş ortakları', 'Partners')}</option>
          </select><small>${t('Bant başlığının iddia ettiği ilişkiyi açıkça belirtir.', 'Explicitly states the claimed relationship.')}</small></div>
          <div class="field"><label for="brand-logo-label">${t('Logo bandı başlığı', 'Logo wall heading')}</label><input id="brand-logo-label" name="logoWallLabel" value="${escapeHtml(logoWall.label || 'Trusted by teams at')}"><small>${t('Logolar hero\'nun hemen altında gösterilir.', 'Logos are rendered directly below hero.')}</small></div>
          <div class="field"><label for="brand-testimonial-heading">${t('Testimonials başlığı', 'Testimonials heading')}</label><input id="brand-testimonial-heading" name="testimonialHeading" value="${escapeHtml(socialProof.testimonialHeading || 'What customers say')}"><small>${t('Gelişmiş story rail yoksa basit testimonial bölümü kullanılır.', 'Simple testimonial section is used if advanced story rail is absent.')}</small></div>
          <div class="field wide">
            <div style="display:flex; justify-content:space-between; align-items:center;">
              <label for="brand-client-logos">${t('Doğrulanmış logolar', 'Verified logos')}</label>
              <div class="field-actions" style="display:flex; gap:0.4rem; align-items:center;">
                <span id="brand-client-logos-status" style="font-size:0.68rem; font-weight:600; padding:0.15rem 0.45rem; border-radius:4px; transition:all 0.15s ease;"></span>
                <button class="small-action-btn" type="button" data-textarea-id="brand-client-logos" data-action="pick-asset" title="${t('Görsel Seç ve Ekle', 'Pick Asset')}">🖼️ ${t('Seç', 'Pick')}</button>
                <button class="small-action-btn" type="button" data-textarea-id="brand-client-logos" data-action="beautify" title="${t('JSON Formatla', 'Format JSON')}">✨ ${t('Formatla', 'Format')}</button>
              </div>
            </div>
            <textarea id="brand-client-logos" name="clientLogos" rows="8" spellcheck="false">${escapeHtml(clientLogos.length ? JSON.stringify(clientLogos, null, 2) : '')}</textarea>
            <small>${t('JSON array. Her logo için name, src ve isteğe bağlı href. Seçilen ilişki türüne ait en az 2 gerçek logo ekleyin.', 'JSON array. Each logo requires name, src, and optional href. Minimum 2 verified logos.')}</small>
          </div>
          <div class="field wide">
            <div style="display:flex; justify-content:space-between; align-items:center;">
              <label for="brand-testimonials">Testimonials</label>
              <div class="field-actions" style="display:flex; gap:0.4rem; align-items:center;">
                <span id="brand-testimonials-status" style="font-size:0.68rem; font-weight:600; padding:0.15rem 0.45rem; border-radius:4px; transition:all 0.15s ease;"></span>
                <button class="small-action-btn" type="button" data-textarea-id="brand-testimonials" data-action="pick-asset" title="${t('Görsel Seç ve Ekle', 'Pick Asset')}">🖼️ ${t('Seç', 'Pick')}</button>
                <button class="small-action-btn" type="button" data-textarea-id="brand-testimonials" data-action="beautify" title="${t('JSON Formatla', 'Format JSON')}">✨ ${t('Formatla', 'Format')}</button>
              </div>
            </div>
            <textarea id="brand-testimonials" name="testimonials" rows="10" spellcheck="false">${escapeHtml(testimonials.length ? JSON.stringify(testimonials, null, 2) : '')}</textarea>
            <small>${t('JSON array. quote, author, role, company, sourceLabel ve doğrulanabilir sourceUrl zorunludur.', 'JSON array. quote, author, role, company, sourceLabel, and verifiable sourceUrl are required.')}</small>
          </div>
        </div>
      </fieldset>
      <fieldset class="wide pricing-onboarding">
        <legend>${t('Pricing bölümü' + (editing ? '' : ' (sayfa üretimi)'), 'Pricing section' + (editing ? '' : ' (page generation)'))}</legend>
        <p>${t('Marka sayfalarında fiyatlandırma gösterilecekse modeli seçin. Seçilmediğinde pricing bölümü hiçbir sayfada render edilmez.', 'Select pricing model if prices should be rendered. If set to none, pricing section is omitted.')}</p>
        <div class="field">
          <label for="brand-pricing-model">${t('Pricing modeli', 'Pricing model')}</label>
          <select id="brand-pricing-model" name="pricingModel">
            <option value="none" ${pricingModel === 'none' ? 'selected' : ''}>${t('Pricing bölümü yok', 'No pricing section')}</option>
            <option value="one-time" ${pricingModel === 'one-time' ? 'selected' : ''}>${t('Tek seferlik ödeme', 'One-time payment')}</option>
            <option value="subscription" ${pricingModel === 'subscription' ? 'selected' : ''}>${t('Abonelik', 'Subscription')}</option>
            <option value="tiered" ${pricingModel === 'tiered' ? 'selected' : ''}>${t('Çok planlı', 'Tiered')}</option>
          </select>
        </div>
        <div class="pricing-fields" data-pricing-fields ${pricingModel === 'none' ? 'hidden' : ''}>
          <div class="field"><label for="brand-pricing-heading">${t('Bölüm başlığı', 'Section heading')}</label><input id="brand-pricing-heading" name="pricingHeading" data-pricing-required value="${escapeHtml(pricing.heading || '')}"></div>
          <div class="field"><label for="brand-pricing-product">${t('Ürün veya paket adı', 'Product or package name')}</label><input id="brand-pricing-product" name="pricingProductName" data-pricing-required value="${escapeHtml(pricing.productName || '')}"></div>
          <div class="field wide"><label for="brand-pricing-intro">${t('Kısa açıklama', 'Short description')}</label><textarea id="brand-pricing-intro" name="pricingIntro" rows="3">${escapeHtml(pricing.intro || '')}</textarea></div>
          <div class="field wide"><label for="brand-pricing-features">${t('Özellikler', 'Features')}</label><textarea id="brand-pricing-features" name="pricingFeatures" rows="7" data-pricing-required>${escapeHtml(pricing.features?.join('\n') || '')}</textarea><small>${t('Her satıra bir özellik yazın. En az 2, en fazla 8 özellik.', 'One feature per line. Min 2, max 8 features.')}</small></div>
          <div class="field wide">
            <div style="display:flex; justify-content:space-between; align-items:center;">
              <label for="brand-pricing-plans">${t('Planlar', 'Plans')}</label>
              <div class="field-actions" style="display:flex; gap:0.4rem; align-items:center;">
                <span id="brand-pricing-plans-status" style="font-size:0.68rem; font-weight:600; padding:0.15rem 0.45rem; border-radius:4px; transition:all 0.15s ease;"></span>
                <button class="small-action-btn" type="button" data-textarea-id="brand-pricing-plans" data-action="beautify" title="${t('JSON Formatla', 'Format JSON')}">✨ ${t('Formatla', 'Format')}</button>
              </div>
            </div>
            <textarea id="brand-pricing-plans" name="pricingPlans" rows="12" spellcheck="false" data-pricing-required>${escapeHtml(pricing.plans?.length ? JSON.stringify(pricing.plans, null, 2) : '')}</textarea>
            <small>${t('JSON array. Her plan için id, name, billingLabel, price, ctaLabel ve güvenli ctaHref zorunludur. priceDetail ve description isteğe bağlıdır.', 'JSON array. id, name, billingLabel, price, ctaLabel, and ctaHref required.')}</small>
          </div>
          <div class="field wide"><label for="brand-pricing-footnote">${t('Alt not', 'Footnote')}</label><input id="brand-pricing-footnote" name="pricingFootnote" value="${escapeHtml(pricing.footnote || '')}"></div>
        </div>
      </fieldset>
      <div class="field wide">
        <div style="display:flex; justify-content:space-between; align-items:center;">
          <label for="brand-story-rail">Customer stories rail</label>
          <div class="field-actions" style="display:flex; gap:0.4rem; align-items:center;">
            <span id="brand-story-rail-status" style="font-size:0.68rem; font-weight:600; padding:0.15rem 0.45rem; border-radius:4px; transition:all 0.15s ease;"></span>
            <button class="small-action-btn" type="button" data-textarea-id="brand-story-rail" data-action="pick-asset" title="${t('Görsel Seç ve Ekle', 'Pick Asset')}">🖼️ ${t('Seç', 'Pick')}</button>
            <button class="small-action-btn" type="button" data-textarea-id="brand-story-rail" data-action="beautify" title="${t('JSON Formatla', 'Format JSON')}">✨ ${t('Formatla', 'Format')}</button>
          </div>
        </div>
        <textarea id="brand-story-rail" name="storyRail" rows="14" spellcheck="false">${escapeHtml(brand.storyRail ? JSON.stringify(brand.storyRail, null, 2) : '')}</textarea>
        <small>${t('İsteğe bağlı. Kaynaklı testimonial ve ürün arayüzü kartları için JSON yapılandırması.', 'Optional. JSON config for sourced testimonials & product UI cards.')}</small>
      </div>
      <div class="field"><label for="brand-ga4">GA4 id</label><input id="brand-ga4" name="ga4Id" value="${escapeHtml(brand.analytics.ga4Id || '')}"></div>
      <div class="field"><label for="brand-ads">Google Ads id</label><input id="brand-ads" name="googleAdsId" value="${escapeHtml(brand.analytics.googleAdsId || '')}"></div>
      <div class="field"><label for="brand-linkedin">LinkedIn Partner id</label><input id="brand-linkedin" name="linkedInPartnerId" value="${escapeHtml(brand.analytics.linkedInPartnerId || '')}"></div>
      <div class="field"><label for="brand-protocol">${t('Deploy protokolü', 'Deploy protocol')}</label><select id="brand-protocol" name="protocol"><option value="sftp" ${brand.deployTarget.protocol === 'sftp' ? 'selected' : ''}>sftp</option><option value="rsync" ${brand.deployTarget.protocol === 'rsync' ? 'selected' : ''}>rsync</option></select></div>
      <div class="field"><label for="brand-host">Deploy host</label><input id="brand-host" name="host" value="${escapeHtml(brand.deployTarget.host)}"></div>
      <div class="field"><label for="brand-path">Deploy path</label><input id="brand-path" name="path" value="${escapeHtml(brand.deployTarget.path)}"></div>
      <fieldset class="wide"><legend>${t('Aktif tier', 'Active tiers')}</legend><div class="checks">${['T1', 'T2', 'T3'].map((tier) => `<label><input type="checkbox" name="activeTiers" value="${tier}" ${brand.activeTiers.includes(tier) ? 'checked' : ''}>${tier}</label>`).join('')}</div></fieldset>
      <div class="form-actions"><fluent-button id="cancel-brand" appearance="outline">${t('Vazgeç', 'Cancel')}</fluent-button><fluent-button id="save-brand" appearance="accent">${t('Kaydet', 'Save')}</fluent-button></div>
    </form>`;
  const pricingModelInput = document.querySelector('#brand-pricing-model');
  const pricingFields = document.querySelector('[data-pricing-fields]');
  const syncPricingFields = () => {
    const enabled = pricingModelInput.value !== 'none';
    pricingFields.hidden = !enabled;
    pricingFields.querySelectorAll('input, textarea').forEach((input) => {
      input.disabled = !enabled;
      input.required = enabled && input.hasAttribute('data-pricing-required');
    });
  };
  pricingModelInput.addEventListener('change', syncPricingFields);
  syncPricingFields();
  document.querySelector('#cancel-brand').addEventListener('click', () => renderBrands());
  document.querySelector('#save-brand').addEventListener('click', async () => {
    const form = document.querySelector('#brand-form');
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    const nullable = (name) => String(data.get(name) || '').trim() || null;
    let storyRail;
    let clientLogos;
    let testimonials;
    let pricingPlans;
    try {
      storyRail = parseOptionalJson(data.get('storyRail'), 'Customer stories rail');
      clientLogos = parseOptionalJson(data.get('clientLogos'), 'Müşteri logoları');
      testimonials = parseOptionalJson(data.get('testimonials'), 'Testimonials');
      if (data.get('pricingModel') !== 'none') {
        pricingPlans = parseOptionalJson(data.get('pricingPlans'), 'Pricing planları');
      }
    } catch (error) {
      showNotice(error.message, 'error');
      return;
    }
    if (clientLogos && !Array.isArray(clientLogos)) {
      showNotice('Müşteri logoları bir JSON array olmalı.', 'error');
      return;
    }
    if (testimonials && !Array.isArray(testimonials)) {
      showNotice('Testimonials bir JSON array olmalı.', 'error');
      return;
    }
    if (data.get('pricingModel') !== 'none' && !Array.isArray(pricingPlans)) {
      showNotice('Pricing planları bir JSON array olmalı.', 'error');
      return;
    }
    const logoWallLabel = String(data.get('logoWallLabel') || '').trim();
    const testimonialHeading = String(data.get('testimonialHeading') || '').trim();
    const socialProofPayload = {
      ...(clientLogos?.length ? {
        logoWall: {
          relationship: String(data.get('logoWallRelationship') || 'customers'),
          label: logoWallLabel,
          logos: clientLogos,
        },
      } : {}),
      ...(testimonials?.length ? { testimonialHeading, testimonials } : {}),
    };
    const hasSocialProof = Boolean(socialProofPayload.logoWall || socialProofPayload.testimonials);
    const pricingModelValue = String(data.get('pricingModel'));
    const pricingIntro = String(data.get('pricingIntro') || '').trim();
    const pricingFootnote = String(data.get('pricingFootnote') || '').trim();
    const pricingPayload = pricingModelValue === 'none' ? undefined : {
      model: pricingModelValue,
      heading: String(data.get('pricingHeading') || '').trim(),
      intro: pricingIntro || undefined,
      productName: String(data.get('pricingProductName') || '').trim(),
      features: String(data.get('pricingFeatures') || '').split('\n').map((item) => item.trim()).filter(Boolean),
      plans: pricingPlans,
      footnote: pricingFootnote || undefined,
    };
    const payload = {
      id: String(data.get('id')).trim(), domain: String(data.get('domain')).trim(),
      languages: String(data.get('languages')).split(',').map((item) => item.trim()).filter(Boolean),
      primaryCta: String(data.get('primaryCta')).trim(), tone: String(data.get('tone')).trim(),
      theme: { primary: String(data.get('primary')).trim(), secondary: String(data.get('secondary')).trim(), font: String(data.get('font')).trim() },
      analytics: { ga4Id: nullable('ga4Id'), googleAdsId: nullable('googleAdsId'), linkedInPartnerId: nullable('linkedInPartnerId') },
      waitlistEndpoint: String(data.get('waitlistEndpoint')).trim(),
      deployTarget: { protocol: data.get('protocol'), host: String(data.get('host')), path: String(data.get('path')) },
      activeTiers: data.getAll('activeTiers'),
      ...(brand.customerProof ? { customerProof: brand.customerProof } : {}),
      ...(brand.generationBrief ? { generationBrief: brand.generationBrief } : {}),
      ...(hasSocialProof ? { socialProof: socialProofPayload } : {}),
      ...(storyRail ? { storyRail } : {}),
      ...(pricingPayload ? { pricing: pricingPayload } : {}),
    };
    try {
      await api(editing ? `/api/brands/${brand.id}` : '/api/brands', { method: editing ? 'PUT' : 'POST', body: payload });
      showNotice('Marka kaydedildi.', 'success');
      await renderBrands();
    } catch (error) { showNotice(error.message, 'error'); }
  });

  const bindGenericValidation = (textareaId, statusId) => {
    const ta = document.getElementById(textareaId);
    const st = document.getElementById(statusId);
    if (!ta || !st) return;
    const validate = () => {
      const val = ta.value.trim();
      if (!val) {
        st.textContent = '';
        st.style.background = 'transparent';
        return;
      }
      try {
        JSON.parse(val);
        st.textContent = '✓ Geçerli JSON';
        st.style.background = 'rgba(16, 124, 65, 0.1)';
        st.style.color = 'var(--success)';
      } catch (err) {
        st.textContent = `Hatalı JSON: ${err.message.substring(0, 30)}`;
        st.style.background = 'rgba(180, 35, 24, 0.1)';
        st.style.color = 'var(--danger)';
      }
    };
    ta.addEventListener('input', validate);
    validate();
  };

  bindGenericValidation('brand-client-logos', 'brand-client-logos-status');
  bindGenericValidation('brand-testimonials', 'brand-testimonials-status');
  bindGenericValidation('brand-pricing-plans', 'brand-pricing-plans-status');
  bindGenericValidation('brand-story-rail', 'brand-story-rail-status');
}

async function renderMatrix() {
  await refreshMeta();
  await loadContentCombos();
  if (!state.brandId) {
    content.innerHTML = `${heading(t('Sayfa matrisi', 'Page Matrix'), t('Önce bir marka oluşturun.', 'Create a brand first.'))}<div class="empty">${t('Matrix düzenlemek için marka gerekli.', 'A brand is required to manage page matrix.')}</div>`;
    return;
  }
  const rows = await api(`/api/brands/${state.brandId}/matrix`);
  content.innerHTML = `
    ${heading(t('Sayfa matrisi', 'Page Matrix'), t('Segment, tier, dil, varyant ve sayfa tipi sözleşmelerini yönetin.', 'Manage segment, tier, language, variant, and page type contracts.'), '<fluent-button id="new-row" appearance="accent">' + t('Yeni satır', 'New row') + '</fluent-button>')}
    <div class="toolbar" style="display:flex; flex-wrap:wrap; gap:0.75rem; align-items:end; margin-bottom:1.25rem;">
      <div class="field" style="flex:1 1 12rem;"><label for="matrix-brand">${t('Marka', 'Brand')}</label><select id="matrix-brand">${brandOptions()}</select></div>
      <div class="field" style="flex:2 1 18rem;"><label for="matrix-search">${t('Ara', 'Search')}</label><input type="search" id="matrix-search" placeholder="${t('Segment, ID, dil, varyant ara...', 'Search segment, ID, language, variant...')}"></div>
      <div class="field" style="flex:1 1 8rem;"><label for="matrix-filter-tier">${t('Tier Filtresi', 'Tier Filter')}</label><select id="matrix-filter-tier"><option value="all">${t("Tüm Tier'lar", 'All Tiers')}</option><option value="T1">T1</option><option value="T2">T2</option><option value="T3">T3</option></select></div>
      <div class="field" style="flex:1 1 10rem;"><label for="matrix-filter-type">${t('Sayfa Tipi Filtresi', 'Page Type Filter')}</label><select id="matrix-filter-type"><option value="all">${t('Tüm Tipler', 'All Types')}</option>${state.meta.pageTypes.map(typ => `<option value="${escapeHtml(typ.id)}">${escapeHtml(typ.id)}</option>`).join('')}</select></div>
    </div>
    <div class="data-wrap"><table><thead><tr><th>Page id</th><th>Segment</th><th>Tier</th><th>${t('Sayfa tipi', 'Page type')}</th><th>${t('Dil', 'Languages')}</th><th>${t('Varyant', 'Variants')}</th><th>${t('Canlı Web Linki (Effective URL)', 'Effective Web Link')}</th><th></th></tr></thead><tbody>
      ${rows.length ? rows.map((row) => {
        const brand = state.meta.brands.find(b => b.id === state.brandId);
        const matrixDomain = (brand?.routing?.primaryDomain || brand?.domain || (state.brandId + '.net')).replace(/^https?:\/\//, '').replace(/\/+$/, '');
        const basePath = (brand?.routing?.basePath || '/lp').replace(/\/+$/, '');
        const normalizedBasePath = basePath.startsWith('/') ? basePath : '/' + basePath;
        const matrixLiveUrl = `https://${matrixDomain}${normalizedBasePath}/${row.langs[0]}/${row.segment}/`;
        const hasContent = state.contentCombos.some(c => c.row.pageId === row.pageId && c.exists);
        const isTierActive = !brand?.activeTiers?.length || brand.activeTiers.includes(row.tier);
        const isPublished = hasContent && isTierActive;
        const liveCellContent = isPublished ? `
          <a class="text-link" href="${escapeHtml(matrixLiveUrl)}" target="_blank" rel="noreferrer" style="font-family:ui-monospace,monospace;font-size:0.8rem;word-break:break-all;">${escapeHtml(matrixLiveUrl)} ↗</a>
          ${row.variants.includes('paid') ? `<div style="font-size:0.75rem;margin-top:2px;"><a class="text-link" href="https://${matrixDomain}${normalizedBasePath}/go/${row.langs[0]}/${row.segment}/" target="_blank" rel="noreferrer" style="opacity:0.75;font-family:ui-monospace,monospace;">paid: .../go/${row.langs[0]}/${row.segment}/ ↗</a></div>` : ''}
        ` : `
          <div style="display:flex;align-items:center;gap:0.35rem;flex-wrap:wrap;">
            <span style="font-family:ui-monospace,monospace;font-size:0.8rem;color:var(--text-muted,#64748b);word-break:break-all;">${escapeHtml(matrixLiveUrl)}</span>
            <span style="font-size:0.68rem;padding:1px 6px;border-radius:4px;font-weight:500;${hasContent ? 'background:#e0f2fe;color:#0369a1;' : 'background:#f1f5f9;color:#64748b;'}">
              ${hasContent ? t('Build Gerekli', 'Build required') : t('Planlandı (Henüz üretilmedi)', 'Planned (Not built yet)')}
            </span>
          </div>
        `;
        return `<tr>
          <td><code>${escapeHtml(row.pageId)}</code></td>
          <td><a class="text-link" href="${previewUrl({ row, lang: row.langs[0], variant: row.variants[0] })}" data-preview-row="${escapeHtml(row.pageId)}">${escapeHtml(row.segment)}</a></td>
          <td>${row.tier}</td>
          <td>${escapeHtml(row.pageType)}</td>
          <td>${row.langs.map(escapeHtml).join(', ')}</td>
          <td>${row.variants.map(escapeHtml).join(', ')}</td>
          <td>${liveCellContent}</td>
          <td><div class="row-actions"><fluent-button appearance="subtle" data-preview-row-button="${escapeHtml(row.pageId)}">${t('Önizle', 'Preview')}</fluent-button><fluent-button appearance="subtle" data-edit-row="${escapeHtml(row.pageId)}">${t('Düzenle', 'Edit')}</fluent-button><fluent-button appearance="subtle" data-delete-row="${escapeHtml(row.pageId)}">${t('Sil', 'Delete')}</fluent-button></div></td>
        </tr>`;
      }).join('') : `<tr><td colspan="8" class="empty">${t('Matrix boş.', 'Matrix is empty.')}</td></tr>`}
    </tbody></table></div>`;
  document.querySelector('#matrix-brand').addEventListener('change', async (event) => { setActiveBrand(event.target.value); await renderMatrix(); });
  
  const searchInput = document.querySelector('#matrix-search');
  const tierFilter = document.querySelector('#matrix-filter-tier');
  const typeFilter = document.querySelector('#matrix-filter-type');

  const filterMatrixTable = () => {
    const query = searchInput.value.toLowerCase().trim();
    const tier = tierFilter.value;
    const pageType = typeFilter.value;

    const tableRows = document.querySelectorAll('.data-wrap tbody tr');
    tableRows.forEach((tr) => {
      if (tr.querySelector('.empty')) return;
      
      const pageId = tr.children[0].textContent.toLowerCase();
      const segment = tr.children[1].textContent.toLowerCase();
      const rowTier = tr.children[2].textContent;
      const rowType = tr.children[3].textContent;
      const langs = tr.children[4].textContent.toLowerCase();
      const variants = tr.children[5].textContent.toLowerCase();

      const matchesQuery = !query ||
        pageId.includes(query) ||
        segment.includes(query) ||
        rowType.includes(query) ||
        langs.includes(query) ||
        variants.includes(query);

      const matchesTier = tier === 'all' || rowTier === tier;
      const matchesType = pageType === 'all' || rowType === pageType;

      if (matchesQuery && matchesTier && matchesType) {
        tr.style.display = '';
      } else {
        tr.style.display = 'none';
      }
    });
  };

  searchInput.addEventListener('input', filterMatrixTable);
  tierFilter.addEventListener('change', filterMatrixTable);
  typeFilter.addEventListener('change', filterMatrixTable);

  document.querySelector('#new-row').addEventListener('click', () => renderMatrixEditor());
  const previewRow = (pageId) => {
    const row = rows.find((item) => item.pageId === pageId);
    if (row) openPreview({ row, lang: row.langs[0], variant: row.variants[0] });
  };
  document.querySelectorAll('[data-preview-row]').forEach((link) => link.addEventListener('click', (event) => {
    event.preventDefault();
    previewRow(link.dataset.previewRow);
  }));
  document.querySelectorAll('[data-preview-row-button]').forEach((button) => button.addEventListener('click', () => previewRow(button.dataset.previewRowButton)));
  document.querySelectorAll('[data-edit-row]').forEach((button) => button.addEventListener('click', async () => {
    try {
      renderMatrixEditor(await api(`/api/brands/${state.brandId}/matrix/${button.dataset.editRow}`));
    } catch (error) {
      showNotice(error.message, 'error');
    }
  }));
  document.querySelectorAll('[data-delete-row]').forEach((button) => button.addEventListener('click', async () => {
    const pageId = button.dataset.deleteRow;
    if (!window.confirm(t(`${pageId} satırı silinsin mi? İlgili içerikler korunur.`, `Delete row ${pageId}? Associated content files are preserved.`))) return;
    try {
      await api(`/api/brands/${state.brandId}/matrix/${pageId}`, { method: 'DELETE' });
      showNotice(t('Matrix satırı silindi.', 'Matrix row deleted.'), 'success');
      await renderMatrix();
    } catch (error) { showNotice(error.message, 'error'); }
  }));
}

function renderMatrixEditor(row = { pageId: '', segment: '', axis: 'use-case', tier: 'T1', pageType: 'b2b-saas-matrix', langs: ['en'], variants: ['organic'], hook: '' }) {
  const editing = Boolean(row.pageId);
  content.innerHTML = `
    ${heading(editing ? (currentLang === 'tr' ? `${row.pageId} satırını düzenle` : `Edit row ${row.pageId}`) : t('Yeni matrix satırı', 'New matrix row'), t(`${state.brandId} için route ve içerik sözleşmesini tanımlayın.`, `Define route and content contract for ${state.brandId}.`))}
    <form id="matrix-form" class="panel form-grid">
      <div class="field"><label for="row-id">Page id</label><input id="row-id" name="pageId" required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value="${escapeHtml(row.pageId)}" ${editing ? 'readonly' : ''}></div>
      <div class="field"><label for="row-segment">${t('URL segmenti', 'URL segment')}</label><input id="row-segment" name="segment" required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value="${escapeHtml(row.segment)}"></div>
      <div class="field"><label for="row-axis">${t('Eksen', 'Axis')}</label><input id="row-axis" name="axis" required value="${escapeHtml(row.axis)}"></div>
      <div class="field"><label for="row-page-type">${t('Sayfa tipi', 'Page type')}</label><select id="row-page-type" name="pageType">${pageTypeOptions(row.pageType)}</select></div>
      <div class="field"><label for="row-tier">Tier</label><select id="row-tier" name="tier">${['T1', 'T2', 'T3'].map((tier) => `<option ${tier === row.tier ? 'selected' : ''}>${tier}</option>`).join('')}</select></div>
      <div class="field"><label for="row-langs">${t('Diller', 'Languages')}</label><input id="row-langs" name="langs" required value="${escapeHtml(row.langs.join(', '))}"></div>
      <fieldset class="wide"><legend>${t('Varyantlar', 'Variants')}</legend><div class="checks">${['organic', 'paid'].map((variant) => `<label><input type="checkbox" name="variants" value="${variant}" ${row.variants.includes(variant) ? 'checked' : ''}>${variant}</label>`).join('')}</div></fieldset>
      <div class="field wide"><label for="row-hook">${t('Çekirdek mesaj', 'Core hook')}</label><textarea id="row-hook" name="hook" required>${escapeHtml(row.hook)}</textarea></div>
      <div class="form-actions"><fluent-button id="cancel-row" appearance="outline">${t('Vazgeç', 'Cancel')}</fluent-button><fluent-button id="save-row" appearance="accent">${t('Kaydet', 'Save')}</fluent-button></div>
    </form>`;
  document.querySelector('#cancel-row').addEventListener('click', () => renderMatrix());
  document.querySelector('#save-row').addEventListener('click', async () => {
    const form = document.querySelector('#matrix-form');
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    const payload = {
      pageId: String(data.get('pageId')).trim(), segment: String(data.get('segment')).trim(), axis: String(data.get('axis')).trim(),
      hook: String(data.get('hook')).trim(), tier: data.get('tier'), pageType: data.get('pageType'),
      langs: String(data.get('langs')).split(',').map((item) => item.trim()).filter(Boolean), variants: data.getAll('variants'),
    };
    try {
      await api(editing ? `/api/brands/${state.brandId}/matrix/${row.pageId}` : `/api/brands/${state.brandId}/matrix`, { method: editing ? 'PUT' : 'POST', body: payload });
      showNotice(t('Matrix satırı kaydedildi.', 'Matrix row saved.'), 'success');
      await renderMatrix();
    } catch (error) { showNotice(error.message, 'error'); }
  });
}

async function loadContentCombos() {
  const [rows, files] = await Promise.all([
    api(`/api/brands/${state.brandId}/matrix`),
    api(`/api/brands/${state.brandId}/content`),
  ]);
  const existing = new Set(files.filter((file) => !file.invalidName).map((file) => `${file.lang}/${file.pageId}/${file.variant}`));
  state.contentCombos = rows.flatMap((row) => row.langs.flatMap((lang) => row.variants.map((variant) => ({
    row, lang, variant, exists: existing.has(`${lang}/${row.pageId}/${variant}`),
  }))));
  if (state.selectedContentIndex >= state.contentCombos.length) state.selectedContentIndex = 0;
}

async function renderPreview() {
  await refreshMeta();
  if (!state.brandId) {
    content.innerHTML = `${heading(t('Sayfa önizleme', 'Page Preview'), t('Önce bir marka oluşturun.', 'Create a brand first.'))}<div class="empty-state"><strong>${t('Önizlenecek marka yok', 'No brand to preview')}</strong><p>${t('Marka ve matrix satırı oluşturduğunuzda sayfalar burada görünecek.', 'Pages will appear here once you create a brand and matrix rows.')}</p></div>`;
    return;
  }
  await loadContentCombos();
  const selected = state.contentCombos.find((combo) => previewKey(combo) === state.previewKey)
    ?? state.contentCombos.find((combo) => combo.exists)
    ?? state.contentCombos[0];
  if (selected) state.previewKey = previewKey(selected);

  const brand = state.meta.brands.find(b => b.id === state.brandId);
  const primaryDomain = (brand?.routing?.primaryDomain || brand?.domain || (state.brandId + '.net')).replace(/^https?:\/\//, '').replace(/\/+$/, '');
  const basePath = (brand?.routing?.basePath || '/lp').replace(/\/+$/, '');
  const normalizedBasePath = basePath.startsWith('/') ? basePath : '/' + basePath;
  const variantSubPath = selected?.variant === 'paid' ? '/go/' : '/';
  const effectiveLiveUrl = selected ? (`https://${primaryDomain}${normalizedBasePath}${variantSubPath}${selected.lang}/${selected.row.segment}/`) : '';

  content.innerHTML = `
    ${heading(t('Sayfa önizleme', 'Page Preview'), t('Build edilmiş landing page çıktısını masaüstü, tablet ve telefon ölçülerinde inceleyin.', 'Inspect built landing page output across desktop, tablet, and mobile viewport sizes.'), '<fluent-button id="build-preview-brand" appearance="accent">' + t('Yeni build al', 'Run new build') + '</fluent-button>')}
    <div class="preview-layout">
      <aside class="panel preview-index" aria-label="${t('Önizlenebilir sayfalar', 'Previewable pages')}">
        <div class="field"><label for="preview-brand">${t('Marka', 'Brand')}</label><select id="preview-brand">${brandOptions()}</select></div>
        <div class="field"><label for="preview-search">${t('Sayfa ara', 'Search pages')}</label><input id="preview-search" type="search" placeholder="${t('Segment, dil veya varyant', 'Segment, language, or variant')}"></div>
        <div class="preview-list">
          ${state.contentCombos.length ? state.contentCombos.map((combo) => `<button type="button" data-preview-key="${escapeHtml(previewKey(combo))}" data-search="${escapeHtml(`${combo.row.pageId} ${combo.row.segment} ${combo.lang} ${combo.variant}`.toLowerCase())}" aria-current="${previewKey(combo) === state.previewKey ? 'true' : 'false'}"><span><strong>${escapeHtml(combo.row.segment)}</strong><small>${escapeHtml(combo.lang)} / ${escapeHtml(combo.variant)}</small></span><span class="preview-row-status-group"><span class="status" data-status="${combo.exists ? 'success' : 'missing'}">${combo.exists ? t('Hazır', 'Ready') : t('Eksik', 'Missing')}</span>${!combo.exists ? `<span class="preview-generate-btn" data-generate-key="${escapeHtml(previewKey(combo))}" role="button" tabindex="0" title="${t('Yapay Zeka ile Üret', 'Generate with AI')}" aria-label="${t('Yapay Zeka ile Üret', 'Generate with AI')}">✨</span>` : ''}</span></button>`).join('') : `<div class="empty">${t('Matrix içinde önizlenecek sayfa yok.', 'No previewable pages in matrix.')}</div>`}
        </div>
      </aside>
      <section class="preview-workspace">
        ${selected ? `
          <div class="preview-toolbar">
            <div class="preview-address" title="${escapeHtml(effectiveLiveUrl)}">
              <strong>${escapeHtml(selected.row.segment)}</strong>
              <a href="${escapeHtml(effectiveLiveUrl)}" target="_blank" rel="noreferrer" style="color:var(--accent,#0f6cbd);text-decoration:none;font-family:ui-monospace,monospace;font-size:0.8rem;margin-left:0.5rem;" title="${t('Canlı Web Linki', 'Effective Live URL')}">${escapeHtml(effectiveLiveUrl)} ↗</a>
            </div>
            <div class="device-switch" aria-label="${t('Preview cihaz ölçüsü', 'Preview viewport size')}">
              ${['desktop', 'tablet', 'mobile'].map((device) => `<button type="button" data-device="${device}" aria-pressed="${state.previewDevice === device}">${device === 'desktop' ? t('Masaüstü', 'Desktop') : device === 'tablet' ? t('Tablet', 'Tablet') : t('Telefon', 'Mobile')}</button>`).join('')}
            </div>
            <fluent-button id="reload-preview" appearance="subtle">${t('Yenile', 'Reload')}</fluent-button>
            <div style="display:flex;gap:0.5rem;">
              <a class="button-link" href="${previewUrl(selected)}" target="_blank" rel="noreferrer">${t('Önizleme ↗', 'Preview ↗')}</a>
              <a class="button-link" href="${escapeHtml(effectiveLiveUrl)}" target="_blank" rel="noreferrer" style="font-weight:600;">${t('Canlı Web Linki ↗', 'Live URL ↗')}</a>
            </div>
          </div>
          <div class="preview-canvas" data-device="${state.previewDevice}">
            <div class="preview-frame-shell" data-loading="true">
              <div class="preview-loading" aria-hidden="true"><span></span><span></span><span></span></div>
              <iframe id="page-preview" title="${escapeHtml(selected.row.segment)} ${t('sayfa önizlemesi', 'page preview')}" src="${previewUrl(selected)}" sandbox="allow-scripts allow-same-origin allow-forms allow-popups"></iframe>
            </div>
          </div>
          <p class="preview-note">${t('Preview son statik build\'i gösterir. İçerik değişikliğinden sonra yeni build alın.', 'Preview displays the latest static build. Run a new build after editing content.')}</p>
        ` : `<div class="empty-state"><strong>${t('Önizlenecek sayfa yok', 'No pages to preview')}</strong><p>${t('Önce bu marka için matrix satırı oluşturun.', 'Create a matrix row for this brand first.')}</p></div>`}
      </section>
      <div class="preview-inspector-wrap">
        <div class="inspector-resize-handle" id="inspector-resize-handle" title="${t('Genişliği ayarla', 'Drag to resize')}"></div>
        <button class="inspector-toggle-btn" id="inspector-toggle-btn" aria-label="${t('Paneli kapat/aç', 'Toggle inspector panel')}" title="${t('Paneli kapat/aç', 'Toggle inspector')}"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg></button>
        <aside class="panel preview-inspector" id="preview-inspector" aria-label="${t('Özellikler Paneli', 'Properties Panel')}">
          <div class="preview-inspector-placeholder">
            <div style="text-align: center; color: var(--muted); padding: 3rem 1rem;">
              <span style="font-size: 2.5rem; display: block; margin-bottom: 1rem;">🎯</span>
              <strong>${t('Öğe Seçilmedi', 'No Element Selected')}</strong>
              <p style="font-size: 0.75rem; margin-top: 0.5rem; color: var(--muted);">${t('Önizleme ekranından herhangi bir başlığa, paragrafa veya görsele tıklayarak anında düzenlemeye başlayın.', 'Click on any heading, paragraph, or image in the preview to start editing.')}</p>
            </div>
          </div>
          <div class="preview-inspector-content" style="display: none; height: 100%; flex-direction: column; gap: 1rem;"></div>
        </aside>
      </div>
    </div>
    <div id="preview-inline-toolbar" role="toolbar" aria-label="Inline edit toolbar"></div>`;


  // ── Inspector Panel: Collapse Toggle + Drag-to-Resize ─────────────────────
  (function setupInspectorResizer() {
    const layout = document.querySelector('.preview-layout');
    const wrap   = document.getElementById('inspector-resize-handle')?.closest('.preview-inspector-wrap');
    const toggleBtn = document.getElementById('inspector-toggle-btn');
    const resizeHandle = document.getElementById('inspector-resize-handle');
    if (!layout || !wrap || !toggleBtn || !resizeHandle) return;

    const MIN_W = 240;  // px
    const MAX_W = 560;  // px
    const CHEVRON_LEFT = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>`;
    const CHEVRON_RIGHT = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>`;

    // Restore saved state
    const savedW = parseInt(localStorage.getItem('inspectorWidth') || '0', 10);
    const savedCollapsed = localStorage.getItem('inspectorCollapsed') === 'true';

    function applyWidth(px) {
      const clamped = Math.min(MAX_W, Math.max(MIN_W, px));
      document.documentElement.style.setProperty('--inspector-w', `${clamped}px`);
      localStorage.setItem('inspectorWidth', String(clamped));
    }

    function setCollapsed(collapsed) {
      if (collapsed) {
        layout.classList.add('inspector-collapsed');
        wrap.classList.add('inspector-collapsed');
        toggleBtn.innerHTML = CHEVRON_LEFT;
        toggleBtn.setAttribute('aria-label', t('Paneli aç', 'Expand panel'));
        toggleBtn.setAttribute('title', t('Paneli aç', 'Expand panel'));
      } else {
        layout.classList.remove('inspector-collapsed');
        wrap.classList.remove('inspector-collapsed');
        toggleBtn.innerHTML = CHEVRON_RIGHT;
        toggleBtn.setAttribute('aria-label', t('Paneli kapat', 'Collapse panel'));
        toggleBtn.setAttribute('title', t('Paneli kapat', 'Collapse panel'));
      }
      localStorage.setItem('inspectorCollapsed', String(collapsed));
    }

    if (savedW >= MIN_W) applyWidth(savedW);
    setCollapsed(savedCollapsed);

    // Toggle collapse on button click
    toggleBtn.addEventListener('click', () => {
      const isCollapsed = layout.classList.contains('inspector-collapsed');
      setCollapsed(!isCollapsed);
    });

    // Drag-to-resize
    let dragging = false;
    let startX = 0;
    let startW = 0;

    resizeHandle.addEventListener('mousedown', (e) => {
      if (layout.classList.contains('inspector-collapsed')) return;
      dragging = true;
      startX = e.clientX;
      startW = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--inspector-w'), 10) || 320;
      resizeHandle.classList.add('dragging');
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (!dragging) return;
      const delta = startX - e.clientX; // dragging left = expand, right = shrink
      applyWidth(startW + delta);
    });

    document.addEventListener('mouseup', () => {
      if (!dragging) return;
      dragging = false;
      resizeHandle.classList.remove('dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    });
  })();




  document.querySelector('#preview-brand')?.addEventListener('change', async (event) => {
    setActiveBrand(event.target.value);
    await renderPreview();
  });
  document.querySelector('#preview-search')?.addEventListener('input', (event) => {
    const query = event.target.value.trim().toLowerCase();
    document.querySelectorAll('[data-preview-key]').forEach((button) => {
      button.hidden = Boolean(query) && !button.dataset.search.includes(query);
    });
  });
  document.querySelectorAll('[data-preview-key]').forEach((button) => button.addEventListener('click', async () => {
    state.previewKey = button.dataset.previewKey;
    await renderPreview();
  }));
  // "Eksik" (missing) içerik satırlarındaki ✨ butonu: AI wizard'daki ile aynı
  // üretim akışını (startJob('generate', ...)) bu satıra özel olarak tetikler.
  const triggerGenerateForKey = async (key) => {
    const combo = state.contentCombos.find((item) => previewKey(item) === key);
    if (!combo) return;
    try {
      await startJob('generate', {
        pageIds: [combo.row.pageId],
        langs: [combo.lang],
        variants: [combo.variant],
        force: combo.exists,
      });
    } catch (error) {
      showNotice(error.message, 'error');
    }
  };
  document.querySelectorAll('[data-generate-key]').forEach((el) => {
    el.addEventListener('click', (event) => {
      event.stopPropagation();
      triggerGenerateForKey(el.dataset.generateKey);
    });
    el.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        triggerGenerateForKey(el.dataset.generateKey);
      }
    });
  });
  document.querySelectorAll('[data-device]').forEach((button) => button.addEventListener('click', () => {
    state.previewDevice = button.dataset.device;
    document.querySelector('.preview-canvas').dataset.device = state.previewDevice;
    document.querySelectorAll('[data-device]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
  }));
  document.querySelector('#reload-preview')?.addEventListener('click', () => {
    const frame = document.querySelector('#page-preview');
    const shell = document.querySelector('.preview-frame-shell');
    shell.dataset.loading = 'true';
    frame.src = frame.src;
  });
  document.querySelector('#page-preview')?.addEventListener('load', () => {
    document.querySelector('.preview-frame-shell').dataset.loading = 'false';
    const frame = document.querySelector('#page-preview');
    setupInlineEditing(frame, selected);
  });
  document.querySelector('#build-preview-brand')?.addEventListener('click', () => startJob('build').catch((error) => showNotice(error.message, 'error')));
}

let originalElementText = '';
let activeEditableElement = null;
let activeEditableIFrame = null;
let activeEditableSelected = null;
let activeEditableContent = null;
let activeEditableBrandData = null;

// ── Floating Inline Toolbar ────────────────────────────────────────────────

function showInlineToolbar(el, frame, selected, pageContent, brandData) {
  const toolbar = document.getElementById('preview-inline-toolbar');
  if (!toolbar) return;

  // Compute element position relative to viewport using iframe offset
  const frameRect = frame.getBoundingClientRect();
  const iframeDoc = frame.contentDocument || frame.contentWindow.document;
  const elRect = el.getBoundingClientRect();

  // elRect is in iframe coordinate space – add iframe's viewport position
  const top = frameRect.top + elRect.top - 44; // 44px above the element
  const centerX = frameRect.left + elRect.left + elRect.width / 2;

  const tagName = el.tagName;
  const isImg = tagName === 'IMG';
  const isLink = tagName === 'A';
  const hasKey = Boolean(el.dataset.inlineKey);

  let buttons = '';
  if (isImg) {
    buttons = `
      <button class="inline-tb-btn tb-accent" data-tb-action="img-library">🖼️ ${t('Kütüphane', 'Library')}</button>
      <span class="inline-tb-sep"></span>
      <button class="inline-tb-btn" data-tb-action="img-upload">📁 ${t('Yükle', 'Upload')}</button>
      <span class="inline-tb-sep"></span>
      <button class="inline-tb-btn" data-tb-action="img-url">🔗 URL</button>
    `;
  } else if (isLink) {
    buttons = `
      <button class="inline-tb-btn tb-accent" data-tb-action="link-edit">✏️ ${t('Metni Düzenle', 'Edit Text')}</button>
      <span class="inline-tb-sep"></span>
      <button class="inline-tb-btn" data-tb-action="link-href">🔗 ${t('URL Değiştir', 'Change URL')}</button>
    `;
  } else {
    buttons = `
      <button class="inline-tb-btn tb-accent" data-tb-action="text-edit">✏️ ${t('Düzenle', 'Edit')}</button>
      ${hasKey ? `<span class="inline-tb-sep"></span><button class="inline-tb-btn" data-tb-action="text-ai">✨ AI</button>` : ''}
    `;
  }

  toolbar.innerHTML = buttons;
  toolbar.style.left = `${Math.max(8, centerX - toolbar.offsetWidth / 2)}px`;
  toolbar.style.top = `${Math.max(8, top)}px`;
  toolbar.classList.add('is-visible');

  // After layout, re-center now we know width
  requestAnimationFrame(() => {
    const w = toolbar.offsetWidth;
    const newLeft = Math.max(8, Math.min(centerX - w / 2, window.innerWidth - w - 8));
    toolbar.style.left = `${newLeft}px`;
  });

  // Wire up toolbar button actions
  toolbar.querySelectorAll('[data-tb-action]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const action = btn.dataset.tbAction;

      if (action === 'img-library') {
        openAssetLibraryModal(state.brandId, (selectedUrl) => {
          el.src = selectedUrl;
          const srcInput = document.getElementById('inspector-img-src');
          if (srcInput) srcInput.value = selectedUrl;
        });
      } else if (action === 'img-upload') {
        const fi = document.createElement('input');
        fi.type = 'file';
        fi.accept = 'image/*';
        fi.addEventListener('change', async () => {
          const file = fi.files[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = async () => {
            try {
              const res = await api('/api/upload', {
                method: 'POST',
                body: { brandId: state.brandId, filename: file.name, content: reader.result }
              });
              el.src = res.url;
              const srcInput = document.getElementById('inspector-img-src');
              if (srcInput) srcInput.value = res.url;
              showNotice(t('Görsel yüklendi.', 'Image uploaded.'), 'success');
              // Auto-save
              const path = el.dataset.inlineKey;
              if (path) await savePathValue(path, res.url, selected);
            } catch (err) {
              showNotice(err.message, 'error');
            }
          };
          reader.readAsDataURL(file);
        });
        fi.click();
      } else if (action === 'img-url') {
        const newUrl = prompt(t('Yeni görsel URL girin:', 'Enter new image URL:'), el.src);
        if (newUrl && newUrl.trim()) {
          el.src = newUrl.trim();
          const srcInput = document.getElementById('inspector-img-src');
          if (srcInput) srcInput.value = newUrl.trim();
        }
      } else if (action === 'text-edit' || action === 'link-edit') {
        // Focus inspector panel textarea or text input
        const textArea = document.getElementById('inspector-text-val') || document.getElementById('inspector-link-text');
        if (textArea) { textArea.focus(); textArea.select(); }
      } else if (action === 'text-ai') {
        const textInput = document.getElementById('inspector-text-val');
        if (textInput) {
          openAiAlternatives(el, frame, selected, pageContent, brandData, textInput);
        }
      } else if (action === 'link-href') {
        const hrefInput = document.getElementById('inspector-link-href');
        if (hrefInput) { hrefInput.focus(); hrefInput.select(); }
      }
    });
  });
}

function hideInlineToolbar() {
  const toolbar = document.getElementById('preview-inline-toolbar');
  if (toolbar) toolbar.classList.remove('is-visible');
}

/** Generic path-based value saver used by toolbar and inspector save buttons */
async function savePathValue(path, newValue, selected) {
  if (path.startsWith('content:')) {
    const latest = await api(`/api/brands/${state.brandId}/content/${selected.lang}/${selected.row.pageId}/${selected.variant}`);
    if (latest) {
      setValueAtPath(latest, path.substring(8), newValue);
      await api(`/api/brands/${state.brandId}/content/${selected.lang}/${selected.row.pageId}/${selected.variant}`, {
        method: 'PUT', body: latest
      });
    }
  } else if (path.startsWith('brand:')) {
    const latest = await api(`/api/brands/${state.brandId}`);
    if (latest) {
      setValueAtPath(latest, path.substring(6), newValue);
      await api(`/api/brands/${state.brandId}`, { method: 'PUT', body: latest });
    }
  }
}


function findValuePath(obj, targetValue, prefix = '') {
  const normalize = (v) => String(v || '').trim()
    .replace(/^https?:\/\/[^/]+/, '')
    .split('?')[0]
    .split('#')[0];
  const targetNorm = normalize(targetValue);
  if (!targetNorm) return null;

  for (const [key, val] of Object.entries(obj)) {
    if (typeof val === 'string') {
      if (normalize(val) === targetNorm) {
        return `${prefix}${key}`;
      }
    } else if (Array.isArray(val)) {
      for (let i = 0; i < val.length; i++) {
        if (typeof val[i] === 'string') {
          if (normalize(val[i]) === targetNorm) {
            return `${prefix}${key}.${i}`;
          }
        } else if (val[i] && typeof val[i] === 'object') {
          const subPath = findValuePath(val[i], targetValue, `${prefix}${key}.${i}.`);
          if (subPath) return subPath;
        }
      }
    } else if (val && typeof val === 'object') {
      const subPath = findValuePath(val, targetValue, `${prefix}${key}.`);
      if (subPath) return subPath;
    }
  }
  return null;
}

function setValueAtPath(obj, path, newValue) {
  const parts = path.split('.');
  let curr = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!(parts[i] in curr)) return;
    curr = curr[parts[i]];
  }
  curr[parts[parts.length - 1]] = newValue;
}

async function showInspector(el, frame, selected, pageContent, brandData) {
  const inspector = document.getElementById('preview-inspector');
  if (!inspector) return;

  const placeholder = inspector.querySelector('.preview-inspector-placeholder');
  const contentEl = inspector.querySelector('.preview-inspector-content');
  if (!placeholder || !contentEl) return;

  placeholder.style.display = 'none';
  contentEl.style.display = 'flex';

  const tagName = el.tagName;
  activeEditableElement = el;
  activeEditableIFrame = frame;
  activeEditableSelected = selected;
  activeEditableContent = pageContent;
  activeEditableBrandData = brandData;
  originalElementText = el.dataset.scrollRevealText || el.textContent;

  // Show context-aware floating toolbar above the selected element
  showInlineToolbar(el, frame, selected, pageContent, brandData);

  if (tagName === 'IMG') {
    const src = el.getAttribute('src');
    let path = el.dataset.inlineKey
      || findValuePath(pageContent, src, 'content:')
      || findValuePath(brandData, src, 'brand:');

    if (!path && src) {
      if (src.includes('photo-1758873269811')) path = 'content:heroPhoto';
      else if (src.includes('photo-1758518729711')) path = 'content:featurePhoto';
      else if (src.includes('photo-1758876017801')) path = 'content:problemPhoto';
      else if (src.includes('photo-1758518730151')) path = 'content:howItWorksPhoto';
      else if (src.includes('photo-1522071820081')) path = 'content:aboutPhoto';
    }

    const label = path
      ? path.replace('content:', `${t('İçerik', 'Content')}: `).replace('brand:', `${t('Marka', 'Brand')}: `)
      : t('Serbest Resim (JSON Eşleşmesi Yok)', 'Free Image (No JSON Match)');

    contentEl.innerHTML = `
      <div class="preview-inspector-header">
        <div class="preview-inspector-title">
          <span>🖼️</span>
          <strong>${t('Resmi Düzenle', 'Edit Image')}</strong>
        </div>
        <button type="button" class="inspector-close-btn" id="inspector-close-btn" title="${t('Kapat', 'Close')}" aria-label="${t('Kapat', 'Close')}">✕</button>
      </div>
      <div class="preview-inspector-field">
        <label class="preview-inspector-label">${t('Eşleşen Alan', 'Matched Field')}</label>
        <div class="matched-field-badge">
          <span class="matched-field-icon">🏷️</span>
          <span class="matched-field-key">${escapeHtml(label)}</span>
        </div>
      </div>
      <div class="preview-inspector-field">
        <label class="preview-inspector-label" for="inspector-img-src">${t('Görsel Adresi (URL)', 'Image Address (URL)')}</label>
        <input type="text" id="inspector-img-src" value="${escapeHtml(src || '')}">
      </div>
      <div class="preview-inspector-actions">
        <button type="button" class="btn" id="inspector-choose-asset" style="padding:0.45rem;font-size:0.75rem;cursor:pointer;flex:1;">🖼️ ${t('Kütüphane', 'Library')}</button>
        <button type="button" class="btn preview-toolbox-upload-btn" style="padding:0.45rem;font-size:0.75rem;cursor:pointer;flex:1;text-align:center;">
          📁 ${t('Yükle', 'Upload')}
          <input type="file" id="inspector-upload-file" accept="image/*">
        </button>
      </div>
      <div class="preview-inspector-footer">
        <button type="button" class="btn-cancel-edit" id="inspector-cancel-img">${t('Vazgeç', 'Cancel')}</button>
        <button type="button" class="btn-save-edit" id="inspector-save-img">${t('Değişiklikleri Kaydet', 'Save Changes')}</button>
      </div>
    `;

    contentEl.querySelector('#inspector-close-btn')?.addEventListener('click', () => clearInspectorSelection());
    contentEl.querySelector('#inspector-cancel-img')?.addEventListener('click', () => clearInspectorSelection());

    const srcInput = contentEl.querySelector('#inspector-img-src');
    srcInput.addEventListener('input', (e) => {
      el.src = e.target.value;
    });

    contentEl.querySelector('#inspector-choose-asset').addEventListener('click', () => {
      openAssetLibraryModal(state.brandId, (selectedUrl) => {
        srcInput.value = selectedUrl;
        el.src = selectedUrl;
      });
    });

    contentEl.querySelector('#inspector-upload-file').addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const res = await api('/api/upload', {
            method: 'POST',
            body: {
              brandId: state.brandId,
              filename: file.name,
              content: reader.result,
            }
          });
          srcInput.value = res.url;
          el.src = res.url;
          showNotice(t('Görsel kütüphaneye yüklendi.', 'Image uploaded to library.'), 'success');
        } catch (err) {
          showNotice(err.message, 'error');
        }
      };
      reader.readAsDataURL(file);
    });

    contentEl.querySelector('#inspector-save-img').addEventListener('click', async () => {
      const newSrc = srcInput.value.trim();
      if (!newSrc) return;

      if (path) {
        if (path.startsWith('content:')) {
          const cleanPath = path.substring(8);
          setValueAtPath(pageContent, cleanPath, newSrc);
          await api(`/api/brands/${state.brandId}/content/${selected.lang}/${selected.row.pageId}/${selected.variant}`, {
            method: 'PUT',
            body: pageContent
          });
        } else {
          const cleanPath = path.substring(6);
          setValueAtPath(brandData, cleanPath, newSrc);
          await api(`/api/brands/${state.brandId}`, {
            method: 'PUT',
            body: brandData
          });
        }
        showNotice(t('Görsel başarıyla güncellendi! Yeni build alarak sayfayı yenileyebilirsiniz.', 'Image successfully updated! Rebuild to see changes.'), 'success');
      } else {
        showNotice(t('Bu görsel JSON dosyasında eşleşen bir alana sahip değil, ancak önizlemede güncellendi.', 'This image does not match any JSON field, but was updated in preview.'), 'warning');
      }
      clearInspectorSelection();
    });

  } else if (tagName === 'A') {
    const href = el.getAttribute('href') || '';
    const hrefPath = findValuePath(pageContent, href, 'content:')
      || findValuePath(brandData, href, 'brand:');

    const textKey = el.dataset.inlineKey || 'ctaLabel';

    contentEl.innerHTML = `
      <div class="preview-inspector-header">
        <div class="preview-inspector-title">
          <span>🔗</span>
          <strong>${t('Buton / Link Düzenle', 'Edit Button / Link')}</strong>
        </div>
        <button type="button" class="inspector-close-btn" id="inspector-close-btn" title="${t('Kapat', 'Close')}" aria-label="${t('Kapat', 'Close')}">✕</button>
      </div>
      <div class="preview-inspector-field">
        <label class="preview-inspector-label" for="inspector-link-text">${t('Buton Metni', 'Button Text')} (${escapeHtml(textKey)})</label>
        <input type="text" id="inspector-link-text" value="${escapeHtml(el.textContent.trim())}">
      </div>
      <div class="preview-inspector-field">
        <label class="preview-inspector-label" for="inspector-link-href">${t('Bağlantı Hedefi (HREF)', 'Link Target (HREF)')}</label>
        <input type="text" id="inspector-link-href" value="${escapeHtml(href)}">
      </div>
      <div class="preview-inspector-footer">
        <button type="button" class="btn-cancel-edit" id="inspector-cancel-link">${t('Vazgeç', 'Cancel')}</button>
        <button type="button" class="btn-save-edit" id="inspector-save-link">${t('Değişiklikleri Kaydet', 'Save Changes')}</button>
      </div>
    `;

    contentEl.querySelector('#inspector-close-btn')?.addEventListener('click', () => clearInspectorSelection());
    contentEl.querySelector('#inspector-cancel-link')?.addEventListener('click', () => clearInspectorSelection());

    const textInput = contentEl.querySelector('#inspector-link-text');
    textInput.addEventListener('input', (e) => {
      el.textContent = e.target.value;
    });

    const hrefInput = contentEl.querySelector('#inspector-link-href');
    hrefInput.addEventListener('input', (e) => {
      el.setAttribute('href', e.target.value);
    });

    contentEl.querySelector('#inspector-save-link').addEventListener('click', async () => {
      const newText = textInput.value.trim();
      const newHref = hrefInput.value.trim();

      const latestContent = await api(`/api/brands/${state.brandId}/content/${selected.lang}/${selected.row.pageId}/${selected.variant}`);
      if (latestContent && textKey in latestContent) {
        latestContent[textKey] = newText;
      }

      if (newHref && hrefPath) {
        if (hrefPath.startsWith('content:')) {
          setValueAtPath(latestContent, hrefPath.substring(8), newHref);
        } else {
          const latestBrand = await api(`/api/brands/${state.brandId}`);
          setValueAtPath(latestBrand, hrefPath.substring(6), newHref);
          await api(`/api/brands/${state.brandId}`, {
            method: 'PUT',
            body: latestBrand
          });
        }
      }

      await api(`/api/brands/${state.brandId}/content/${selected.lang}/${selected.row.pageId}/${selected.variant}`, {
        method: 'PUT',
        body: latestContent
      });

      showNotice(t('Bağlantı başarıyla güncellendi! Yeni build alabilirsiniz.', 'Link successfully updated! Rebuild to apply.'), 'success');
      clearInspectorSelection();
    });

  } else {
    const label = el.dataset.inlineKey || 'text';
    const defaultVal = el.dataset.scrollRevealText || el.textContent.trim();
    
    contentEl.innerHTML = `
      <div class="preview-inspector-header">
        <div class="preview-inspector-title">
          <span>✏️</span>
          <strong>${t('Metni Düzenle', 'Edit Text')}</strong>
        </div>
        <button type="button" class="inspector-close-btn" id="inspector-close-btn" title="${t('Kapat', 'Close')}" aria-label="${t('Kapat', 'Close')}">✕</button>
      </div>
      <div class="preview-inspector-field">
        <label class="preview-inspector-label">${t('Eşleşen Alan', 'Matched Field')}</label>
        <div class="matched-field-badge">
          <span class="matched-field-icon">🏷️</span>
          <span class="matched-field-key">${escapeHtml(label)}</span>
        </div>
      </div>
      <div class="preview-inspector-field">
        <label class="preview-inspector-label" for="inspector-text-val">${t('Metin İçeriği', 'Text Content')}</label>
        <textarea id="inspector-text-val" class="preview-inspector-textarea" rows="4">${escapeHtml(defaultVal)}</textarea>
      </div>
      <div class="preview-inspector-actions">
        <button type="button" class="btn-ai-rewrite" id="inspector-ai-rewrite">
          <span>✨</span> ${t('Yapay Zeka ile Yeniden Yaz', 'Rewrite with AI')}
        </button>
      </div>
      <div class="preview-inspector-footer">
        <button type="button" class="btn-cancel-edit" id="inspector-cancel-text">${t('Vazgeç', 'Cancel')}</button>
        <button type="button" class="btn-save-edit" id="inspector-save-text">${t('Değişiklikleri Kaydet', 'Save Changes')}</button>
      </div>
    `;

    contentEl.querySelector('#inspector-close-btn')?.addEventListener('click', () => clearInspectorSelection());
    contentEl.querySelector('#inspector-cancel-text')?.addEventListener('click', () => clearInspectorSelection());

    const textInput = contentEl.querySelector('#inspector-text-val');
    textInput.addEventListener('input', (e) => {
      if (el.dataset.scrollRevealText) {
        el.setAttribute('aria-label', e.target.value);
      } else {
        el.textContent = e.target.value;
      }
    });

    contentEl.querySelector('#inspector-ai-rewrite').addEventListener('click', () => {
      openAiAlternatives(el, frame, selected, pageContent, brandData, textInput);
    });

    contentEl.querySelector('#inspector-save-text').addEventListener('click', async () => {
      const newValue = textInput.value.trim();
      const path = el.dataset.inlineKey;
      if (!path) return;

      if (path.startsWith('content:')) {
        const latestContent = await api(`/api/brands/${state.brandId}/content/${selected.lang}/${selected.row.pageId}/${selected.variant}`);
        if (latestContent) {
          setValueAtPath(latestContent, path.substring(8), newValue);
          await api(`/api/brands/${state.brandId}/content/${selected.lang}/${selected.row.pageId}/${selected.variant}`, {
            method: 'PUT',
            body: latestContent
          });
        }
      } else if (path.startsWith('brand:')) {
        const latestBrand = await api(`/api/brands/${state.brandId}`);
        if (latestBrand) {
          setValueAtPath(latestBrand, path.substring(6), newValue);
          await api(`/api/brands/${state.brandId}`, {
            method: 'PUT',
            body: latestBrand
          });
        }
      }

      showNotice(t('Metin güncellendi! Yeni build alabilirsiniz.', 'Text updated! Rebuild to apply.'), 'success');
      clearInspectorSelection();
    });
  }
}

function clearInspectorSelection() {
  const inspector = document.getElementById('preview-inspector');
  if (!inspector) return;
  const placeholder = inspector.querySelector('.preview-inspector-placeholder');
  const contentEl = inspector.querySelector('.preview-inspector-content');
  if (placeholder && contentEl) {
    placeholder.style.display = 'flex';
    contentEl.style.display = 'none';
    contentEl.innerHTML = '';
  }
  hideInlineToolbar();
  activeEditableElement = null;
}

async function openAiAlternatives(el, frame, selected, pageContent, brandData, textInput) {
  const inspector = document.getElementById('preview-inspector');
  if (!inspector) return;
  const contentEl = inspector.querySelector('.preview-inspector-content');
  if (!contentEl) return;

  const originalContent = contentEl.innerHTML;
  
  contentEl.innerHTML = `
    <div class="ai-rewrite-loading">
      <fluent-progress-ring aria-label="AI Alternatives loading"></fluent-progress-ring>
      <p>${t('Yapay zeka alternatifleri üretiliyor...', 'Generating AI alternatives...')}</p>
    </div>
  `;

  try {
    const res = await api('/api/ai-rewrite', {
      method: 'POST',
      body: {
        text: originalElementText,
        tone: brandData.tone || 'professional',
        key: el.dataset.inlineKey || 'text'
      }
    });

    if (!res.alternatives || res.alternatives.length === 0) {
      throw new Error(t('Alternatifler üretilemedi.', 'Failed to generate alternatives.'));
    }

    contentEl.innerHTML = `
      <div class="preview-inspector-header">
        <div class="preview-inspector-title">
          <span>✨</span>
          <strong>${t('Yapay Zeka Önerileri', 'AI Suggestions')}</strong>
        </div>
        <button type="button" class="inspector-close-btn" id="cancel-ai-rewrite-close" title="${t('Geri Dön', 'Go Back')}" aria-label="${t('Geri Dön', 'Go Back')}">✕</button>
      </div>
      <p class="preview-note" style="margin-bottom:0.75rem;">${t('Önerilerin üzerine gelerek sayfadaki canlı görünümünü inceleyin. Beğendiğinizi seçip onaylayın.', 'Hover over suggestions to inspect them live on the page. Choose one to apply.')}</p>
      <div class="ai-alternative-options">
        ${res.alternatives.map((opt, i) => `
          <div class="ai-alternative-option" data-index="${i}">
            <strong>${t('Seçenek', 'Option')} ${i + 1}</strong>
            <p>${escapeHtml(opt)}</p>
            <fluent-button appearance="accent" class="select-ai-opt" data-index="${i}">${t('Onayla ve Kullan', 'Apply & Use')}</fluent-button>
          </div>
        `).join('')}
      </div>
      <div class="preview-inspector-footer">
        <button type="button" class="btn-cancel-edit" id="cancel-ai-rewrite" style="width:100%;">${t('Geri Dön', 'Go Back')}</button>
      </div>
    `;

    contentEl.querySelector('#cancel-ai-rewrite-close')?.addEventListener('click', () => {
      contentEl.querySelector('#cancel-ai-rewrite')?.click();
    });

    const options = contentEl.querySelectorAll('.ai-alternative-option');
    // Snapshot current text value immediately (textInput DOM node may be replaced after innerHTML swap)
    const snapshotText = textInput?.value ?? originalElementText;

    options.forEach((optCard) => {
      const idx = Number(optCard.dataset.index);
      const optText = res.alternatives[idx];

      optCard.addEventListener('mouseenter', () => {
        if (el.dataset.scrollRevealText) {
          el.setAttribute('aria-label', optText);
        } else {
          el.textContent = optText;
        }
      });

      optCard.addEventListener('mouseleave', () => {
        if (el.dataset.scrollRevealText) {
          el.setAttribute('aria-label', snapshotText);
        } else {
          el.textContent = snapshotText;
        }
      });
    });

    contentEl.querySelectorAll('.select-ai-opt').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const idx = Number(btn.dataset.index);
        const approvedText = res.alternatives[idx];

        // Update element in iframe immediately
        if (el.dataset.scrollRevealText) {
          el.setAttribute('aria-label', approvedText);
        } else {
          el.textContent = approvedText;
        }

        // Restore inspector to text-edit view with approved text pre-filled
        contentEl.innerHTML = originalContent;

        const newTextArea = contentEl.querySelector('#inspector-text-val');
        if (newTextArea) newTextArea.value = approvedText;
        newTextArea?.addEventListener('input', (e) => {
          if (el.dataset.scrollRevealText) {
            el.setAttribute('aria-label', e.target.value);
          } else {
            el.textContent = e.target.value;
          }
        });

        contentEl.querySelector('#inspector-ai-rewrite')?.addEventListener('click', () => {
          openAiAlternatives(el, frame, selected, pageContent, brandData, newTextArea);
        });

        contentEl.querySelector('#inspector-save-text')?.addEventListener('click', async () => {
          const newValue = newTextArea?.value.trim();
          if (!newValue) return;
          const path = el.dataset.inlineKey;
          if (!path) return;
          await savePathValue(path, newValue, selected);
          showNotice(t('Metin güncellendi! Yeni build alabilirsiniz.', 'Text updated! Rebuild to apply.'), 'success');
          clearInspectorSelection();
        });
      });
    });

    contentEl.querySelector('#cancel-ai-rewrite').addEventListener('click', () => {
      // Restore element to original text (use snapshotText captured before innerHTML swap)
      if (el.dataset.scrollRevealText) {
        el.setAttribute('aria-label', snapshotText);
      } else {
        el.textContent = snapshotText;
      }
      contentEl.innerHTML = originalContent;

      const newTextArea = contentEl.querySelector('#inspector-text-val');
      if (newTextArea) newTextArea.value = snapshotText;
      newTextArea?.addEventListener('input', (e) => {
        if (el.dataset.scrollRevealText) {
          el.setAttribute('aria-label', e.target.value);
        } else {
          el.textContent = e.target.value;
        }
      });

      contentEl.querySelector('#inspector-ai-rewrite')?.addEventListener('click', () => {
        openAiAlternatives(el, frame, selected, pageContent, brandData, newTextArea);
      });

      contentEl.querySelector('#inspector-save-text')?.addEventListener('click', async () => {
        const newValue = newTextArea?.value.trim();
        if (!newValue) return;
        const path = el.dataset.inlineKey;
        if (!path) return;
        await savePathValue(path, newValue, selected);
        showNotice(t('Metin güncellendi! Yeni build alabilirsiniz.', 'Text updated! Rebuild to apply.'), 'success');
        clearInspectorSelection();
      });
    });


  } catch (err) {
    showNotice(err.message, 'error');
    contentEl.innerHTML = originalContent;
  }
}

async function setupInlineEditing(frame, selected) {
  try {
    const iframeDoc = frame.contentDocument || frame.contentWindow.document;
    if (!iframeDoc) return;

    const pageContent = await api(`/api/brands/${state.brandId}/content/${selected.lang}/${selected.row.pageId}/${selected.variant}`);
    const brandData = await api(`/api/brands/${state.brandId}`);
    if (!pageContent || !brandData) return;

    const normalizeText = (str) => String(str || '').replace(/\s+/g, ' ').trim();

    const style = iframeDoc.createElement('style');
    style.textContent = `
      [data-inline-key] {
        position: relative;
        outline: 1.5px dashed rgba(0, 120, 212, 0.45);
        cursor: text;
        transition: all 0.15s ease;
      }
      [data-inline-key]:hover {
        outline: 2px dashed rgb(0, 120, 212) !important;
        background-color: rgba(0, 120, 212, 0.05) !important;
      }
      [data-inline-key]:focus {
        outline: 2.5px solid rgb(0, 120, 212) !important;
        background-color: rgba(0, 120, 212, 0.1) !important;
      }
      img[data-inline-key] {
        cursor: pointer !important;
        outline: 2px dashed rgba(0, 120, 212, 0.5) !important;
      }
      img[data-inline-key]:hover {
        outline: 3.5px dashed rgb(0, 120, 212) !important;
      }
    `;
    iframeDoc.head.appendChild(style);

    const elements = iframeDoc.querySelectorAll('h1, h2, h3, h4, h5, h6, p, li, a, button, span, strong, em, img, dt, dd, blockquote, figcaption, td, th, label');
    for (const el of elements) {
      if (el.tagName === 'IMG') {
        const src = el.getAttribute('src');
        if (!src) continue;
        let path = findValuePath(pageContent, src, 'content:')
          || findValuePath(brandData, src, 'brand:');
        
        if (!path) {
          if (src.includes('photo-1758873269811-4e62e346b4b7')) {
            path = 'content:heroPhoto';
          } else if (src.includes('photo-1758518729711-1cbacd55efdb')) {
            path = 'content:featurePhoto';
          } else if (src.includes('photo-1758876017801-f5a892ee460a')) {
            path = 'content:problemPhoto';
          } else if (src.includes('photo-1758518730151-cf64fddb4f0a')) {
            path = 'content:howItWorksPhoto';
          } else if (src.includes('photo-1522071820081-009f0129c71c')) {
            path = 'content:aboutPhoto';
          }
        }

        if (path) {
          el.dataset.inlineKey = path;
          el.setAttribute('data-inline-key', path);
        }
      } else {
        const rawText = el.dataset.scrollRevealText || el.textContent;
        const text = normalizeText(rawText);
        if (!text) continue;

        const path = findValuePath(pageContent, text, 'content:')
          || findValuePath(brandData, text, 'brand:');

        if (path) {
          if (!el.dataset.scrollRevealText) {
            el.contentEditable = 'true';
          }
          el.dataset.inlineKey = path;
          el.setAttribute('data-inline-key', path);
        }
      }
    }

    iframeDoc.addEventListener('click', (e) => {
      const target = e.target;
      const editableEl = target.closest('[data-inline-key], img, a, button, h1, h2, h3, h4, h5, h6, p, li, dt, dd, blockquote, span, strong, em');
      if (!editableEl) {
        clearInspectorSelection();
        return;
      }

      // Dynamic path discovery if clicked element didn't have data-inline-key
      if (!editableEl.dataset.inlineKey) {
        if (editableEl.tagName === 'IMG') {
          const src = editableEl.getAttribute('src');
          let path = findValuePath(pageContent, src, 'content:')
            || findValuePath(brandData, src, 'brand:');
          if (!path && src) {
            if (src.includes('photo-1758873269811')) path = 'content:heroPhoto';
            else if (src.includes('photo-1758518729711')) path = 'content:featurePhoto';
            else if (src.includes('photo-1758876017801')) path = 'content:problemPhoto';
            else if (src.includes('photo-1758518730151')) path = 'content:howItWorksPhoto';
            else if (src.includes('photo-1522071820081')) path = 'content:aboutPhoto';
          }
          if (path) {
            editableEl.dataset.inlineKey = path;
            editableEl.setAttribute('data-inline-key', path);
          }
        } else {
          const rawText = editableEl.dataset.scrollRevealText || editableEl.textContent;
          const text = normalizeText(rawText);
          if (text) {
            const path = findValuePath(pageContent, text, 'content:')
              || findValuePath(brandData, text, 'brand:');
            if (path) {
              editableEl.dataset.inlineKey = path;
              editableEl.setAttribute('data-inline-key', path);
              if (!editableEl.dataset.scrollRevealText) {
                editableEl.contentEditable = 'true';
              }
            }
          }
        }
      }

      if (editableEl.tagName === 'A' || editableEl.closest('a')) {
        e.preventDefault();
      }

      showInspector(editableEl, frame, selected, pageContent, brandData);
    });

    iframeDoc.addEventListener('scroll', () => {
      // Keep inspector open so they can edit from sidebar
    });

    iframeDoc.addEventListener('blur', async (e) => {
      const target = e.target;
      const path = target.dataset.inlineKey;
      if (!path || target.tagName === 'IMG') return;

      const newValue = target.textContent.trim();

      if (path.startsWith('content:')) {
        const latestContent = await api(`/api/brands/${state.brandId}/content/${selected.lang}/${selected.row.pageId}/${selected.variant}`);
        if (latestContent) {
          setValueAtPath(latestContent, path.substring(8), newValue);
          await api(`/api/brands/${state.brandId}/content/${selected.lang}/${selected.row.pageId}/${selected.variant}`, {
            method: 'PUT',
            body: latestContent
          });
        }
      } else if (path.startsWith('brand:')) {
        const latestBrand = await api(`/api/brands/${state.brandId}`);
        if (latestBrand) {
          setValueAtPath(latestBrand, path.substring(6), newValue);
          await api(`/api/brands/${state.brandId}`, {
            method: 'PUT',
            body: latestBrand
          });
        }
      }

      showNotice(
        t('İçerik inline olarak güncellendi! Değişiklikleri yansıtmak için "Yeni build al" butonuna tıklayın.', 'Content updated inline! Click "New build" to reflect changes.'),
        'success'
      );
    }, true);


  } catch (error) {
    console.warn('Inline editing setup failed:', error);
  }
}

async function renderContent() {
  await refreshMeta();
  if (!state.brandId) {
    content.innerHTML = `${heading(t('İçerikler', 'Content'), t('Önce bir marka oluşturun.', 'Create a brand first.'))}<div class="empty">${t('İçerik düzenlemek için marka gerekli.', 'A brand is required to edit content.')}</div>`;
    return;
  }
  await loadContentCombos();
  const selected = state.contentCombos[state.selectedContentIndex];
  content.innerHTML = `
    ${heading(t('İçerikler', 'Content'), t('Her dil ve varyant için yapılandırılmış JSON içeriğini düzenleyin veya yeniden üretin.', 'Edit or regenerate structured JSON copy for every language and variant.'))}
    <div class="toolbar content-toolbar"><div class="field grow"><label for="content-brand">${t('Marka', 'Brand')}</label><select id="content-brand">${brandOptions()}</select></div><div class="field grow"><label for="content-search">${t('Sayfa ara', 'Search pages')}</label><input id="content-search" type="search" placeholder="${t('Page id, dil veya varyant', 'Page ID, language, or variant')}"></div></div>
    <div class="split">
      <section class="panel list" aria-label="${t('İçerik dosyaları', 'Content files')}">
        ${state.contentCombos.length ? state.contentCombos.map((combo, index) => `<button type="button" data-content-index="${index}" data-search="${escapeHtml(`${combo.row.pageId} ${combo.row.segment} ${combo.lang} ${combo.variant}`.toLowerCase())}" aria-current="${index === state.selectedContentIndex ? 'true' : 'false'}"><span><strong>${escapeHtml(combo.row.pageId)}</strong><small>${escapeHtml(combo.lang)} / ${escapeHtml(combo.variant)}</small></span><span class="status" data-status="${combo.exists ? 'success' : 'missing'}">${combo.exists ? t('Hazır', 'Ready') : t('Eksik', 'Missing')}</span></button>`).join('') : `<div class="empty">${t('Matrix içinde içerik kombinasyonu yok.', 'No content combinations in matrix.')}</div>`}
      </section>
      <section id="content-editor" class="panel">${selected ? `<fluent-progress-bar aria-label="${t('İçerik yükleniyor', 'Loading content')}"></fluent-progress-bar>` : `<div class="empty">${t('Düzenlenecek içerik yok.', 'No content to edit.')}</div>`}</section>
    </div>`;
  document.querySelector('#content-brand').addEventListener('change', async (event) => { setActiveBrand(event.target.value); state.selectedContentIndex = 0; await renderContent(); });
  document.querySelectorAll('[data-content-index]').forEach((button) => button.addEventListener('click', async () => {
    state.selectedContentIndex = Number(button.dataset.contentIndex);
    await renderContent();
  }));
  document.querySelector('#content-search')?.addEventListener('input', (event) => {
    const query = event.target.value.trim().toLowerCase();
    document.querySelectorAll('[data-content-index]').forEach((button) => {
      button.hidden = Boolean(query) && !button.dataset.search.includes(query);
    });
  });
  if (selected) await renderContentEditor(selected);
}

async function renderContentEditor(combo) {
  const type = state.meta.pageTypes.find((item) => item.id === combo.row.pageType);
  const template = Object.fromEntries(type.copyFields.map((field) => [
    field,
    state.meta.contentFields?.[field]?.type === 'array' ? [] : '',
  ]));
  let value = template;
  if (combo.exists) value = await api(`/api/brands/${state.brandId}/content/${combo.lang}/${combo.row.pageId}/${combo.variant}`);
  const editor = document.querySelector('#content-editor');
  editor.innerHTML = `
    <div class="panel-heading">
      <div>
        <h2>${escapeHtml(combo.row.pageId)}</h2>
        <p>${escapeHtml(combo.lang)} / ${escapeHtml(combo.variant)} / ${escapeHtml(combo.row.pageType)}</p>
      </div>
      <div style="display: flex; gap: 0.5rem;">
        <fluent-button id="preview-content" appearance="outline">${t('Önizle', 'Preview')}</fluent-button>
        <fluent-button id="external-preview-content" appearance="outline">${t('Dış Önizleme ↗', 'External Preview ↗')}</fluent-button>
      </div>
    </div>
    <div class="field">
      <div style="display:flex; justify-content:space-between; align-items:center;">
        <label for="content-json">${t('İçerik JSON', 'Content JSON')}</label>
        <div class="field-actions" style="display:flex; gap:0.4rem; align-items:center;">
          <span id="json-validation-status" style="font-size:0.68rem; font-weight:600; padding:0.15rem 0.45rem; border-radius:4px; transition:all 0.15s ease;"></span>
          <button class="small-action-btn" type="button" data-textarea-id="content-json" data-action="pick-asset" title="${t('Görsel Seç ve Ekle', 'Pick Asset')}">🖼️ ${t('Seç', 'Pick')}</button>
          <button class="small-action-btn" type="button" data-textarea-id="content-json" data-action="beautify" title="${t('JSON Formatla', 'Format JSON')}">✨ ${t('Formatla', 'Format')}</button>
        </div>
      </div>
      <textarea id="content-json" class="code" spellcheck="false">${escapeHtml(JSON.stringify(value, null, 2))}</textarea>
      <small>${t('Zorunlu alanlar', 'Required fields')}: ${type.copyFields.map(escapeHtml).join(', ')}</small>
    </div>
    <div class="toolbar">
      <fluent-button id="save-content" appearance="accent">${t('Kaydet', 'Save')}</fluent-button>
      <fluent-button id="generate-content" appearance="outline">${combo.exists ? t('Zorla yeniden üret', 'Force regenerate') : t('AI ile üret', 'Generate with AI')}</fluent-button>
      ${combo.exists ? '<fluent-button id="delete-content" appearance="subtle">' + t('Çöpe taşı', 'Move to trash') + '</fluent-button>' : ''}
    </div>`;

  const contentJsonTextarea = document.querySelector('#content-json');
  const validationStatusEl = document.querySelector('#json-validation-status');
  const runValidation = () => {
    const val = contentJsonTextarea.value.trim();
    if (!val) {
      validationStatusEl.textContent = '';
      validationStatusEl.style.background = 'transparent';
      return;
    }
    try {
      const parsed = JSON.parse(val);
      let missing = [];
      let wrongTypes = [];
      for (const field of type.copyFields) {
        if (!(field in parsed)) {
          missing.push(field);
        } else {
          const expectedType = state.meta.contentFields?.[field]?.type;
          const valType = Array.isArray(parsed[field]) ? 'array' : typeof parsed[field];
          if (expectedType && expectedType !== valType) {
            wrongTypes.push(`${field} (${valType} yerine ${expectedType} olmalı)`);
          }
        }
      }
      if (missing.length > 0 || wrongTypes.length > 0) {
        let msg = '';
        if (missing.length > 0) msg += (currentLang === 'tr' ? `Eksik: ${missing.join(', ')}` : `Missing: ${missing.join(', ')}`);
        if (wrongTypes.length > 0) msg += (msg ? ' | ' : '') + (currentLang === 'tr' ? `Tür: ${wrongTypes.join(', ')}` : `Type: ${wrongTypes.join(', ')}`);
        validationStatusEl.textContent = msg;
        validationStatusEl.style.background = 'var(--accent-soft)';
        validationStatusEl.style.color = 'var(--warning)';
      } else {
        validationStatusEl.textContent = t('✓ Geçerli & Şema Doğru', '✓ Valid & Schema Matched');
        validationStatusEl.style.background = 'rgba(16, 124, 65, 0.1)';
        validationStatusEl.style.color = 'var(--success)';
      }
    } catch (err) {
      validationStatusEl.textContent = currentLang === 'tr' ? `Hatalı JSON: ${err.message.substring(0, 30)}` : `Invalid JSON: ${err.message.substring(0, 30)}`;
      validationStatusEl.style.background = 'rgba(180, 35, 24, 0.1)';
      validationStatusEl.style.color = 'var(--danger)';
    }
  };
  contentJsonTextarea.addEventListener('input', runValidation);
  runValidation();

  document.querySelector('#preview-content').addEventListener('click', () => openPreview(combo));
  document.querySelector('#external-preview-content').addEventListener('click', () => {
    window.open(previewUrl(combo), '_blank');
  });
  document.querySelector('#save-content').addEventListener('click', async () => {
    try {
      const payload = JSON.parse(document.querySelector('#content-json').value);
      await api(`/api/brands/${state.brandId}/content/${combo.lang}/${combo.row.pageId}/${combo.variant}`, { method: 'PUT', body: payload });
      showNotice(t('İçerik kaydedildi ve yedeği alındı.', 'Content saved and backed up.'), 'success');
      combo.exists = true;
      runValidation();
    } catch (error) { showNotice(error.message, 'error'); }
  });
  document.querySelector('#generate-content').addEventListener('click', () => generateContentAndBuild(combo));
  document.querySelector('#delete-content')?.addEventListener('click', async () => {
    if (!window.confirm(t('Bu içerik geri alınabilir çöpe taşınsın mı?', 'Move this content to recoverable trash?'))) return;
    try {
      await api(`/api/brands/${state.brandId}/content/${combo.lang}/${combo.row.pageId}/${combo.variant}`, { method: 'DELETE' });
      showNotice(t('İçerik çöpe taşındı.', 'Content moved to trash.'), 'success');
      await renderContent();
    } catch (error) { showNotice(error.message, 'error'); }
  });
}

const JOB_STATUS_ICON = { success: '✅', error: '❌', running: '⏳' };

function jobStatusLabel(status) {
  const icon = JOB_STATUS_ICON[status] || '•';
  const label = status === 'success' ? t('Başarılı', 'Success')
    : status === 'error' ? t('Hata', 'Error')
    : status === 'running' ? t('Çalışıyor', 'Running')
    : status;
  return `${icon} ${label}`;
}

function jobDuration(job) {
  if (!job.createdAt) return null;
  const startMs = new Date(job.createdAt).getTime();
  const endMs = job.finishedAt ? new Date(job.finishedAt).getTime() : Date.now();
  const seconds = Math.max(0, (endMs - startMs) / 1000);
  return seconds < 60 ? `${seconds.toFixed(1)}s` : `${Math.floor(seconds / 60)}dk ${Math.round(seconds % 60)}s`;
}

function jobScopeLabel(job) {
  const parts = [];
  if (job.pageIds?.length) parts.push(job.pageIds.join(', '));
  if (job.langs?.length) parts.push(job.langs.join('/'));
  if (job.variants?.length) parts.push(job.variants.join('/'));
  return parts.length ? parts.join(' · ') : t('(tüm aktif kapsam)', '(all active scope)');
}

async function renderJobs() {
  const jobs = await api('/api/jobs');
  const selected = jobs.find((job) => job.id === state.selectedJobId) ?? jobs[0] ?? null;
  state.selectedJobId = selected?.id ?? null;
  content.innerHTML = `
    ${heading(t('İşlemler', 'Jobs'), t('İçerik üretimi ve statik build süreçlerini, çıkış kodlarını ve logları izleyin.', 'Monitor content generation and static build jobs, exit codes, and live logs.'))}
    <div class="split">
      <section class="panel list" aria-label="${t('İşlem geçmişi', 'Job history')}">
        ${jobs.length ? jobs.map((job) => `<button type="button" data-job-id="${job.id}" aria-current="${job.id === state.selectedJobId ? 'true' : 'false'}"><strong>${job.kind === 'build' ? 'Build' : t('İçerik üretimi', 'Content generation')} / ${escapeHtml(job.brandId)}</strong><small>${escapeHtml(jobStatusLabel(job.status))} · ${escapeHtml(jobDuration(job) || '')} · ${new Date(job.createdAt).toLocaleString(currentLang === 'tr' ? 'tr-TR' : 'en-US')}</small></button>`).join('') : `<div class="empty">${t('Henüz işlem çalıştırılmadı.', 'No jobs have run yet.')}</div>`}
      </section>
      <section class="panel">
        ${selected ? `<div class="page-heading"><div><h2>${selected.kind === 'build' ? 'Build' : t('İçerik üretimi', 'Content generation')}</h2><p>${escapeHtml(selected.brandId)} — ${escapeHtml(jobScopeLabel(selected))}</p></div><span class="status" data-status="${escapeHtml(selected.status)}">${escapeHtml(jobStatusLabel(selected.status))}</span></div><div class="job-summary-banner" data-status="${escapeHtml(selected.status)}">${jobStatusLabel(selected.status)} ${selected.status === 'running' ? t('— iş sürüyor…', '— job in progress…') : t(`— ${jobDuration(selected)} sürdü (exit code: ${selected.exitCode ?? '—'})`, `— took ${jobDuration(selected)} (exit code: ${selected.exitCode ?? '—'})`)}</div><pre>${escapeHtml(selected.log || t('İşlem başladı, çıktı bekleniyor...', 'Job started, awaiting output...'))}</pre>` : `<div class="empty">${t('Bir işlem başlattığınızda log burada görünecek.', 'Logs will appear here once you run a job.')}</div>`}
      </section>
    </div>`;
  document.querySelectorAll('[data-job-id]').forEach((button) => button.addEventListener('click', async () => { state.selectedJobId = button.dataset.jobId; await renderJobs(); }));
  if (jobs.some((job) => job.status === 'running')) state.jobTimer = setTimeout(renderJobs, 1200);
}

function applyTheme(dark) {
  setTheme(dark ? webDarkTheme : webLightTheme);
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  if (themeToggle) {
    themeToggle.checked = dark;
    themeToggle.setAttribute('aria-checked', String(dark));
  }
  const authThemeToggle = document.querySelector('#auth-theme-toggle');
  if (authThemeToggle) {
    authThemeToggle.checked = dark;
    authThemeToggle.setAttribute('aria-checked', String(dark));
  }
  localStorage.setItem('pagix-admin-theme', dark ? 'dark' : 'light');
}

document.querySelectorAll('[data-nav]').forEach((button) => button.addEventListener('click', () => navigate(button.dataset.nav)));
sidebarBrand.addEventListener('change', async () => {
  setActiveBrand(sidebarBrand.value);
  state.selectedContentIndex = 0;
  await navigate(state.section);
});
mobileMenu.addEventListener('click', () => {
  const open = sidebar.dataset.open !== 'true';
  sidebar.dataset.open = String(open);
  mobileMenu.setAttribute('aria-expanded', String(open));
});
themeToggle.addEventListener('change', () => applyTheme(themeToggle.checked));
document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeMobileMenu(); });

document.addEventListener('click', (event) => {
  const actionBtn = event.target.closest('[data-action]');
  if (actionBtn) {
    event.preventDefault();
    const action = actionBtn.dataset.action;
    const targetId = actionBtn.dataset.textareaId;
    const textarea = document.getElementById(targetId);
    if (!textarea) return;

    if (action === 'beautify') {
      try {
        const parsed = JSON.parse(textarea.value);
        textarea.value = JSON.stringify(parsed, null, 2);
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        showNotice('JSON başarıyla formatlandı.', 'success');
      } catch (err) {
        showNotice('Geçersiz JSON, formatlanamadı: ' + err.message, 'error');
      }
    } else if (action === 'pick-asset') {
      let brandId = '';
      if (state.section === 'wizard' && state.wizardDraft?.brand?.id) {
        brandId = state.wizardDraft.brand.id;
      } else if (state.brandId) {
        brandId = state.brandId;
      }
      
      if (!brandId) {
        showNotice('Lütfen önce marka ID değerini belirleyin.', 'error');
        return;
      }

      openAssetLibraryModal(brandId, (selectedUrl) => {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const val = textarea.value;
        const wrappedUrl = `"${selectedUrl}"`;
        textarea.value = val.substring(0, start) + wrappedUrl + val.substring(end);
        textarea.selectionStart = textarea.selectionEnd = start + wrappedUrl.length;
        textarea.focus();
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        showNotice('Görsel URL\'si imlecin bulunduğu yere eklendi.', 'success');
      });
    }
    return;
  }

  const picker = event.target.closest('[data-asset-picker]');
  if (picker) {
    event.preventDefault();
    const input = picker.previousElementSibling;
    
    let brandId = '';
    if (state.section === 'wizard' && state.wizardDraft?.brand?.id) {
      brandId = state.wizardDraft.brand.id;
    } else if (state.brandId) {
      brandId = state.brandId;
    }
    
    if (!brandId) {
      showNotice('Lütfen önce marka ID değerini belirleyin.', 'error');
      return;
    }
    
    openAssetLibraryModal(brandId, (selectedUrl) => {
      input.value = selectedUrl;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
    return;
  }
});

const updateInputThumbnail = (input) => {
  const group = input.closest('.input-action-group');
  if (!group) return;
  let thumb = group.querySelector('.input-thumbnail-preview');
  if (!thumb) {
    thumb = document.createElement('div');
    thumb.className = 'input-thumbnail-preview';
    group.insertBefore(thumb, input);
  }
  const val = input.value.trim();
  if (val && (val.startsWith('/') || val.startsWith('http') || val.startsWith('data:'))) {
    thumb.innerHTML = `<img src="${escapeHtml(val)}" alt="Önizleme" onerror="this.style.display='none'">`;
    thumb.style.display = 'flex';
  } else {
    thumb.innerHTML = '';
    thumb.style.display = 'none';
  }
};

document.addEventListener('input', (event) => {
  if (event.target.matches('[data-logo-field="src"], [data-story-field="mediaSrc"], [data-story-field="companyLogoSrc"]')) {
    updateInputThumbnail(event.target);
  }
});

new MutationObserver(() => enhanceFluentSemantics()).observe(document.body, { childList: true, subtree: true });
enhanceFluentSemantics();

const langToggle = document.querySelector('#lang-toggle');
if (langToggle) {
  langToggle.value = currentLang;
  langToggle.addEventListener('change', (e) => {
    currentLang = e.target.value;
    localStorage.setItem('pagix-admin-lang', currentLang);
    const authLangToggle = document.querySelector('#auth-lang-toggle');
    if (authLangToggle) authLangToggle.value = currentLang;
    applyStaticTranslations();
    if (state.auth.authenticated) navigate(state.section);
  });
}

const authLangToggle = document.querySelector('#auth-lang-toggle');
if (authLangToggle) {
  authLangToggle.value = currentLang;
  authLangToggle.addEventListener('change', (e) => {
    currentLang = e.target.value;
    localStorage.setItem('pagix-admin-lang', currentLang);
    if (langToggle) langToggle.value = currentLang;
    applyStaticTranslations();
    if (state.auth.authenticated) navigate(state.section);
  });
}

const authThemeToggle = document.querySelector('#auth-theme-toggle');
if (authThemeToggle) {
  authThemeToggle.addEventListener('change', () => applyTheme(authThemeToggle.checked));
}

const authPasswordToggle = document.querySelector('#auth-password-toggle');
if (authPasswordToggle) {
  authPasswordToggle.addEventListener('click', () => {
    const passInput = document.querySelector('#auth-password');
    if (passInput) {
      const isPassword = passInput.type === 'password';
      passInput.type = isPassword ? 'text' : 'password';
      authPasswordToggle.textContent = isPassword ? '🙈' : '👁️';
      authPasswordToggle.setAttribute('aria-label', isPassword ? t('Şifreyi gizle', 'Hide password') : t('Şifreyi göster', 'Show password'));
    }
  });
}

const authForm = document.querySelector('#auth-form');
if (authForm) {
  authForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const submitBtn = document.querySelector('#auth-submit-btn');
    const authError = document.querySelector('#auth-error');
    const usernameInput = document.querySelector('#auth-username');
    const passwordInput = document.querySelector('#auth-password');
    if (authError) {
      authError.hidden = true;
      authError.textContent = '';
    }
    if (submitBtn) submitBtn.disabled = true;

    try {
      const result = await api('/api/auth/login', {
        method: 'POST',
        body: {
          username: usernameInput.value.trim(),
          password: passwordInput.value,
        },
      });
      showAppScreen(result.user);
      await refreshMeta();
      await navigate(state.section || 'overview');
    } catch (err) {
      if (authError) {
        authError.textContent = err.message || t('Geçersiz kullanıcı adı veya şifre.', 'Invalid username or password.');
        authError.hidden = false;
      }
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });
}

const logoutBtn = document.querySelector('#logout-btn');
if (logoutBtn) {
  logoutBtn.addEventListener('click', async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch (e) {
      // Ignore logout network errors
    }
    showAuthScreen();
  });
}

applyStaticTranslations();

async function initApp() {
  applyTheme((localStorage.getItem('pagix-admin-theme') || localStorage.getItem('landcraft-admin-theme')) === 'dark');
  applyStaticTranslations();

  try {
    const sessionData = await api('/api/auth/session');
    state.auth = sessionData;

    const devHint = document.querySelector('#auth-dev-hint');
    if (devHint) devHint.hidden = !sessionData.devMode;

    if (sessionData.authenticated) {
      showAppScreen(sessionData.user);
      await refreshMeta();
      const initialSection = new URLSearchParams(window.location.search).get('section') || window.location.hash.replace('#', '') || 'overview';
      await navigate(sectionMeta[initialSection] ? initialSection : 'overview');
    } else {
      showAuthScreen();
    }
  } catch (error) {
    showAuthScreen(error.message);
  }
}

initApp();
